const _DASH_THEMES = {
  fanteam:    { active:   "bg-emerald-700 border-emerald-600 text-white",
                inactive: "bg-slate-800 border-emerald-800 text-emerald-500 hover:border-emerald-700 hover:text-emerald-300",
                badge: "bg-emerald-900/40 border-emerald-700/60 text-emerald-300",
                matchSel: "border-emerald-300 bg-emerald-900/40",
                cardLeft: "border-l-emerald-500", color: "#10b981" },
  draftkings: { active:   "bg-green-800 border-green-700 text-orange-300",
                inactive: "bg-slate-800 border-orange-900 text-orange-600 hover:border-orange-700 hover:text-orange-400",
                badge: "bg-green-900/40 border-green-700/60 text-orange-300",
                matchSel: "border-orange-400 bg-green-950/40",
                cardLeft: "border-l-orange-500", color: "#f97316" },
  fanduel:    { active:   "bg-blue-700 border-blue-600 text-white",
                inactive: "bg-slate-800 border-blue-900 text-blue-500 hover:border-blue-700 hover:text-blue-300",
                badge: "bg-blue-900/40 border-blue-700/60 text-blue-300",
                matchSel: "border-blue-300 bg-blue-900/40",
                cardLeft: "border-l-blue-500", color: "#3b82f6" },
};

// Stat columns + position groups now live in static/js/sports/<sport>.js,
// surfaced via window.SportRegistry. Local shims keep call sites unchanged.
const _playerPosGroup = (sportId, p) => window.SportRegistry.posGroup(sportId, p);

function _statVal(p, key) {
  if (!p.stats) return null;
  const v = p.stats[key];
  return (v === undefined || v === null) ? null : v;
}

function _fmtToi(v) {
  if (v === null) return "–";
  const f = parseFloat(v);
  if (isNaN(f)) return "–";
  return String(Math.floor(f));
}

function _fmtStat(col, p) {
  if (col.ftBonus) {
    const v = p.ft_bonus;
    if (v === undefined || v === null) return "–";
    return (v >= 0 ? "+" : "") + v.toFixed(2);
  }
  const v = _statVal(p, col.key);
  if (v === null) return "–";
  if (col.toi) return _fmtToi(v);
  return v;
}

// Team abbrev → {primary colour, logo} maps, memoised. These are STATIC
// reference data (a team's colour/logo never changes), but the _teamColors /
// _teamLogos getters used to rebuild them by iterating the whole player pool
// (500+) on EVERY read — and teamColor()/logoFor() are called per player row,
// so rendering the pool was ~rows×pool iterations (O(n²)). The perf profile put
// get _teamColors at #1 self-time (785ms). Cache keyed on the pool + games array
// identity: applyData swaps in new arrays only when data actually changes, so we
// rebuild once per update, not per read. Module scope → no reactive-write loop.
const _EMPTY_ARR = [];
const _teamMapCache = new WeakMap();   // playersArray → { games, colors, logos }
// lineupRows array -> { key, active, inCount }. activeLineupRows and _inCountMap
// are getters (Alpine has no computed), read by every "in N/150" chip, Wire row
// and scoreboard card: at 150 lineups each read re-sorted 150 rows and re-walked
// 1,350 players - ~0.5s of every push on a desktop (Phase 10, 2026-10-11). The
// rows array is replaced on every push, so its identity plus the filter state
// is the whole key; the filter state is still READ each call, so Alpine keeps
// tracking it.
const _luMemo = new WeakMap();
const _sbNamer = new WeakMap();   // lineup rows array -> LSRace namer (sbMine)
const _shellMemo = new WeakMap();  // lineup rows array -> { mineByGame, exposure } (phone shell)
let _topScorerCache = null;            // { pool: <allSlatePlayers ref>, byGame: {} }
function _teamMaps(players, games) {
  players = players || _EMPTY_ARR;
  games   = games   || _EMPTY_ARR;
  let e = _teamMapCache.get(players);
  if (e && e.games === games) return e;
  const colors = {}, logos = {};
  for (const p of players) {
    const t = (p.team || '').toUpperCase();
    if (!t) continue;
    if (p.team_primary && !colors[t]) colors[t] = p.team_primary;
    if (p.team_logo    && !logos[t])  logos[t]  = p.team_logo;
  }
  for (const g of games) {
    const ht = (g.home_team || '').toUpperCase(), at = (g.away_team || '').toUpperCase();
    if (ht) { if (g.home_primary && !colors[ht]) colors[ht] = g.home_primary;
              if (g.home_logo    && !logos[ht])  logos[ht]  = g.home_logo; }
    if (at) { if (g.away_primary && !colors[at]) colors[at] = g.away_primary;
              if (g.away_logo    && !logos[at])  logos[at]  = g.away_logo; }
  }
  e = { games, colors, logos };
  _teamMapCache.set(players, e);
  return e;
}

let _DASH_PLATFORMS = [];

function _dbEnrichSlates(rawSlates, allTemplates) {
  return rawSlates.map(s => {
    const plat  = _DASH_PLATFORMS.find(p => p.id === s.platform_id)
                  || { id: s.platform_id, name: s.platform_id,
                       logo: `/static/img/${s.platform_id}.png`, sports: [],
                       currency: "£", theme: _DASH_THEMES.fanteam };
    const sport = plat.sports.find(sp => sp.id === s.sport_id) || { id: s.sport_id, name: s.sport_id };
    const tmpl  = allTemplates.find(t => t.id === s.slate_template_id) || null;
    return { ...s, platform: plat, sport, template: tmpl };
  });
}

// Status-meta lookup delegates to the per-sport registry. Each sport's
// labels live in its own static/js/sports/<sport>.js file.
const sportStatusMeta = (sportId) => window.SportRegistry.statusMeta(sportId);

// sportIconHtml and sportIcon are provided by platform-themes.js (window.sportIconHtml / window.sportIconSrc)

// Merge server-side team names into the global _TEAM_NAMES (declared in teams.js).
//
// Deferred to DOMContentLoaded, and that is load-bearing. teams.js is loaded
// with `defer` in base.html; this file is NOT, and a non-deferred script in the
// body runs DURING parsing — before any deferred one. So this ran first and
// threw "ReferenceError: _TEAM_NAMES is not defined", killing the rest of the
// file's top-level execution. Worse, `const _TEAM_NAMES` in teams.js would then
// have replaced whatever we merged anyway, so the merge had to move regardless
// of the error. Deferred scripts all execute before DOMContentLoaded fires,
// which makes this the correct join point rather than a guess about ordering.
document.addEventListener('DOMContentLoaded', function () {
  const _srv = (window.DASH_INIT && window.DASH_INIT.teamNames) || {};
  if (typeof _TEAM_NAMES === 'undefined') return;   // teams.js absent: names fall back to abbrevs
  for (const sport of Object.keys(_srv)) {
    _TEAM_NAMES[sport] = Object.assign(_TEAM_NAMES[sport] || {}, _srv[sport]);
  }
});
function teamFullName(sportId, abbr) {
  if (!abbr) return abbr;
  // Guarded the same way platform-themes.js guards it: this can be called from
  // a widget that renders before teams.js has run, and an abbreviation is a
  // perfectly good fallback for a full name.
  if (typeof _TEAM_NAMES === 'undefined') return abbr;
  return (_TEAM_NAMES[(sportId || '').toLowerCase()] || {})[abbr.toUpperCase()] || abbr;
}

function dashboard() {
  return {
    savedSlates:   [],   // all slates (active + completed)
    _discoverSlates: [], // P2: other LIVE slates you're NOT entered on — lets the on-board switcher jump to any live contest (→ read-only spectator)
    selectedSlate: null,
    spectatorMode: false, // true when viewing a slate the user has NO lineup on
    _warmT0:       0,     // when this board started waiting for live player stats (first in, 2026-11-01)
    _warmSecs:     0,     // seconds waited, ticked by _warmTick while warming
    _warmDoneAt:   0,     // when stats landed after a wait - the "You warmed this up" moment
                          // (watch/scout) — host renders the 'spectator' preset.
    newToLineups:  false, // has NEVER entered a lineup anywhere - a guest included (2026-10-20).
    isGuest:       false, // signed out: watching without an account; adding a lineup means signing up.
                          // Separates a newcomer from a scout: both spectate,
                          // only one needs to be shown the way in.
    sportMeta:     sportStatusMeta(null),   // updated on slate select
    _fetchSeq:     0,    // incremented on each slate select; stale responses are discarded
    allGames:      [],
    players:       [],
    lineupRows:    [],
    // A visitor watching the demo gets ONE contextual ask, under the plays,
    // after a minute. The only other one is the end card, ~394s away - longer
    // than anybody watches (run 8, first contact #16). The bar is a bar; this
    // one is in the page, where what they are watching explains it.
    _demoAsk: false,
    _shellHeroPulse: '',   // 'up' | 'down' for ~900ms after the hero total moves
    _shellHeroLast:  null,
    feed:          [],   // vidiprinter events
    _vidiQueue:    [],   // drip queue — events waiting to pop in one by one
    _vidiDripTimer: null,
    _lastVidiBatchAt: 0, // timestamp (ms) of most recent batch push — anchors adaptive drip spacing
    // Vidi replay guards (slate-scoped, reset in selectSlate):
    //  _vidiSeeded      — false until the FIRST data apply for this slate; that
    //                     first apply's scorer buffer is entirely historical, so
    //                     it's committed statically (no drip / sound / animation)
    //                     instead of replaying the whole night one-by-one.
    //  _vidiHadLiveEvent — false until the first genuinely-new event DRIPS post-
    //                     seed; while false every lane paint is instant (no drop-
    //                     in), so seeded rows (and the bulk pbp render) never
    //                     animate on open or on a later tab-mount. The first live
    //                     drip flips it and normal entrance animation resumes.
    _vidiSeeded:   false,
    _vidiHadLiveEvent: false,
    freshIds:      {},
    newestVidiIds: {},
    statFlashIds:  {},
    prevStats:     {},
    prevAllStats:  {},
    prevGames:     {},
    // Personal-moment detector state. Each tracks the last-known signal
    // so we only fire on the EDGE (e.g. crossing into "ahead of pace",
    // not on every tick that's still ahead). Reset on selectSlate.
    _momentPrevBestLineupId: null,
    _momentLineupAhead:      {},     // { lineup_id: bool }  was it ahead-of-pace last tick
    _momentSlateOverFired:   false,
    _seenMatchEventIds: new Set(),
    _bdHoverId: null,
    _bdPos: { x: 0, y: 0 },
    // Per-sport vidi prefs (vidi_<sport>_<leaf>) are read on demand via
    // vidiPref(sport, leaf, fallback) from this._vidiSettings — see init().
    // Don't reintroduce per-sport camelCase mirror props; if you find
    // yourself writing `this.vidiFooBarEvents`, you want vidiPref('foo',
    // 'bar_events', true) instead. The accessor is the only sanctioned
    // way to read per-sport vidi settings inside live.js or any
    // SPORT_REGISTRY detector module.
    _vidiSettings: {},
    vidiScope:       'my',
    // vidiDensity gates the broadcast play-by-play layer on top of the
    // scorer feed. 'mine' = scorer events only (existing behaviour);
    // 'big'  = scorer + PbP plays tagged salience:'big';
    // 'all'  = scorer + ALL PbP plays. Currently only MLB ships PbP, but
    // the dedup + filter pipeline is sport-agnostic so future per-sport
    // feeds (NBA quarter narrative, soccer commentary) plug in for free.
    vidiDensity:     'mine',
    _pbpFeed:        [],  // accumulated PbP events from data.pbp_events
    _vidiRaf:      null,
    _vidiFeed:     null,      // Vidi v2 node-persistent renderer (window.VidiFeed)
    _vidiDripPaused: false,   // freezes the drip queue while a replay is paused
    slateOver:     false,
    soundOn:       true,
    // Reactive mirror of document.fullscreenElement for the control-deck
    // toggle. Updated by the fullscreenchange listener wired in init().
    isFullscreen:  false,
    _audioCtx:     null,
    _suppressSound: false,
    nLineups:      0,
    bestLive:      0,
    avgLive:       0,
    bestEst:       0,
    // Reactive flag for v2 host's KPI gate (see kpiShouldRender in
    // live_v2/index.js). Set true once any user projection lands.
    hasProjections: false,
    lastUpdated:   null,
    // Most-recent live_stats write across the slate's games (ISO UTC).
    // Distinct from lastUpdated, which is the response-build timestamp.
    // When the poller stalls, dataUpdatedAt freezes while lastUpdated
    // keeps ticking — the staleness pill watches dataUpdatedAt.
    // _timerNow (set up by _startClock at 1s) drives reactive age getters.
    dataUpdatedAt: null,
    sseReconnecting: false,   // true while the SSE stream is down (drives the pill)
    loading:       true,
    dataReady:     false,
    // Set when the board could not be loaded, so a failed request stops
    // presenting as an empty-but-successful dashboard. See _fetchInitialData:
    // the primary payload's status was never checked, so a 5xx JSON error body
    // flowed into _applyData(), which skips the player/lineup block but still
    // flips dataReady and clears loading - a fully "loaded" board with nothing
    // on it and no way to tell that anything went wrong.
    loadFailed:    false,
    slatesFailed:  false,
    _initialized:  false,
    spPage:        20,    // My Players visible row cap
    apPage:        50,    // All Players visible row cap (page size; +50 per "Show more")
    activeTab:     "summary",
    filtersOpen:   false, // shared collapse toggle for all tab filter bars
    scoringRules:    {},  // {stat: weight} — position_group="" rules (NBA etc.)
    scoringRulesByPos: {}, // {pos_group: {stat: weight}} — for position-split sports (soccer)
    spShowNS:      true,
    spShowLive:    true,
    spShowDone:    true,
    sortCol:       "live",
    sortDir:       -1,
    openLineups:      new Set(),
    selectedGames:    new Set(),
    selectedStakes:   new Set(),
    luShowNS:         true,
    luShowLive:       true,
    luShowDone:       true,
    luSortKey:        "live",
    luSortDir:        "desc",
    luSearch:         "",
    luPage:           20,   // My Lineups visible lineup cap (page size; +20 per "Show more")
    allSlatePlayers:  [],
    apSearch:         "",
    spSearch:         "",
    spPosFilter:      null,
    apSortCol:        "live",
    apSortDir:        -1,
    apPosFilter:      null,
    apShowNS:         true,
    apShowLive:       true,
    apShowDone:       true,
    dreamTeam:        [],
    salaryCap:        0,
    pa_brackets:      [],   // D/ST points-allowed tiers (sse.js) - nfl_dst_bracket
    formatVariant:    '',   // slate scoring format ('single_stat_hr' etc.); '' = standard
    _sse:             null,
    isAdmin:          false,
    // Showcase demo (public /demo/<slug>) — a synthetic slate fed by a
    // CLIENT-DRIVEN playback clock (Xav 2026-07-10): we own `demoT` and poll
    // the snapshot endpoint, so the demo no longer auto-loops and the user gets
    // play / pause / restart + speed via the subnav control. Hides the logged-in
    // chrome (settings/admin/edit/picker) in the template.
    demoShowcase:     false,
    // Playback position, seconds. A fresh visitor joins where the server says
    // (window.DEMO.open_at, ~18% in) so the first frame is a board that is
    // already alive. t=0 is kick-off and is CORRECT - nothing has happened yet
    // - but it is a poor showcase: measured on prod, the first non-zero number
    // arrives ~16.8s after landing. Replay still starts at 0, deliberately:
    // by then the visitor has seen the game and "Replay" means from the top.
    demoT:            (window.DEMO && +window.DEMO.open_at) || 0,
    demoPlaying:      true,     // ▶ / ❚❚
    demoSpeed:        0.5,      // 0.25 / 0.5 / 1 / 2 / 4 — default 0.5× so first-time
                                // visitors land on the calmer pace (Xav 2026-07-10)
    demoDuration:     120,      // total length (overwritten from payload.duration_secs)
    demoEnded:        false,    // reached the end — button offers Replay
    _demoTimer:       null,
    _demoSeq:         0,        // guards against a restart racing an in-flight fetch
    // Demo picker (showcase subnav) — the current demo + the full list, so a
    // visitor can hop between sports. Populated from window.DEMO / DEMO_LIST.
    demoSlug:         '',
    demoName:         '',
    demoSport:        '',
    demoList:         [],

    // ── Mobile/iPad SHELL (docs/mobile_shell_spec.md, Phase 1) ──────────────
    // A touch-first bottom-tab shell that replaces the stacked desktop grid on
    // a phone: each tab is ONE full-screen widget (reusing the same templates).
    // Scoped to the showcase demo on a phone for NOW (drop demoShowcase in the
    // getter to roll it out to every mobile user). shellTab = the active tab.
    shellTab:         'live',
    _vidiRulesBySport: {},
    scoreChangeFeed:  [],
    scoreBumpIds:     {},
    fptToasts:        [],
    fptDeltaMap:      {},
    // Panel show/hide bools deleted 2026-06-06 — leftover from v1 when
    // there were static panel divs in live.html. In v2 widget visibility
    // is layout-driven (Edit pill → × on a widget). The ls_panel_* keys
    // were never read by any v2 template — confirmed by grep.
    flashIds:         {},

    // Scoreboard pin set. User-curated subset of live games shown on the
    // top-row Scoreboard. On first visit to a slate we auto-pin the first
    // PIN_CAP live games so the user lands on something useful; localStorage
    // takes over after the first manual edit. Tennis caps at 6 because each
    // Wimbledon-style card is wider than a team-sport card.
    pinnedGames:        new Set(),
    // Subset of pinnedGames tracking pins the user added MANUALLY via
    // togglePin (from the Matches widget pin button). Auto-prune of
    // finished games skips entries in this set so a user-pinned final
    // box-score sticks around until the user removes it.
    _userPinnedGames:   new Set(),
    _pendingAutoPin:    false,
    get PIN_CAP() {
      // 15 ceiling (Xav 2026-06-01): scoreboard supports multi-row
      // layouts now (1 row of advanced cards ≤5, or 2-3 rows of
      // compact cards 6-15). Tennis kept tighter — the Wimbledon-
      // style card is wider so 8 fits the visual envelope.
      const sport = (this.selectedSlate?.sport_id || this.selectedSlate?.sport?.id || '').toLowerCase();
      return sport === 'tennis' ? 8 : 15;
    },

    /** Tiered card width for the headline scoreboard. Locked 2026-05-27:
     *  fewer cards → bigger per-card width, so 1 game doesn't stretch
     *  ugly and 7 games stay readable. Frame width math always matches
     *  the row content. Used by .mlb-scoreboard's inline :style binding
     *  to set the --ls-card-w CSS variable (consumed by .mlb-card's
     *  flex-basis + width + max-width). */
    get scoreboardCardWidth() {
      const n = (this.scoreboardGames || []).length;
      const widths = [null, 460, 380, 320, 280, 260, 230, 210];
      return widths[n] || 280;
    },

    /** Widget requirements + scale-hint helpers (Xav 2026-05-27).
     *  Drive the picker pills + on-tile ⓘ for mismatch states. Hard
     *  requirements ('projections', 'lineups', etc.) prevent a KPI from
     *  rendering useful output; soft `bestAt` is a scale hint.
     *
     *  requirementMet(name) — true if the user's current state satisfies
     *  the named requirement.
     *  requirementLabel(name) — short user-facing label, e.g. 'projections'
     *  → 'needs projections'. Used in pill text.
     *  lineupScaleTier() — 'solo' | 'multi'. Threshold at 5 lineups
     *  feels right for MLB DFS (solo player has 1-3, mid-volume 10+,
     *  grinder 50+).
     *  bestAtMismatch(bestAt) — true if widget's scale hint doesn't
     *  match the user's current lineup count tier. 'any' never mismatches. */
    requirementMet(name) {
      switch (name) {
        case 'projections':
          return !!this.hasProjections;
        case 'lineups':
          return (this.nLineups || 0) > 0;
        case 'pitchers': {
          // MLB SP detection — any lineup row contains a player whose
          // pos string includes 'SP' or 'P'.
          const PITCHER = new Set(['SP', 'P', 'RP']);
          return (this.lineupRows || []).some(lu =>
            (lu.players || []).some(p => PITCHER.has(p.pos)),
          );
        }
        case 'hitters': {
          const PITCHER = new Set(['SP', 'P', 'RP']);
          return (this.lineupRows || []).some(lu =>
            (lu.players || []).some(p => p.pos && !PITCHER.has(p.pos)),
          );
        }
        case 'stacks': {
          // A "stack" = 2+ HITTERS from the same team in the same lineup.
          // Exclude pitchers so this gate matches the Best-Stack tile's leader
          // (kpi_mlb.stackLeader), which counts hitters only — otherwise the
          // gate could unlock while the tile shows "no stacks yet".
          const isPitcher = (p) => ['SP', 'RP', 'P'].includes((p.pos || '').toUpperCase());
          for (const lu of (this.lineupRows || [])) {
            const teams = {};
            for (const p of (lu.players || [])) {
              if (isPitcher(p)) continue;
              const t = (p.team || '').toUpperCase();
              if (!t) continue;
              teams[t] = (teams[t] || 0) + 1;
              if (teams[t] >= 2) return true;
            }
          }
          return false;
        }
        case 'live_data':
          // Use effStatus (not raw .status) so a game whose provider is stuck
          // at status=1 but whose wall-clock floor says live still unlocks the
          // gate — consistent with every other display decision in this file.
          return (this.allGames || []).some(g => this.effStatus(g) === 2);
        default:
          return true;   // unknown requirement → don't block (forward-compat)
      }
    },
    requirementLabel(name) {
      const labels = {
        projections: 'needs projections',
        lineups:     'needs a lineup',
        pitchers:    'needs a pitcher',
        hitters:     'needs a hitter',
        stacks:      'needs a stack',
        live_data:   'needs a live game',
      };
      return labels[name] || ('needs ' + name);
    },
    /** Coarse classification of the user's MLB lineup count. Threshold
     *  at 5: 1-4 lineups feels "solo" (narrative-focused); 5+ is
     *  "multi" (aggregating). Tweak after some live use. */
    lineupScaleTier() {
      return (this.nLineups || 0) >= 5 ? 'multi' : 'solo';
    },
    bestAtMismatch(bestAt) {
      if (!bestAt || bestAt === 'any') return false;
      return bestAt !== this.lineupScaleTier();
    },
    /** Human-readable scale-pill text for the picker. */
    bestAtLabel(bestAt) {
      if (!bestAt || bestAt === 'any') return 'any scale';
      if (bestAt === 'solo')  return 'best for solo';
      if (bestAt === 'multi') return 'best at scale';
      return bestAt;
    },

    /** My Best Player — highest-FPts unique player across all my
     *  lineups. Returns {name, team, live_fpts} or null if no
     *  lineups. Xav 2026-05-27. Sport-agnostic (`*` scope). */
    get _myBestPlayer() {
      const seen = new Set();
      let best = null;
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          const k = String(p.player_id || p.external_player_id || '');
          if (seen.has(k)) continue;
          seen.add(k);
          if (!best || (p.live_fpts || 0) > (best.live_fpts || 0)) {
            best = p;
          }
        }
      }
      return best;
    },

    /** My Best Lineup — top lineup by live_total. Returns
     *  {lineup, rank, total_count} or null. The rank object lets the
     *  KPI foot show "#1 of N" context at scale. */
    get _myBestLineup() {
      const lus = (this.lineupRows || []);
      if (!lus.length) return null;
      const sorted = [...lus].sort((a, b) => (b.live_total || 0) - (a.live_total || 0));
      return { lineup: sorted[0], rank: 1, total_count: lus.length };
    },

    /** Graph-library KPIs (Xav 2026-06-06 split of the Graphs panel).
     *  Spread = max-min lineup total. Null when <2 lineups; the KPI
     *  partial uses null as the "—" sentinel. */
    get _kpiLineupSpread() {
      const lus = (this.lineupRows || []);
      if (lus.length < 2) return null;
      const totals = lus.map(l => l.live_total || 0);
      return Math.max(...totals) - Math.min(...totals);
    },

    /** Hottest player = slate-wide leader by live_fpts ÷ proj. Only
     *  players with a positive projection AND non-zero live points AND
     *  a started game qualify — otherwise the ratio is undefined or
     *  meaningless. Returns the player object with an extra `_ratio`
     *  field so the KPI partial can show "180%" inline. */
    get _kpiHottestPlayer() {
      const pool = (this.allSlatePlayers || [])
        .filter(p => (p.proj || 0) > 0
                  && p.game_phase
                  && p.game_phase !== 'not_started'
                  && (p.live_fpts || 0) > 0);
      if (!pool.length) return null;
      return pool.map(p => ({ ...p, _ratio: (p.live_fpts || 0) / (p.proj || 1) }))
                 .sort((a, b) => b._ratio - a._ratio)[0];
    },

    // _kpiColdestPlayer getter retired 2026-06-15 with the kpi_coldest_player
    // widget — "coldest" was a weak headline (end-of-slate zero pile-up).

    // ── MLB premium KPIs ────────────────────────────────────────────
    // Logic lives in static/js/dashboard/kpi_mlb.js (LSKpiMlb.*) to
    // keep sport-specific reductions out of the foundation controller.
    // Names preserved so widgets keep calling dashboard._mlbMyBombs etc.
    _mlbTopScorer(g) {
      // Memoise per (pool, game). The scoreboard calls this ~4× per game across
      // its bindings (scoreboard_mlb.html) and each call filtered the whole
      // ~995-player pool — 12 games × 4 × 995 ≈ 48k iterations PER render, re-run
      // on every reactive tick. That was the pre-game load's ~3.6s (Xav found
      // it). Cache resets when applyData swaps in a new pool array; recompute
      // once per game per commit.
      if (!g || !g.game_id) return null;
      const pool = this.allSlatePlayers;
      if (!_topScorerCache || _topScorerCache.pool !== pool) _topScorerCache = { pool, byGame: {} };
      const gid = g.game_id;
      if (!(gid in _topScorerCache.byGame)) _topScorerCache.byGame[gid] = LSKpiMlb.topScorer(this, g);
      return _topScorerCache.byGame[gid];
    },
    get _mlbMyBombs()              { return LSKpiMlb.myBombs(this); },
    get _mlbPitcherLines()         { return LSKpiMlb.pitcherLines(this); },
    get _mlbCooking()               { return LSKpiMlb.cooking(this); },
    get _mlbSweatMeter()           { return LSKpiMlb.sweatMeter(this); },
    get _mlbStackLeader()          { return LSKpiMlb.stackLeader(this); },
    _mlbPaddedInnings(g)           { return LSKpiMlb.paddedInnings(this, g); },

    /** Phase: scoreboard cap at 7 (Xav 2026-05-27).
     *  Returns the SUBSET of pinned games that actually render on the
     *  scoreboard, capped at PIN_CAP and priority-sorted. Both the MLB
     *  bespoke scoreboard partial and the wildcard partial iterate
     *  this — user can pin more than 7 (via Matches widget), but only
     *  the top 7 by status priority display. Sort order:
     *    1. Live   (status === 2) — most relevant, always wins
     *    2. Soon   (status === 1) — upcoming today
     *    3. Final  (status === 3) — historical, deprioritised
     *  Within a status tier, earlier start_time wins (chronological).
     *
     *  Matches widget cog option `onlyMyPlayers` (if ON) additionally
     *  filters to games where the user has a lineup player on either
     *  team. Falls back to "all pinned" if the filter would yield zero
     *  results — so the scoreboard never goes blank just because the
     *  user toggled the option. */
    /** Broadcast: the rail shows the whole slate - live, then to come, then
     *  final - until you pick games yourself (a hand pin or a preset). The
     *  first-visit auto-pin kept live games only and the prune dropped each
     *  one as it finished, so a 12-game slate read as one card in an empty
     *  rail (v2 run 1, UX-2 / PREM-3). Simple / Analytical keep their pins. */
    get _sbShowsAll() {
      return this.activeSkin === 'broadcast' && !this.sbFollowMine
        && !(this._userPinnedGames && this._userPinnedGames.size);
    },
    get scoreboardGames() {
      const sport = (this.selectedSlate?.sport_id || this.selectedSlate?.sport?.id || '').toLowerCase();
      // "My player's games" is a MODE, not a one-off pin (Xav 2026-09-29): the
      // board follows every game holding one of my players, so a lineup edit or
      // a new slate game re-scopes it without re-picking. Anything else - All,
      // Live only, a hand-picked game - drops back to the pin set.
      const mine = this.sbFollowMine ? this._myTeamKeys : null;
      const every = !mine && this._sbShowsAll;
      const all = (this.allGames || []).filter(g =>
        g.sport_id === sport && (every || (mine
          ? this._gameTeamKeys(g).some(k => mine.has(k))
          : this.pinnedGames.has(g.game_id))),
      );
      // Note: the "Only my players" filter (formerly per-sport opt on
      // the matches widget) was removed 2026-06-01 along with the
      // Matches widget itself. The Scoreboard cog popover now offers
      // the same intent via its "My player's games" preset
      // (pinMyPlayerGames host method), which writes directly to
      // pinnedGames rather than re-filtering at render time.
      const bucket = (s) => s === 2 ? 0 : s === 1 ? 1 : 2;
      return [...all].sort((a, b) => {
        const ba = bucket(a.status), bb = bucket(b.status);
        if (ba !== bb) return ba - bb;
        // Same field mix-up as the countdown had: games sort by game_time_utc.
        return (a.game_time_utc || a.start_time || '')
          .localeCompare(b.game_time_utc || b.start_time || '');
      }).slice(0, this.PIN_CAP);
    },

    /** Shared scoreboard density resolver. Every bespoke per-sport
     *  scorecard reads Auto / Full / Compact from its own cog option and
     *  falls back to "compact once there are more than N pinned games" —
     *  only the widget id + the auto threshold differ (MLB 5, NBA 4, …).
     *  Widgets delegate their _isCompact getter here instead of each
     *  re-implementing the same three-line resolution. Lives on the
     *  controller so the widget x-data reaches it via the Alpine
     *  prototype chain. (Xav 2026-07-04 — shared scoreboard shell.) */
    /** The scoreboard follows my players' games (a persistent mode). Only
     *  when I have lineups - a spectator has no "mine" to follow. */
    get sbFollowMine() {
      return typeof this.getOpt === 'function'
        && this.getOpt('scoreboard', 'follow', 'pins') === 'mine'
        && this._myTeamKeys.size > 0;
    },
    /** Team keys (id first) of every player in my lineups. */
    get _myTeamKeys() {
      const mine = new Set();
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          const k = this._teamKey(p);
          if (k) mine.add(k);
        }
      }
      return mine;
    },
    /** How many slate games hold one of my players - the popover's count. */
    get myGamesCount() {
      const mine = this._myTeamKeys;
      return mine.size ? (this.allSortedGames || []).filter(g => this._gameTeamKeys(g).some(k => mine.has(k))).length : 0;
    },
    /** The Broadcast board's "mine" line on a scoreboard card: who of mine
     *  is in this game (either side, joined by team id like _myTeamKeys -
     *  never by name), each with their OWN points, and how many of my
     *  lineups have a stake in it. Never a sum: adding players from different
     *  lineups made a figure no lineup scored (KC@LV 99.70 while the leader
     *  got 0 from it - Broadcast audit run 1, NUM-1, Xav P1). Before kick-off
     *  points would all read 0.00, so it counts players instead.
     *  null = none of mine. */
    sbMine(g) {
      if (!g) return null;
      const keys = new Set(this._gameTeamKeys(g));
      if (!keys.size) return null;
      const lus = this.activeLineupRows || this.lineupRows || [];
      const seen = new Map();
      let stake = 0;
      for (const lu of lus) {
        let here = false;
        for (const p of (lu.players || [])) {
          const k = this._teamKey(p);
          if (!k || !keys.has(k)) continue;
          here = true;
          const id = p.player_id || p.external_player_id;
          if (id && !seen.has(id)) seen.set(id, p);
        }
        if (here) stake++;
      }
      if (!seen.size) return null;
      const ps = [...seen.values()].sort((a, b) => (Number(b.live_fpts) || 0) - (Number(a.live_fpts) || 0));
      const R = window.LSRace;
      // One namer per rows array (memoised): built from all 1,350 players at
      // 150 lineups, per game card, per render (Phase 10).
      let nm = _sbNamer.get(lus);
      if (!nm) {
        nm = R && R.namer ? R.namer(lus.flatMap(lu => lu.players || [])) : (p) => p.name;
        _sbNamer.set(lus, nm);
      }
      const fmt = (v) => (typeof this._fmtScore === 'function' ? this._fmtScore(v) : (Number(v) || 0).toFixed(2));
      const started = (g.status || 0) >= 2;
      const top = started ? ps.slice(0, 2).map(p => ({ name: nm(p), pts: fmt(p.live_fpts) })) : [];
      const more = started ? ps.length - top.length : 0;
      return {
        top, more,
        // the "+N" says who it is on hover (Xav 2026-10-06)
        rest: started ? ps.slice(top.length).map(p => nm(p) + ' ' + fmt(p.live_fpts)).join('\n') : '',
        count: started ? '' : ps.length + (ps.length === 1 ? ' player' : ' players'),
        stake: lus.length > 1 ? 'in ' + stake + '/' + lus.length : '',
      };
    },
    _sbFollow(mode) {
      if (typeof this.setOpt === 'function' && this.getOpt('scoreboard', 'follow', 'pins') !== mode) {
        this.setOpt('scoreboard', 'follow', mode);
      }
    },

    _sbCompact(widgetId, autoThreshold) {
      const d = this.getOpt(widgetId, 'density', 'auto');
      if (d === 'compact') return true;
      if (d === 'full')    return false;
      return (this.scoreboardGames || []).length > autoThreshold;
    },

    /* abbrev → logo path. Built from two sources:
       1. allSlatePlayers (server enriches each player with team_logo)
       2. games' home_logo / away_logo (always populated on the games payload)
       The games-fallback covers any case where platform_teams lookup misses. */
    get _teamLogos() {
      return _teamMaps(this.allSlatePlayers, this.allGames).logos;
    },

    // Active team_token mode (A–E) — see _setTtMode. Persisted per-browser.

    /* Team primary colour by abbrev — mirrors _teamLogos, sourced from the
       games payload (home_primary/away_primary, from teams.primary_color via
       core/slate_live.py). Feeds the shared team_token colour modes. */
    get _teamColors() {
      return _teamMaps(this.allSlatePlayers, this.allGames).colors;
    },
    teamColor(abbr) { return (this._teamColors || {})[(abbr || '').toUpperCase()] || ''; },
    // Contrast text colour for a fill hex — dark ink on light teams, white on dark.
    teamText(hex) {
      if (!hex) return '';
      const n = parseInt(String(hex).replace('#', ''), 16);
      if (isNaN(n)) return '#ffffff';
      const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#0f172a' : '#ffffff';
    },
    /** A team plate's colours that read (WCAG AA, 4.5:1): white or dark ink,
     *  whichever has more contrast on the team's colour; when neither does
     *  (a mid blue - LAD's plate was 3.86:1), the colour darkens until white
     *  does. Returns a style string: "--tc:#..;color:#.." (audit v2 run 3,
     *  COLOUR-5). */
    plateCss(hex) {
      const parse = (h) => { const n = parseInt(String(h || '').replace('#', ''), 16); return isNaN(n) ? null : [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
      const lum = (c) => { const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }; return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); };
      const cr = (a, b) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
      const hex2 = (c) => '#' + c.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
      let c = parse(hex) || [42, 52, 64];
      const W = [255, 255, 255], K = [15, 23, 42];
      if (cr(W, c) >= 4.5) return '--tc:' + hex2(c) + ';color:#ffffff';
      if (cr(K, c) >= 4.5) return '--tc:' + hex2(c) + ';color:#0f172a';
      for (let i = 0; i < 20 && cr(W, c) < 4.5; i++) c = c.map(v => v * 0.9);
      return '--tc:' + hex2(c) + ';color:#ffffff';
    },

    /* ── Sport-aware identity for the team_token ─────────────────────────
       Team sports: team logo + team abbrev. Individual sports (each matchup
       "team" is a single player): player photo + 3-char surname. Static set,
       mirrors server _INDIVIDUAL_SPORTS (core.sports). */
    _INDIV_SPORTS: ['tennis', 'mma', 'pga', 'golf', 'nascar', 'f1'],
    get _indivSport() {
      return this._INDIV_SPORTS.includes((this.selectedSlate?.sport_id || '').toLowerCase());
    },
    _shortName(name) {
      const parts = String(name || '').trim().split(/\s+/);
      return (parts.length ? parts[parts.length - 1] : '').slice(0, 3).toUpperCase();
    },
    /** Team logos ship at 256px and this app draws them at 16-40px, so four of
     *  them were 106KB - most of the image weight on the live screen (audit
     *  2026-09-13). Point at the 128px WebP built beside them
     *  (scripts/build_team_logos.py): covers 40px at DPR 3, ~6KB instead of
     *  ~26KB. Only rewrites OUR logo paths; a player photo or an external URL
     *  is passed through untouched, and /tv builds its own paths so the
     *  broadcast board keeps the full-size art. */
    _smallLogo(src) {
      return (typeof window.lsSmallLogo === 'function') ? window.lsSmallLogo(src) : (src || '');
    },
    identImg(p) {
      if (!p) return '';
      return this._indivSport ? (p.player_photo || '')
        : this._smallLogo(p.team_logo || this._teamLogos[(p.team || '').toUpperCase()] || '');
    },
    identLabel(p) {
      if (!p) return '';
      return this._indivSport ? this._shortName(p.name) : (p.team || '');
    },


    get vidiStream() {
      // Build dedup keys from scorer events first so the PbP firehose
      // can be filtered against them. Key = `${game_pk}:${at_bat_index}`
      // — scorer events carry these fields when the underlying provider
      // tags them (MLB does via core/scorers/mlb.py). When a scorer event
      // covers the same at-bat as a PbP event, the scorer wins (it has
      // the FPts delta + animation hooks; PbP only has broadcast text).
      const sport     = (this.selectedSlate?.sport_id || '').toLowerCase();
      // Routine plays (the no-point pbp firehose — outs, Ks) default OFF: the
      // wire reads cleaner as a scoring feed. Opt in via the wire cog. Keep in
      // sync with the settings.js defaults (vidi_mlb_routine).
      const routineOn = this.vidiPref(sport, 'routine', false);
      // Min FPts is a fantasy-points threshold: a single-stat slate scores a
      // count (a goal is 1), so a min of 2 hid its whole Wire (NHL Points,
      // Xav 2026-10-11). Only formats scored in FPts apply it.
      const fpUnit    = !window.LSFormat || window.LSFormat.traitsOf(this.formatVariant || '').unit === 'FPts';
      const minDelta  = fpUnit ? Math.abs(Number(this.vidiPref(sport, 'min_delta', 0)) || 0) : 0;
      // Score-change bands never reach The Wire (Xav 2026-10-13): the scores
      // have their own timeline, the scoreboard's Plays view, so The Wire is
      // player plays only. The "Score events" switch is gone with it - an old
      // saved "on" (vidi_<sport>_score_events) no longer brings them back.
      const isMatchEvt = e => e && (e.event_type === 'start' || e.event_type === 'end' ||
        e.event_type === 'ot' || e.event_type === 'oncourt' || e.event_type === 'offcourt' ||
        e.event_type === 'score');   // score-change band = a game-level event, never min-delta dropped

      const scorerKeys = new Set();
      for (const e of this.feed) {
        if (e.game_pk != null && e.at_bat_index != null) {
          scorerKeys.add(e.game_pk + ':' + e.at_bat_index);
        }
      }
      // Scorer feed — apply the Min-FPts threshold (magnitude), but never drop
      // a match-event divider on it (those are gated by their own toggle).
      let feed = this.feed;
      if (minDelta > 0) {
        feed = feed.filter(e => isMatchEvt(e) || Math.abs(Number(e.fpts) || 0) >= minDelta);
      }
      feed = feed.filter(e => e.event_type !== 'score');
      // Seeded scoring plays — what happened before the page was opened.
      // The scoring tier is derived by diffing polls, so a restart leaves it
      // empty until new plays land, while the play-by-play refills instantly
      // from the provider. These come from slate_score_events (the same read
      // that feeds Big Moments) and are deduped against anything live.
      let seedLayer = [];
      if (this._wireSeed && this._wireSeed.length) {
        const liveIds = new Set(feed.map(e => e.id));
        const liveAt  = new Set(feed.map(
          e => String(e._pid || e.player_id || '') + ':' + String(e.total)));
        seedLayer = this._wireSeed.filter(sd =>
          !liveIds.has(sd.id) &&
          !liveAt.has(String(sd.player_id || '') + ':' + String(sd.total)));
        // The stored Wire carries the live rows as they were, score bands and
        // all, so the same toggles apply to it as to the live feed above.
        if (minDelta > 0) seedLayer = seedLayer.filter(e => isMatchEvt(e) || Math.abs(Number(e.fpts) || 0) >= minDelta);
        seedLayer = seedLayer.filter(e => e.event_type !== 'score');
      }

      // PbP layer = the "routine plays" firehose (grey, no-point at-bats).
      // Included only when density > 'mine' AND the Routine-plays toggle is on.
      // Pre-dedup against the scorer set so a scored play fires once, not twice.
      //
      // AND never on its own. This layer is texture around scoring plays; with
      // the scoring tier empty after a restart it became the whole wire — nine
      // rows of "grounds out, +0.00" — which is not what the toggle is for. An
      // honest empty wire beats a page of noise.
      const haveScoring = feed.length > 0 || seedLayer.length > 0;
      let pbpLayer = [];
      if (routineOn && haveScoring && this.vidiDensity !== 'mine'
          && this._pbpFeed && this._pbpFeed.length) {
        pbpLayer = this._pbpFeed.filter(e => {
          if (this.vidiDensity === 'big' && e.salience !== 'big') return false;
          if (e.game_pk != null && e.at_bat_index != null) {
            const k = e.game_pk + ':' + e.at_bat_index;
            if (scorerKeys.has(k)) return false;
          }
          return true;
        });
      }
      // Merge by id (de-dup within stream too), keeping scorer order then
      // PbP order. Both feeds are stored newest-first so a straight concat
      // keeps newest-first overall.
      const seen   = new Set();
      const merged = [];
      for (const e of [...feed, ...seedLayer, ...pbpLayer]) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        merged.push(e);
      }
      // Placed by the most exact thing the event carries: its own game_id
      // first, then _teamKey (team_id, else the label). The two streams merged
      // here do not carry the same fields - scorer events are stamped
      // server-side, the play-by-play tier ships raw - so an event that
      // identifies neither still passes, as it always did. This filter narrows
      // to the chosen games; it does not hide what it cannot place.
      const _keys = this.selectedTeamKeys;
      const filtered = merged.filter(e => {
        // A single-stat slate scores a count: a play worth none of it (a shot
        // on an NHL Points slate) is not on its Wire - it read "+0 PTS"
        if (!fpUnit && e && e.rule_id != null && !(Number(e.fpts) || 0)) return false;
        if (this.selectedGames.size === 0) return true;
        if (e.game_id != null) return this.selectedGames.has(e.game_id);
        const k = this._teamKey(e);
        return !k || _keys.has(k);
      });
      // Single-stat money-event lock. These formats score exactly ONE stat, so
      // the play-by-play firehose (singles, walks, routine outs) is pure noise —
      // only the money event matters. Lock the feed to that event's rule_id,
      // resolved from the format's metadata (window.LSFormat.meta[fv].vidi_event,
      // e.g. single_stat_hr → 'mlb_home_run'), keeping bare game-state dividers
      // (start/end/OT) for rhythm. Standard formats fall straight through.
      // The money event to lock the feed to is a format trait (vidi_event) —
      // '' for standard/tiers (no filter), set for single-stat variants.
      const fv = this.formatVariant || '';
      const moneyEvent = (window.LSFormat ? window.LSFormat.traitsOf(fv).vidi_event : '') || '';
      if (moneyEvent) {
        const isDivider = e => e && (e.event_type === 'start' ||
          e.event_type === 'end' || e.event_type === 'ot');
        return this._markCorrections(this._retractReversed(filtered).filter(e => e.rule_id === moneyEvent || isDivider(e)));
      }
      return this._groupPlays(this._restampTotals(this._foldCorrections(this._markCorrections(this._retractReversed(filtered)))));   // newest-first, merged + deduped + game-filtered
    },
    /** Every running total on the Wire, from the board's own numbers, in the
     *  order the Wire shows. Rows arrive from three places - stored rows
     *  stamped when they were saved, the snapshot's restamp, the live drip -
     *  each with its total from a different moment, so one man's column went
     *  9.75 under 13.25 for a later rebound (Xav 2026-10-15). Now: his newest
     *  row carries what he is on NOW (the board's live_fpts) and each older
     *  row peels off the points of the rows above it, so the column always
     *  adds up down the Wire. A player the board does not hold keeps his. */
    _restampTotals(list) {
      const cur = {};
      for (const p of ((this.allSlatePlayers && this.allSlatePlayers.length) ? this.allSlatePlayers : (this.players || []))) {
        const k = String(p.player_id || '');
        if (k && p.live_fpts != null) cur[k] = Number(p.live_fpts) || 0;
      }
      if (!list || !list.length || !Object.keys(cur).length) return list;
      const run = Object.assign({}, cur);
      return list.map(e => {
        if (!e || e.rule_id == null || e.event_type === 'score' || e.event_type === 'corr_sum') return e;
        const k = String(e._pid || e.player_id || '');
        if (!k || !(k in run)) return e;
        const t = run[k];
        run[k] = t - (Number(e.fpts) || 0);
        return Object.assign({}, e, { total: Math.round(t * 100) / 100 });
      });
    },
    /** A play the league took back leaves the Wire with the row that takes it
     *  back. Each reversal row (a key stat going DOWN - workers/live_poller.py
     *  _reversals) cancels that player's latest earlier row of the same rule,
     *  and both go: the goal re-credited from Killorn to Nesterenko read as two
     *  scorers of one goal (ANA @ CGY, Xav 2026-10-11). A reversal with nothing
     *  to cancel (its play scrolled out) stays, as a Correction. */
    _retractReversed(list) {
      if (!list.some(e => e && e.reverses)) return list;
      const drop = new Set(), open = new Map();
      for (let i = list.length - 1; i >= 0; i--) {             // oldest first
        const e = list[i];
        if (!e || !e.rule_id) continue;
        const k = String(e._pid || e.player_id || '') + ':' + (e.reverses || e.rule_id);
        if (e.reverses) {
          const st = open.get(k);
          if (st && st.length) { drop.add(st.pop()); drop.add(e.id); }
        } else if ((Number(e.fpts) || 0) > 0) {
          if (!open.has(k)) open.set(k, []);
          open.get(k).push(e.id);
        }
      }
      return drop.size ? list.filter(e => !e || !drop.has(e.id)) : list;
    },
    /** One goal, one row (Xav 2026-10-09, mock claude.ai/artifact/KfzvpU59ZLK7TWDJpUeJs6):
     *  the scorer's, the assists' and the goalie's rows of ONE NHL goal share
     *  the play's id (the poller hands each the goal's at_bat_index by NHL
     *  player id - _borrow_play), and fold into one card with a line per
     *  player: his points from the play and his running total, as each row
     *  showed alone. Nothing is summed, so it reads the same with no lineup;
     *  a player you hold is lit with "in N/M". The card sits where its newest
     *  row was; an assist that lands a poll later joins it. All four sports
     *  whose rows carry the play id: NHL, NFL (passer + receiver), NBA (basket
     *  + assist), MLB (batter + pitcher of one at-bat). */
    _groupPlays(list) {
      const sport = String((this.selectedSlate && this.selectedSlate.sport_id) || '').toLowerCase();
      // every sport whose play-by-play names the players in a play (Xav
      // 2026-10-09: NHL goal + assists; NFL pass + catch; NBA basket +
      // assist; MLB the batter + the pitcher of one at-bat)
      if (!['nhl', 'nfl', 'nba', 'mlb'].includes(sport) || !list || !list.length) return list;
      const keyOf = (e) => (e && e.event_type !== 'score' && e.event_type !== 'corr_sum' && !e.is_correction
                            && e.at_bat_index != null && e.game_id != null && e.rule_id != null)
                           ? String(e.game_id) + ':' + e.at_bat_index : null;
      const groups = new Map();
      for (const e of list) { const k = keyOf(e); if (k) (groups.get(k) || groups.set(k, []).get(k)).push(e); }
      // A live feed row carries its player as `_pid` (event_detection), a
      // stored or seeded one as `player_id` - reading only the second made
      // every live play look like one player, so nothing ever grouped and no
      // row was ever marked yours (Broadcast audit v2 run 3, WIRE-3 / WIRE-7)
      const idOf = (e) => String((e && (e._pid || e.player_id || e.external_player_id)) || '');
      const isGoalPlay = (es) => es.length > 1 && new Set(es.map(idOf)).size > 1;
      const inMap = this._inCountMap || {}, nLu = (this.activeLineupRows || this.lineupRows || []).length;
      // who leads the card: the one who made the play (the scorer, the passer,
      // the batter), then who helped, then who was on the wrong end of it
      const role = (e) => {
        const l = String(e.label || '');
        if (e.is_goalie || e.is_pitcher || /against|allowed|earned run/i.test(l)) return 2;
        return /assist|catch|reception|receiving/i.test(l) ? 1 : 0;
      };
      const out = [], done = new Set();
      for (const e of list) {
        const k = keyOf(e), es = k ? groups.get(k) : null;
        if (!es || !isGoalPlay(es)) {
          // a single row says it is yours as a play card does ("in 2/3",
          // "yours") - 3 of my players on the demo's Wire, 0 markers (audit
          // v2 run 3, WIRE-7). Never a multiplied number, a count.
          const n = (e && e.rule_id != null && !e.is_correction) ? (inMap[idOf(e)] || 0) : 0;
          out.push(n ? Object.assign({}, e, { _own: nLu > 1 ? n + '/' + nLu : 'yours' }) : e);
          continue;
        }
        if (done.has(k)) continue;
        done.add(k);
        const ms = es.slice().sort((a, b) => role(a) - role(b));
        const scorer = ms.find(m => role(m) === 0) || ms[0];
        out.push({
          id: 'pg:' + k, event_type: 'play_group', game_id: e.game_id, at_bat_index: e.at_bat_index,
          // NHL: a goal names the card - but a shot and its save are a
          // two-player play too, and read "Goal" (Xav 2026-10-11)
          tag: (sport === 'nhl' && ms.some(m => /^(goal|assist)$/.test(String(m.rule_id))))
            ? 'Goal' : String(scorer.label || 'Play'),
          game_state: e.game_state, gameTime: e.gameTime, team: scorer.team, team_logo: scorer.team_logo,
          desc: (ms.find(m => m.desc) || {}).desc || '', ts: e.ts,
          _rows: ms,   // the plays inside, for Big Moments (_vidiLanes reads rows, not cards)
          members: ms.map(m => {
            const pid = idOf(m), n = inMap[pid] || 0;
            // his own team and crest: the card leads with each player's badge
            return { name: m.name, team: m.team, team_logo: m.team_logo, pos: m.pos, label: m.label, fpts: m.fpts, total: m.total, rule_id: m.rule_id,
                     own: n && nLu > 1 ? n + '/' + nLu : (n ? 'yours' : '') };
          }),
        });
      }
      return out;
    },
    /** Every game final: the provider's overnight stat corrections stop
     *  leading the Wire - 9 of NHL's top 10 rows were "Points update" - and
     *  fold into ONE row at the top, "4 stat corrections · net −3.20", each
     *  one in its tooltip (Xav 2026-10-09, run 2 UX-11 / LIVE-10: 1a). */
    _foldCorrections(list) {
      const gs = this.allGames || [];
      if (!gs.length || !gs.every(g => this.effStatus(g) >= 3)) return list;
      // A correction is what _markCorrections (or the server) FLAGGED as one -
      // never a row picked out by its words. Folding every "Points update"
      // turned a whole night's scoring into "18 stat corrections, +201.00
      // net" (Broadcast audit v2 run 3, WIRE-2 / NUMBERS-1 / CODE-1).
      const corr = list.filter(e => e && e.is_correction), rest = list.filter(e => !(e && e.is_correction));
      if (!corr.length) return list;
      // ...and they follow the night's plays, never lead them: one alone at
      // the bottom, two or more as one summary row there (WIRE-4)
      if (corr.length < 2) return [...rest, ...corr];
      const net = corr.reduce((t, e) => t + (Number(e.fpts) || 0), 0);
      const lines = corr.slice(0, 20).map(e => (e.name || '') + ' ' + ((Number(e.fpts) || 0) >= 0 ? '+' : '') + (Number(e.fpts) || 0).toFixed(2));
      return [...rest, { id: 'corr-sum', event_type: 'corr_sum', is_correction: true, n: corr.length, fpts: Math.round(net * 100) / 100,
                tip: lines.join('\n') + (corr.length > 20 ? '\nand ' + (corr.length - 20) + ' more' : '') }];
    },

    /** A stat correction is not a play. The feed revising a box score the
     *  next morning came through labelled as the play it amended - "Kirk
     *  Cousins · pass td −1.00", "run · 1 yd +2.20" (Broadcast audit run 1,
     *  NUM-9). An event is a correction when it lands more than 5h after its
     *  game started (no game runs that long), or when it takes points away
     *  under the name of a play that only ever adds them, or calls a few
     *  tenths a touchdown. Its clock gets the
     *  day when it is not the game's day (NUM-19). */
    _markCorrections(list) {
      const games = new Map((this.allGames || []).map(g => [String(g.game_id), g]));
      // Seeded rows (the slate's score log) carry the player, not the game.
      const gameOfPlayer = new Map((this.allSlatePlayers || []).map(p => [String(p.player_id || ''), p.game_id]));
      // Football words: a "run" or a "pass" that TAKES points is a revision.
      // In baseball an Earned Run takes points and is a real play, so these
      // word tests are NFL's alone (Broadcast audit v2 run 3, CODE-2).
      const isNfl = String((this.selectedSlate && this.selectedSlate.sport_id) || '').toLowerCase() === 'nfl';
      const scoring = /\b(td|touchdown|yds?|yards?|rec|reception|catch|run|rush|pass|bonus)\b/i;
      return list.map(e => {
        if (!e || e.is_correction || !(Number(e.fpts) || 0)) return e;
        // A rebuilt row worth the player's whole total is the first time the
        // log saw him - the night's points arriving together, not a revision
        // of them. Logged late (the log started after the games) it read as
        // "18 stat corrections, +201.00" (WIRE-2).
        if (e._seed && e.total != null && Math.abs((Number(e.fpts) || 0) - (Number(e.total) || 0)) < 0.005) {
          return /^points update$/i.test(String(e.label || '')) ? Object.assign({}, e, { label: 'Total', desc: 'Total', _mixed: true }) : e;
        }
        const gid = e.game_id != null ? e.game_id : gameOfPlayer.get(String(e._pid || e.player_id || ''));
        const g = gid != null ? games.get(String(gid)) : null;
        const start = g ? Date.parse(g.game_time_utc || g.start_time || '') : NaN;
        const at = typeof e.ts === 'string' ? Date.parse(e.ts) : NaN;
        const late = !isNaN(start) && !isNaN(at) && at - start > 5 * 3600e3;
        const lbl = String(e.label || '');
        // (a loss of yards is a real play that takes points: "Run · -3 yd -0.30")
        const wrongWay = isNfl && Number(e.fpts) < 0 && scoring.test(lbl) && !/-\s*\d+\s*yds?\b/i.test(lbl);
        // A touchdown is worth at least 3 on every scoring system we carry; a
        // "pass td +0.20" is the box score's yardage being revised.
        const tooSmall = isNfl && /\b(td|touchdown)\b/i.test(lbl) && Math.abs(Number(e.fpts)) < 3;
        if (!late && !wrongWay && !tooSmall) {
          // One poll can carry several plays: "+2.20" under "run · 1 yd" (worth
          // 0.10) is the sum of more than that play. Said as what it is, not as
          // the last play's name (audit run 2, NUM-9). A catch (+1) and a bonus
          // (+3) are allowed for.
          const yd = lbl.match(/(\d+)\s*yds?\b/i);
          // Exact, not a ceiling: "+0.20 run · 1 yd" passed the old "more than
          // N x 0.1 + 1.25" test (audit run 3, NUM-9). One yardage play is
          // worth N x 0.1 (rush / receiving), plus 1 or 0.5 for the catch, or
          // N x 0.04 (passing); anything else is more than that one play.
          const n = Number(yd && yd[1]), v = Math.abs(Number(e.fpts));
          const one = yd && [n * 0.1, n * 0.1 + 1, n * 0.1 + 0.5, n * 0.04].some(x => Math.abs(v - x) < 0.015);
          if (isNfl && yd && !/\b(td|touchdown|bonus|field goal|fg)\b/i.test(lbl) && !one) {
            return Object.assign({}, e, { label: 'Several plays', _mixed: true });
          }
          return e;
        }
        const day = late && !isNaN(at) && new Date(at).toDateString() !== new Date(start).toDateString()
          ? new Date(at).toLocaleDateString([], { weekday: 'short' }) + ' ' : '';
        // and no play sentence: "Pass TD" under a correction re-told a play it
        // only amends (Broadcast audit v2, SPORT-8)
        return Object.assign({}, e, { is_correction: true, label: 'Correction', desc: '',
          gameTime: e.gameTime ? day + e.gameTime : e.gameTime });
      });
    },


    // Vidi v2 two-lane split (memory project_vidi_v2_motion). Both lanes
    // newest-first; the renderer caps each to its own fill-to-height.
    //   wire    — every event
    //   moments — key events (achievement / big_play), topped up with the
    //             biggest plays when the sport has not produced enough.
    //
    // THE BACKFILL RUNS AT EVERY DENSITY, not just 'mine' (Xav 2026-09-23:
    // "Moments now empty even though the slate has been going a while").
    //
    // Why it has to: what counts as a Moment is per-sport, and MLB barely
    // produces any mid-game. Of its nineteen vidi rules only six are
    // achievement/big_play, and five of those — perfect game, no-hitter, win,
    // shutout, complete game — can only fire at the END of a game. So for the
    // whole middle of an MLB slate the lane is a HOME RUN feed, and on five
    // games in the 4th with no homers yet it is correctly, uselessly empty.
    // NFL has nine including every touchdown, which is why the same lane feels
    // alive there and dead here.
    //
    // The backfill already existed and was scoped to 'mine', so the default
    // SLATE-WIDE view — the one most people are looking at — never got it.
    /* Load the Big Moments seed for the current slate, once.
     *
     * Its own call rather than a field on the slate-live payload: this is
     * wanted once per open, while that payload is pushed every few seconds to
     * every viewer, so bolting it on would re-ship the same dozen cards down
     * the SSE stream all night. Failure is silent and harmless — the lane
     * behaves exactly as it did before, filling from live events. */
    async _loadMomentSeed() {
      const slate = this.selectedSlate;
      const sid = slate && slate.id;
      if (!sid) { this._momentSeed = []; this._wireSeed = []; return; }
      if (this._momentSeedFor === sid) return;      // already have this slate's
      this._momentSeedFor = sid;
      this._momentSeed = [];
      this._wireSeed = [];
      const sport = slate.sport_id || (slate.sport && slate.sport.id) || '';
      try {
        const r = await fetch(`/api/slate-moments?slate_id=${encodeURIComponent(sid)}`
                              + `&sport_id=${encodeURIComponent(sport)}`,
                              { credentials: 'same-origin' });
        if (!r.ok) return;
        const j = await r.json();
        // The slate may have changed while this was in flight.
        if (this.selectedSlate && this.selectedSlate.id === sid) {
          // Stamp the WHEN. A live row shows the inning it happened in; a
          // rebuilt one cannot, because the log records a player's points
          // moving and not the state of the game around it. The clock time is
          // the honest substitute and it is the thing someone catching up
          // actually wants — "this was twenty minutes ago". Formatted here so
          // it lands in the viewer's own timezone.
          const _clock = (iso) => {
            if (!iso) return '';
            const d = new Date(iso);
            return isNaN(d) ? '' : d.toLocaleTimeString([], {
              hour: '2-digit', minute: '2-digit' });
          };
          const _stamp = (arr) => (Array.isArray(arr) ? arr : []).map(
            e => Object.assign({}, e, { gameTime: e.gameTime || _clock(e.ts) }));
          this._momentSeed = _stamp(j.moments);
          this._wireSeed   = _stamp(j.wire);
        }
      } catch (_) { /* the lane just opens as it always did */ }
    },

    _vidiLanes() {
      // Big Moments reads the PLAYS, not the Wire's cards: a goal with its
      // assists is one 'play_group' card on the Wire, and a card is no
      // big_play - so no NHL goal (nor an NFL touchdown pass) ever reached
      // Big Moments (Xav 2026-10-16).
      const stream = (this.vidiStream || []).flatMap(e => (e && e.event_type === 'play_group') ? (e._rows || []) : [e]);
      const isKey = e => e.event_type === 'achievement' || e.event_type === 'big_play';
      // a moment is ONE play that scored: a stat correction on a touchdown
      // (-1.00), a poll's worth of plays ("+0.92 · Several plays") or a
      // player's first logged total can carry a key type, and none of them is
      // a big moment - nor a best-play fill
      const onePlay = e => !e.is_correction && !e._mixed && (Number(e.fpts) || 0) > 0;
      // A goal the league took back (re-credited to someone else) is not a
      // moment: each reversal row (a stat going DOWN, workers/live_poller.py
      // _reversals) retracts that player's latest such play before it - Big
      // Moments showed Lombardi AND Boqvist scoring one goal (Xav 2026-10-10).
      const retracted = new Set();
      {
        const open = new Map();
        for (let i = stream.length - 1; i >= 0; i--) {       // oldest first
          const e = stream[i], k = String(e._pid || e.player_id || '') + ':' + (e.reverses || e.rule_id || '');
          if (e.reverses) { const st = open.get(k); if (st && st.length) retracted.add(st.pop()); }
          else if (isKey(e)) { if (!open.has(k)) open.set(k, []); open.get(k).push(e.id); }
        }
      }
      const isMoment = e => isKey(e) && onePlay(e) && !retracted.has(e.id);
      let moments = stream.filter(isMoment);
      const cap = 8;   // soft ceiling (two rows of four on Broadcast); renderer re-caps to height

      // What happened before you opened the page. Rebuilt server-side from
      // slate_score_events (core/moments_seed.py) because the narrated events
      // live in the poller's memory and do not survive a restart — which is why
      // this lane used to open empty on a slate that had already had two home
      // runs. Deduped against the live events on (player, running total): the
      // same play arriving live carries the total the seed was built from, and
      // the live copy wins because it has the narration.
      if (moments.length < cap && (this._momentSeed || []).length) {
        const seenId = new Set(moments.map(e => e.id));
        const seenAt = new Set(moments.map(
          e => String(e._pid || e.player_id || '') + ':' + String(e.total)));
        const fromSeed = this._momentSeed.filter(sd =>
          (Number(sd.fpts) || 0) > 0 && !sd.is_correction && !sd._mixed &&
          !seenId.has(sd.id) &&
          !seenAt.has(String(sd.player_id || '') + ':' + String(sd.total)));
        moments = moments.concat(fromSeed.slice(0, cap - moments.length));
      }

      // A finished slate with no key play recorded says so (the empty state)
      // rather than putting up a best-play card that reads as a big moment
      // ("★ Interception +2.00" on TB @ DAL - Xav 2026-10-10).
      // Broadcast's Big Moments shows big moments only (Xav 2026-10-10: "is
      // that big moment showing a big moment?") - a best-play top-up read as
      // one ("★ Interception +2.00", four "MAX D BONUS +10.00" at kickoff).
      // Until the first, the panel docks and the Director and the Wire take
      // the room. Other boards keep the top-up, never on a finished slate.
      if (moments.length < cap && this.activeSkin !== 'broadcast' && !(moments.length === 0 && this._slateFinal)) {
        const have = new Set(moments.map(e => e.id));
        const fill = stream
          .filter(e => !isKey(e) && !have.has(e.id) && onePlay(e))
          .sort((a, b) => (Number(b.fpts) || 0) - (Number(a.fpts) || 0))
          .slice(0, cap - moments.length)
          .map(e => Object.assign({}, e, { _fill: true }));
        moments = moments.concat(fill);
      }
      // Each moment's play in words, from the NEWEST row of that play: the
      // league adds assists to a goal after the fact, and the assist rows
      // carry the corrected sentence. The scorer's own name and the score
      // the card already shows come off it.
      const latest = new Map();
      for (const e of stream) {                              // newest first
        if (e && e.at_bat_index != null && e.desc) {
          const k = String(e.game_pk || e.game_id || '') + ':' + e.at_bat_index;
          if (!latest.has(k)) latest.set(k, e.desc);
        }
      }
      moments = moments.map(m => {
        const k = String(m.game_pk || m.game_id || '') + ':' + m.at_bat_index;
        let say = (m.at_bat_index != null && latest.get(k)) || m.desc || '';
        const nm = String(m.name || '').trim(), full = say;
        const last = nm.split(/\s+/).pop();
        say = say.replace(/\s*·\s*[A-Z]{2,4} \d+-\d+ [A-Z]{2,4}\s*$/, '');          // the score - the card has it
        // "Nico Hischier scores, his 2nd - snap shot, from ..." -> "his 2nd - snap shot, from ..."
        const i = last ? say.toLowerCase().indexOf(last.toLowerCase()) : -1;
        if (i > -1 && i < 30) say = say.slice(i + last.length).replace(/^\s*(scores|homers|hits|runs|catches|passes)?[\s,.:-]*/i, '');
        say = say.charAt(0).toUpperCase() + say.slice(1);
        return (say && say !== m.label) ? Object.assign({}, m, { _say: say, _sayFull: full }) : m;
      });
      // A play row worth nothing to anyone says nothing: "NE D/ST · 33 Yd
      // Field Goal · 4.00" with no points and no change (audit v2 run 3,
      // WIRE-12). Score bands, status lines, play cards and the corrections
      // summary are not plays and stay.
      const KEEP = new Set(['score', 'play_group', 'corr_sum', 'start', 'end', 'oncourt', 'offcourt', 'ot']);
      const wire = (this.vidiStream || []).filter(e => !e || KEEP.has(e.event_type) || e.is_correction || (Number(e.fpts) || 0) !== 0
                                     || (e.event_type && e.event_type !== 'big_play' && e.event_type !== 'achievement'));
      return { wire, moments };
    },

    // Is the MOMENTS lane empty? Not the same question as "is the stream
    // empty": moments are `achievement` / `big_play` only, a strict subset, so
    // a busy slate with no key plays yet gave vidi_moments a title bar and an
    // empty 89px body - its empty state was keyed on vidiStream.length and
    // stayed hidden, so the panel said nothing at all (WIDGET-5). Reuses
    // _vidiLanes so the 'mine' backfill counts the same way the renderer does.
    get vidiMomentsEmpty() {
      return !(this._vidiLanes().moments || []).length;
    },

    // Lazily build the shared VidiFeed renderer. ONE instance drives whichever
    // lanes are mounted — the combined `vidi` widget mounts both; the split
    // `vidi_wire` / `vidi_moments` widgets mount one lane each into the same
    // instance. Returns null if the renderer script hasn't loaded yet.
    _vidiEnsure() {
      if (!window.VidiFeed) return null;
      if (!this._vidiFeed) {
        this._vidiFeed = new window.VidiFeed({
          helpers: {
            logoFor:   team => (this._teamLogos || {})[(team || '').toUpperCase()] || '',
            colorFor:  team => this.teamColor(team),
            textFor:   hex  => this.teamText(hex),
            indiv:     () => this._indivSport,
            shortName: name => this._shortName(name),
            // Live-score precision from the format's `decimals` trait (2 fantasy
            // points / 0 single-stat count). Read at render time so a slate swap
            // is honoured without recreating the renderer.
            decimals:  () => (window.LSFormat ? window.LSFormat.traitsOf(this.formatVariant || '').decimals : 2),
            // Unit suffix for single-stat events (" HR"); "" on standard so the
            // feed doesn't tack "FPts" onto every play.
            unitSfx:   () => {
              const u = window.LSFormat ? window.LSFormat.traitsOf(this.formatVariant || '').unit : 'FPts';
              return (u && u !== 'FPts') ? ' ' + u : '';
            },
          },
        });
      }
      return this._vidiFeed;
    },

    // Bind the renderer to the widget lane container(s) (called from each
    // widget's x-init once its DOM refs exist) and paint immediately.
    // Combined widget — both lanes in one panel.
    _vidiFeedMount(wireEl, momEl) {
      const f = this._vidiEnsure(); if (!f) return;
      f.mount(wireEl, momEl);
      this._vidiFeedRender();
    },
    // Split widgets — one lane each, sharing the single renderer instance.
    _vidiWireMount(el)    { const f = this._vidiEnsure(); if (!f) return; f.mountWire(el);    this._vidiFeedRender(); },
    _vidiMomentsMount(el) { const f = this._vidiEnsure(); if (!f) return; f.mountMoments(el); this._vidiFeedRender(); },

    // Repaint both lanes. Called from an x-effect on the event stream, so
    // it re-runs whenever this.feed / density / game filter change.
    _vidiFeedRender() {
      if (this._vidiFeed && this._vidiFeed.mounted()) {
        // Paint instantly (no drop-in animation) until the first live event drips
        // — so the historical seed on open, and a later tab-mount of those rows,
        // never replay. Genuinely-new events (which set _vidiHadLiveEvent in the
        // drip) animate normally from then on.
        this._vidiFeed.render(this._vidiLanes(), !this._vidiHadLiveEvent);
        // written after the effect (a write inside x-effect is dropped)
        const more = this._vidiFeed.wireMore || 0;
        if (more !== this.vidiWireMore) setTimeout(() => { this.vidiWireMore = more; }, 0);
      }
    },
    vidiWireMore: 0,   // plays the Wire holds that do not fit (vidi_wire.html "+N earlier")

    get filteredGames() {
      if (!this.selectedSlate) return this.allGames;
      const matchups = this.selectedSlate.matchups || [];
      if (!matchups.length) return this.allGames;
      // Match live_games by sport_id + exact away/home team pair from slate matchups.
      const sportId = (this.selectedSlate.sport_id || this.selectedSlate.sport?.id || "").toLowerCase();
      // Paired by team_id. The matchup's abbrev is the DFS PLATFORM's and the
      // game's is the LIVE FEED's, so a team the two spell differently
      // (Washington: WAS vs WSH) matched no game and dropped out of
      // filteredGames silently - the `matched.length ? ... : allGames` guard
      // below only rescues the case where NOTHING matched, not the far more
      // likely one where most games matched and a couple did not.
      // Joined on the ids rather than the matchup's own game_id on purpose:
      // that id is schedule-valid and live_games is live-valid, which is the
      // trap in project_slate_games_fk_trap.
      // ...but the matchup's away_id / home_id are DRAFTKINGS team ids (the
      // scraper's atid / htid) and the game's are the live feed's, two
      // different numberings. They only ever agreed by accident: on the NBA
      // Classic of 2026-10-21 DK's IND @ NOP (11, 3) happened to equal the
      // feed's MIN @ MIA (11, 3), so the scoreboard picker showed MIN @ MIA
      // and nothing else out of 11 games (Xav). Where nothing coincided, the
      // "nothing matched -> every game" guard below hid it.
      // So the matchup's own game_id goes first: the slate's games ARE keyed
      // by it (slate_games), and on every current slate it is the feed's id.
      const byId = new Set(matchups.map(m => String(m.game_id || "")).filter(Boolean));
      const matched = this.allGames.filter(g =>
        (g.sport_id || "").toLowerCase() === sportId &&
        (byId.has(String(g.game_id)) || matchups.some(m => !m.game_id && (
          (m.away_id != null && m.home_id != null)
            ? (m.away_id === g.away_team_id && m.home_id === g.home_team_id)
            // Slates written before matchups carried ids fall back to the
            // labels, which is what this always did.
            : ((m.away || "").toUpperCase() === (g.away_team || "").toUpperCase() &&
               (m.home || "").toUpperCase() === (g.home_team || "").toUpperCase())
        )))
      );
      return matched.length ? matched : this.allGames;
    },

    get liveGames() {
      // Most advanced game first (furthest elapsed). Uses effStatus so a
      // game whose provider is stuck at 2 past its end window correctly
      // drops out of the live tab. The elapsed metric is sport-aware
      // (SportRegistry.liveElapsed) — the old NBA-only 4×720 clock formula
      // tied every MLB game past the 4th inning (MLB's clock_secs is
      // synthetic), collapsing the sort to insertion order on the MLB page.
      const sportId = (this.selectedSlate?.sport_id || this.selectedSlate?.sport?.id || '').toLowerCase();
      return this.filteredGames.filter(g => this.effStatus(g) === 2).sort((a, b) =>
        window.SportRegistry.liveElapsed(sportId, b) - window.SportRegistry.liveElapsed(sportId, a)
      );
    },
    get preGames() {
      // Earliest kickoff first — use game_time_utc for reliable sort
      return this.filteredGames.filter(g => this.effStatus(g) === 1).sort((a, b) => {
        const ta = a.game_time_utc ? new Date(a.game_time_utc).getTime() : 9e15;
        const tb = b.game_time_utc ? new Date(b.game_time_utc).getTime() : 9e15;
        return ta - tb;
      });
    },
    get allSortedGames() {
      return [...this.liveGames, ...this.preGames, ...this.finishedGames];
    },

    get finishedGames() {
      // Most recently finished first
      return this.filteredGames.filter(g => this.effStatus(g) === 3).sort((a, b) => {
        const ta = a.game_time_utc ? new Date(a.game_time_utc).getTime() : 0;
        const tb = b.game_time_utc ? new Date(b.game_time_utc).getTime() : 0;
        return tb - ta;
      });
    },

    // ──────────────────────────────────────────────────────────────────
    // Scoreboard redesign — Phase 1 foundation. See memory:
    // project_scoreboard_redesign.md for the full plan.
    //
    // slate_phase / sport_type are read by templates/partials/dash_matches_duel.html
    // (and Phase 3+ by the future dash_fixtures.html partial). Phase 1
    // adds the helpers without changing any visible UI; subsequent phases
    // hang banner / lifecycle / density logic off them.
    // ──────────────────────────────────────────────────────────────────

    get slate_phase() {
      // 'pre' | 'live' | 'post' for the currently selected slate.
      //   pre  — every game is upcoming (or no games yet)
      //   live — anything happening: at least one live game, OR mixed
      //          state (some final + some upcoming with none currently live)
      //   post — every game has finished
      // Mixed-without-live (e.g. one early game ended, others not started)
      // counts as 'live' so the slate stays "in progress" until everything
      // is final — matters for the user's continued attention.
      const games = this.filteredGames || [];
      if (!games.length)                          return 'pre';
      if (games.every(g => this.effStatus(g) === 1)) return 'pre';
      if (games.every(g => this.effStatus(g) === 3)) return 'post';
      return 'live';
    },

    sport_type(sport_id) {
      // 'team' | 'h2h' | 'tournament' — drives which scoreboard layout
      // a sport gets. Read from the LIVESLATE registry that base.html
      // injects from core/sports.SPORT_META.
      //   team       — NBA, MLB, NHL, NFL, soccer, WNBA, CBB, CFB, EL
      //                (anything where SPORT_META.individual is falsy)
      //   h2h        — Tennis, MMA (individual + matchups exist)
      //   tournament — Golf, F1, NASCAR (individual + single big event,
      //                no head-to-head matchups)
      // Hardcoded h2h list for now; could become a SPORT_META flag if
      // we ever add another head-to-head individual sport (boxing, darts).
      const meta = window.LIVESLATE?.sports?.[sport_id];
      if (!meta || !meta.individual) return 'team';
      return ['tennis', 'mma'].includes(sport_id) ? 'h2h' : 'tournament';
    },

    /** A comparable identity for a team, id first, label only as a last resort.
     *
     *  `side` is 'home' or 'away' for a GAME, and omitted for a player or an
     *  event. Returns '' when the thing carries neither, so a caller can drop
     *  it rather than match everything.
     *
     *  WHY A KEY AND NOT JUST team_id. The ids are not universal yet: 130
     *  platform_teams rows still have no team_id (126 of them soccer), so when
     *  a soccer slate is up its pool players carry team_id null. Comparing
     *  team_id alone would have made those players invisible to every filter
     *  that used to find them by abbrev - trading a wrong match for a missing
     *  one, which on a game filter looks like "I own nobody in this game".
     *  This keys on the id when both sides have one (the fix: WSH and WAS are
     *  the same team) and falls back to the label when they do not (exactly
     *  the old behaviour, no better and no worse). One helper, so there is one
     *  abbrev comparison in the codebase instead of eight. */
    _teamKey(x, side) {
      if (!x) return '';
      const tid = side ? x[side + '_team_id'] : x.team_id;
      if (tid != null) return 'id:' + tid;
      const ab = (side ? x[side + '_team'] : x.team) || '';
      return ab ? 'ab:' + ab.toUpperCase() : '';
    },
    /** Both sides of a game as keys. */
    _gameTeamKeys(g) {
      return [this._teamKey(g, 'home'), this._teamKey(g, 'away')].filter(Boolean);
    },

    /** The same set, by team_id - which is what anything joining a PLAYER to a
     *  game must use. See feedback_no_abbrev_matching_dashboard: an abbrev on a
     *  player comes from the DFS platform and an abbrev on a game comes from
     *  the live feed, and they are not the same alphabet. Filtering the Players
     *  tab to a Washington game listed no Washington players, because the game
     *  said WSH and every player said WAS.
     *
     *  Replaces the old abbrev-only `selectedTeams`, which had no callers left
     *  once every one of them moved to this. */
    get selectedTeamKeys() {
      const keys = new Set();
      for (const gid of this.selectedGames) {
        const g = this.allGames.find(g => g.game_id === gid);
        if (g) for (const k of this._gameTeamKeys(g)) keys.add(k);
      }
      return keys;
    },

    toggleGame(game_id) {
      const s = new Set(this.selectedGames);
      if (s.has(game_id)) { s.delete(game_id); } else { s.add(game_id); }
      this.selectedGames = s;
    },

    // ── Scoreboard pinning ──────────────────────────────────────────
    // togglePin(game_id) toggles the pin state for a live game; rejects
    // attempts that would exceed PIN_CAP. isPinned / canPin support the
    // Matches panel's pin-icon UI. _loadPinnedGames / _savePinnedGames
    // sync the set with localStorage on slate change / mutation.
    togglePin(game_id) {
      // Hand-picking a game leaves "My player's games" mode: start from what
      // the board was showing, then apply the pick.
      if (this.sbFollowMine || this._sbShowsAll) {
        this.pinnedGames = new Set((this.scoreboardGames || []).map(g => g.game_id));
        this._sbFollow('pins');
        // the board's games become your picks, so the prune leaves them be
        this._userPinnedGames = new Set(this.pinnedGames);
      }
      const has = this.pinnedGames.has(game_id);
      if (!has && this.pinnedGames.size >= this.PIN_CAP) return false;
      const next = new Set(this.pinnedGames);
      if (has) next.delete(game_id); else next.add(game_id);
      this.pinnedGames = next;
      // Track this as a USER pin so the auto-prune of finished games
      // (in _applyData) won't silently yank it. On unpin we mirror by
      // removing from the user set too.
      const userNext = new Set(this._userPinnedGames || []);
      if (has) userNext.delete(game_id); else userNext.add(game_id);
      this._userPinnedGames = userNext;
      this._savePinnedGames();
      return true;
    },
    isPinned(game_id) {
      if (this._sbShowsAll) return (this.scoreboardGames || []).some(g => g.game_id === game_id);
      return this.pinnedGames.has(game_id);
    },
    canPin(game_id)       { return this.isPinned(game_id) || this._sbShowsAll || this.pinnedGames.size < this.PIN_CAP; },

    /** Preset pinners for the scoreboard options popover (Xav 2026-06-01).
     *  Each one REPLACES the current pinned set (rather than adding to
     *  it) — they're "set this view", not "queue up more games". All
     *  three respect PIN_CAP so a preset that would exceed it gets
     *  silently clipped (deterministic order: live > soon > final,
     *  earliest start_time first). The resulting set is stamped onto
     *  _userPinnedGames too so the SSE auto-prune of finished games
     *  doesn't yank a preset choice.
     */
    _setPins(game_ids) {
      const cap   = this.PIN_CAP;
      const next  = new Set();
      // Pin in priority order so the cap clips the LEAST important games.
      const games = (this.allSortedGames || [])
        .filter(g => game_ids.has(g.game_id))
        .sort((a, b) => {
          const sa = this.effStatus(a), sb = this.effStatus(b);
          const rank = (s) => s === 2 ? 0 : s === 1 ? 1 : 2;
          if (rank(sa) !== rank(sb)) return rank(sa) - rank(sb);
          return (a.game_time_utc || '').localeCompare(b.game_time_utc || '');
        });
      for (const g of games) {
        if (next.size >= cap) break;
        next.add(g.game_id);
      }
      this.pinnedGames      = next;
      this._userPinnedGames = new Set(next);
      this._savePinnedGames();
    },
    pinAllGames() {
      this._sbFollow('pins');
      this._setPins(new Set((this.allSortedGames || []).map(g => g.game_id)));
    },
    pinLiveOnly() {
      this._sbFollow('pins');
      const live = (this.allSortedGames || [])
        .filter(g => this.effStatus(g) === 2)
        .map(g => g.game_id);
      this._setPins(new Set(live));
    },
    pinMyPlayerGames() {
      // Games where I have at least one rostered player. Fall back to
      // pinLiveOnly if I'm spectating (zero lineups) so the preset
      // still does something useful.
      // By team_id, not abbrev - "my players' games" silently skipped any team
      // the feed and the platform spell differently (Washington, Jacksonville).
      const mine = new Set();
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          const k = this._teamKey(p);
          if (k) mine.add(k);
        }
      }
      if (!mine.size) { this.pinLiveOnly(); return; }
      const ids = (this.allSortedGames || [])
        .filter(g => this._gameTeamKeys(g).some(k => mine.has(k)))
        .map(g => g.game_id);
      this._setPins(new Set(ids));
      // Then FOLLOW them: the pins are only the fallback if the mode can't
      // apply (lineups not loaded yet).
      this._sbFollow('mine');
    },
    _loadPinnedGames() {
      const sid = this.selectedSlate?.id;
      this._pendingAutoPin = false;
      this._userPinnedGames = new Set();
      if (!sid) { this.pinnedGames = new Set(); return; }
      try {
        const userRaw = localStorage.getItem('ls_user_pinned_' + sid);
        if (userRaw !== null) this._userPinnedGames = new Set(JSON.parse(userRaw));
      } catch (e) {}
      try {
        const raw = localStorage.getItem('ls_pinned_' + sid);
        if (raw !== null) {
          const saved = new Set(JSON.parse(raw));
          // Non-empty saved set = real user preference, honour it.
          // Empty saved set falls through to auto-pin: an empty pre-slate
          // dashboard is never the intended state. _maybeAutoPin's
          // pre-slate gate then keeps respecting user pins once any
          // game has been live (so an intentional in-slate clear sticks).
          if (saved.size > 0) {
            this.pinnedGames = saved;
            return;
          }
        }
      } catch (e) {}
      // First visit on this slate (or empty pin state): queue an auto-pin
      // once games load.
      this.pinnedGames     = new Set();
      this._pendingAutoPin = true;
      // If games are already loaded (e.g. switching slates mid-session),
      // run the auto-pin straight away.
      this._maybeAutoPin();
    },
    _maybeAutoPin() {
      const games = this.allGames || [];
      if (!games.length) return;  // games haven't loaded yet — wait
      // Auto-pin fires when:
      //   1. _pendingAutoPin queue set by _loadPinnedGames (first visit), OR
      //   2. Pre-slate phase + nothing pinned. Empty pins before kickoff
      //      are meaningless — the user almost certainly wants the
      //      scoreboard populated so they can scout. Once any game has
      //      been live (status >= 2) the user's pin state DOES matter
      //      and we stop force-resetting it.
      const everLive   = games.some(g => this.effStatus(g) >= 2);
      const isPreSlate = !everLive;
      // Count pins that point to a game CURRENTLY in allGames. Stale
      // pinned game_ids (e.g. yesterday's row that was auto-pinned
      // before the window-guard fix landed) don't count — otherwise
      // the pre-slate gate sees pinnedGames.size > 0 and refuses to
      // refresh, leaving the scoreboard empty even though no live game
      // is actually pinned.
      const gameIdSet      = new Set(games.map(g => g.game_id));
      const livePinCount   = [...(this.pinnedGames || [])].filter(gid => gameIdSet.has(gid)).length;
      const shouldAuto = this._pendingAutoPin
                      || (isPreSlate && livePinCount === 0);
      if (!shouldAuto) return;
      const cap       = this.PIN_CAP;
      const liveGames = games.filter(g => this.effStatus(g) === 2);
      const byKickoff = (a, b) => (a.game_time_utc || '').localeCompare(b.game_time_utc || '');
      let chosen;
      if (liveGames.length === 0) {
        // Pre-slate: pin first cap games by kickoff order so the user sees
        // what's coming up rather than an empty scoreboard.
        chosen = [...games].sort(byKickoff).slice(0, cap);
      } else if (liveGames.length <= cap) {
        chosen = [...liveGames].sort(byKickoff);
      } else {
        // More live games than the cap: rank by user-lineup interest (count
        // of the user's lineup players whose team is in the game). Kickoff
        // order tie-breaks so the top of the scoreboard stays stable.
        // team_id, not abbrev - a Washington player scored zero interest and
        // his game dropped off the bottom of an oversubscribed scoreboard.
        const mine = new Set((this.players || [])
          .map(p => this._teamKey(p)).filter(Boolean));
        const interest = g =>
          this._gameTeamKeys(g).filter(k => mine.has(k)).length;
        chosen = [...liveGames]
          .map(g => ({ g, score: interest(g) }))
          .sort((a, b) => b.score - a.score || byKickoff(a.g, b.g))
          .slice(0, cap)
          .map(x => x.g);
      }
      this.pinnedGames     = new Set(chosen.map(g => g.game_id));
      this._pendingAutoPin = false;
      this._savePinnedGames();
    },
    _savePinnedGames() {
      const sid = this.selectedSlate?.id;
      if (!sid) return;
      try {
        localStorage.setItem('ls_pinned_' + sid, JSON.stringify([...this.pinnedGames]));
        // Persist the user-explicit subset too so the "don't auto-prune
        // user pins" rule survives reloads.
        localStorage.setItem('ls_user_pinned_' + sid,
                             JSON.stringify([...(this._userPinnedGames || [])]));
      }
      catch (e) {}
    },

    // togglePanel() deleted 2026-06-06 along with the show* state — see
    // note in dashboard() above. Widget removal goes through
    // removeWidget() in live_v2/index.js.

    playerMatchesGameFilter(p) {
      if (this.selectedGames.size === 0) return true;
      return this.selectedTeamKeys.has(this._teamKey(p));
    },

    get hasLineup() { return this._studioPreview || this.nLineups > 0; },

    // Spectator warm-up: when you open a slate you have no lineup on, scores show
    // instantly (cheap scoreboard poll) but player stats only land on the next
    // live poll once the slate registers as "watched" (~25s). True while games
    // are live but the player pool hasn't arrived yet — drives the tuning-in
    // banner so the empty leaderboard/optimal never reads as broken. Clears the
    // moment real player data lands.
    get spectatorWarming() {
      // ...or the pool is there but nobody on it has a stat yet: the board's
      // snapshot carries the player POOL before the poller has fetched a single
      // stat for a slate nobody was watching, so the first viewer of a live
      // slate stared at a board of 0.00s for 30-60s (Xav 2026-11-01).
      if (!this.spectatorMode || this.liveGames.length === 0) return false;
      const ps = this.allSlatePlayers || [];
      return !ps.some(p => (+p.live_fpts || 0) !== 0 || (p.stats && Object.keys(p.stats).length));
    },
    /** The "first in tonight" card's clock (live.html). Started and stopped by
     *  the x-effect on the card - never from inside a getter. */
    _warmTick(on) {
      if (on && !this._warmT0) {
        this._warmT0 = Date.now(); this._warmSecs = 0; this._warmDoneAt = 0;
        this._warmTimer = setInterval(() => { this._warmSecs = Math.round((Date.now() - this._warmT0) / 1000); }, 1000);
      } else if (!on && this._warmT0) {
        clearInterval(this._warmTimer); this._warmTimer = null;
        // only a real wait earns the moment; a board that was simply loading does not
        if (this._warmSecs >= 3) this._warmDoneAt = Date.now();
        this._warmT0 = 0;
        setTimeout(() => { this._warmDoneAt = 0; }, 6000);
      }
    },
    get warmEta() {
      // the poller's pass is ~45s and the first viewer usually lands mid-pass:
      // an honest estimate, then "any second now" - never a fake countdown to zero
      const left = 45 - this._warmSecs;
      return left > 5 ? ('~' + left + 's') : 'any second now';
    },
    get warmPct() { return Math.min(95, 8 + this._warmSecs * 2); },

    get activeSlates() {
      return this._sortedSlates(this.savedSlates);
    },

    // ── Resilient game status — prefer the server-stamped effective_status
    // (combines stored .status with a wall-clock floor) so a finished match
    // whose provider is stuck at status=2 still gets treated as ended for
    // every DISPLAY decision (live/upcoming/finished filters, anyLive
    // checks, slate-phase derivation, scoreboard glow, pin gating). The
    // raw .status is preserved on the game object for transition
    // detection (prev.status → g.status) where we want "the provider just
    // told us X changed" semantics, not "wall-clock floor moved."
    effStatus(g) {
      if (!g) return 1;
      return g.effective_status || g.status || 1;
    },

    // ── Data freshness — derives from dataUpdatedAt + the 1s _timerNow ────
    // (the poll-cycle "I just got a response" flash uses syncFlash; this is
    // the orthogonal "is the underlying data actually moving?" signal).
    get dataAgeSeconds() {
      if (!this.dataUpdatedAt) return null;
      const t = new Date(this.dataUpdatedAt).getTime();
      if (!t) return null;
      return Math.max(0, Math.floor((this._timerNow - t) / 1000));
    },
    get dataFreshness() {
      // null = no data yet, or no live games to be fresh about. fresh = recent
      // poll, stale = poller missed at least a couple of cycles, severe = down
      // for many cycles. Thresholds tuned to a 30s poll cadence: 60s ≈ two
      // missed polls, 180s ≈ six.
      const age = this.dataAgeSeconds;
      // OFFLINE IS TRUE BEFORE KICK-OFF TOO. The two exits below - no data yet,
      // and nothing live - ran first, so a phone with no signal sat on a
      // ticking "IN 12m" and then "PRE-GAME" indefinitely: the countdown was
      // the only thing moving, and it was moving on a dead connection (audit
      // LIVE-9, 2026-09-16). Staleness still means nothing pre-game; having no
      // network at all always does.
      if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return { label: 'Offline', cls: 'red', offline: true };
      if (age === null) return null;
      // Suppress entirely when no game is live — staleness is irrelevant
      // pre-tip and post-final.
      const anyLive = (this.allGames || []).some(g => this.effStatus(g) === 2);
      if (!anyLive) return null;
      // No network at all is not a degree of lateness, it is a different
      // state, and it is knowable instantly - don't wait out a 60s threshold
      // to admit it. (Measured: total network loss showed a confident green
      // LIVE for 50s before the age thresholds even began to bite.)
      if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return { label: 'Offline', cls: 'red', offline: true };
      // SSE dropped — surface it immediately rather than waiting for the age
      // thresholds (the stream reconnects itself; this is just the visible cue).
      // But reconnecting is a CUE, NOT A CEILING: this used to return amber
      // before the thresholds ran, so a stream that had been down for 268s
      // still reported the same amber word as one 61s behind, and 'red' was
      // unreachable while reconnecting - a dead feed rendered identically to a
      // healthy one (Michelin run 6, auditors A6 and A7).
      if (this.sseReconnecting)
        return age >= 180 ? { label: 'Stale ' + this._fmtAge(age), cls: 'red' }
                          : { label: 'Reconnecting…', cls: 'amber' };
      if (age < 60)  return null;                 // fresh = no pill
      if (age < 180) return { label: this._fmtAge(age) + ' ago', cls: 'amber' };
      return { label: 'Stale ' + this._fmtAge(age), cls: 'red' };
    },
    // The sport whose feed we are waiting on, as people say it: "NFL", "MLB",
    // "soccer". From the sports master table (LIVE_INIT.sportNames); an
    // all-caps name stays as it is, a word is lower-cased to sit mid-sentence.
    get feedName() {
      const id = this.demoShowcase ? this.demoSport : this.selectedSlate?.sport_id;
      if (!id) return '';
      const name = (window.LIVE_INIT?.sportNames || {})[id] || String(id).toUpperCase();
      return name === name.toUpperCase() ? name : name.toLowerCase();
    },

    // What the feed-status card (desktop) and strip (phone) say. Null when
    // there is nothing to say, which on a healthy feed is always.
    //
    // It NAMES THE SOURCE. The old pill said "1M AGO", which reads as
    // LiveSlates being late; the data comes from the sport's own feed, and
    // "Waiting on the NFL feed" is both true and what a broadcaster would say.
    // Offline and reconnecting are about the viewer's connection, not the
    // feed, so they say that instead (Xav, feed-status mock, 2026-09-26).
    get feedStatus() {
      const f = this.dataFreshness;
      if (!f || this.editMode) return null;
      // A finished replay stops fetching; that is not a late feed (the same
      // guard shellBadge has - audit LIVE-2 = FIRST-8).
      if (this.demoShowcase && this.demoEnded) return null;
      const age = this.dataAgeSeconds;
      const ago = (typeof age === 'number' && isFinite(age) && age > 0) ? this._fmtAge(age) : '';
      const feed = this.feedName ? 'the ' + this.feedName + ' feed' : 'the live feed';
      const Feed = this.feedName ? this.feedName + ' feed' : 'Live feed';
      const last = ago ? 'Last update ' + ago + ' ago · ' : '';
      if (f.offline) return { kind: 'offline', head: "You're offline",
        sub: 'The board picks up where it left off when you reconnect',
        strip: 'Offline · resumes when you reconnect' };
      // "every 30 seconds" was neither the demo's 2s nor the live board's
      // backoff - say that it keeps trying, not a cadence (Broadcast audit
      // v2, LIVE-8); the demo is a replay, not a feed.
      if (f.cls === 'red') return { kind: 'paused', head: this.demoShowcase ? 'Replay paused' : Feed + ' paused',
        sub: last + (this.demoShowcase ? 'the next frame is on its way' : 'still checking, it picks up on its own'),
        strip: Feed + ' paused' + (ago ? ' · ' + ago : '') };
      if (this.sseReconnecting) return { kind: 'reconn', head: 'Reconnecting',
        sub: 'Your connection blinked · back in a moment',
        strip: 'Reconnecting · back in a moment' };
      // A minute or two between updates is a normal live game (a poll gap,
      // a TV break) and the old amber "Waiting on the NFL feed" read as the
      // system struggling - and pushed the bar's pills along (Xav 2026-10-12).
      // Nothing is said until it is a real stop (paused, above); on desktop
      // the slate bar's Online / Offline pill carries it (connPill).
      void feed;
      return null;
    },

    /** The slate bar's connection pill (Xav 2026-10-12): green Online while
     *  the board is connected and updating, red Offline when it is not - no
     *  network, the stream reconnecting, or no update for three minutes (the
     *  same "paused" line as dataFreshness). The reason is in its title. */
    get connPill() {
      if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return { ok: false, label: 'Offline', title: "You're offline - the board picks up where it left off when you reconnect" };
      if (this.sseReconnecting || (this.demoShowcase && (this._demoFailStreak || 0) >= 2))
        return { ok: false, label: 'Offline', title: 'Reconnecting - back in a moment' };
      const f = this.dataFreshness;
      if (f && f.cls === 'red' && !(this.demoShowcase && this.demoEnded))
        return { ok: false, label: 'Offline', title: (this.feedName ? this.feedName + ' feed' : 'The live feed') + ' paused - still checking, it picks up on its own' };
      return { ok: true, label: 'Online', title: 'Connected - scores update live' };
    },

    _fmtAge(secs) {
      if (secs < 60)    return secs + 's';
      const m = Math.floor(secs / 60);
      if (m < 60)       return m + 'm';
      const h = Math.floor(m / 60);
      return h + 'h ' + (m % 60) + 'm';
    },

    // ── Slate badge helpers ──────────────────────────────────────────────────
    badgeStatus(s) {
      // Backend stamps live_status authoritatively (db.get_slates_live_status
      // → sports/__init__.py SportProvider.event_status). Per-sport rules
      // live in sports/<sport>.py — see sports/__init__.py for the extension
      // point. JS just reads it.
      if (s?.live_status === 'live')  return 'live';
      if (s?.live_status === 'ended') return 'ended';
      if (!s?.start_time)              return 'upcoming';
      // Safety net only for sports with no provider yet — time-based "still
      // before start" check so countdown chips work pre-game.
      const diffMs = new Date(s.start_time).getTime() - Date.now();
      return diffMs > -5 * 60000 ? 'upcoming' : 'ended';
    },

    /** The compact game chips on the phone shell.
     *
     *  TWO defects, one getter (audit, live NFL slate):
     *
     *  ORDER. The chips rendered `allGames` in whatever order the feed handed
     *  them over, so on a Sunday the games actually in progress sat wherever
     *  the provider happened to put them - below three finals and a 4:25
     *  kickoff. Live first, then what is about to start, then what is done;
     *  kickoff time inside each group so the strip never reshuffles itself
     *  while you watch it.
     *
     *  COMPLETENESS. An upright tablet mounts the real scoreboard, which shows
     *  the PINNED games - and once anything is live the auto-pin keeps only
     *  live ones. On the desktop board that is fine: the cog is right there to
     *  pin the rest. The shell clones the scoreboard WITHOUT its titlebar, so
     *  on an iPad in portrait there was no cog, no counts, and no way to see
     *  the other ten games at all. The chips become the remainder there -
     *  everything the scoreboard is not already showing - so the slate is
     *  whole again without printing any game twice. */
    get shellChipGames() {
      const all = this.allGames || [];
      let rows = all;
      // `tabletTouch` as well as the flag: rotating an iPad to landscape tears
      // the scoreboard out of the DOM (its x-if closes) but nothing clears
      // _shellSbOn, and the chips would go on hiding the games a scoreboard
      // that is no longer there was covering. The mount is gated on
      // tabletTouch, so the subtraction has to be too.
      if (this.tabletTouch && this._shellSbOn) {
        const shown = new Set((this.scoreboardGames || []).map(g => g.game_id));
        rows = all.filter(g => !shown.has(g.game_id));
      }
      const bucket = (g) => {
        const st = this.effStatus(g);
        return st === 2 ? 0 : st === 1 ? 1 : 2;
      };
      // YOUR games lead. On an 11-game slate the grid was every game in the
      // league, in status order, with nothing saying which ones you have
      // players in - so the first screen was mostly other people's baseball.
      const mine = (g) => this.shellGameMine(g);
      // ...and with Mine selected, the rest are not on the strip at all. The
      // per-card "N yours" count this replaces only worked for a small
      // portfolio: measured on a real 16-game NFL slate it marks 7 of 16 games
      // at one entry and 14 of 16 at 150, so at scale it stopped saying WHICH
      // games were yours (Xav, 2026-09-18).
      if (this.shellGameScopeOn && this.shellGameScope === 'mine') {
        rows = rows.filter(g => mine(g) > 0);
      }
      return [...rows].sort((a, b) =>
        (mine(b) > 0) - (mine(a) > 0) ||     // your games first...
        mine(b) - mine(a) ||                 // ...most of your players first
        bucket(a) - bucket(b) ||
        (a.game_time_utc || a.start_time || '')
          .localeCompare(b.game_time_utc || b.start_time || ''));
    },

    /** How many game cards a phone shows before folding the rest away.
     *  Six, in three columns: the card is compact enough at 360 that three fit
     *  across, so six games cost two rows rather than three (Xav, 2026-09-16).
     *  They are the six you have most players in; the rest are one tap away. */
    // One row on a phone: the Wire comes straight after the games now, and a
    // second row of cards pushed it back under the fold (run 8, FIRST-3). Your
    // games still sort first; the rest are one tap away. Two rows where the
    // Live tab has two columns to spread across.
    get _SHELL_CHIP_CAP() {
      const w = (typeof window !== 'undefined') ? window.innerWidth : 390;
      // 340 and under the cards sit two across, so a row is two.
      return w >= 740 ? 6 : (w <= 340 ? 2 : 3);
    },
    shellChipsAll: false,

    /** Mine / All on the games strip — the Wire's control, applied to games.
     *
     *  It answers "which of these am I in" once, for the whole strip, so no
     *  card has to carry a count. Its own key, not the Wire's: the Wire's Mine
     *  filters PLAYS, this filters GAMES, and wanting every play but only your
     *  games is a reasonable place to stand. Phone-local either way - the
     *  phone plan's rule is that the desktop's settings are never read or
     *  written from here (see setVidiDensity). */
    /* Opt-in, not opt-out: "if you pick mine, only show games the user is
       interested in" (Xav, 2026-09-18). A board that quietly drops nine of
       eleven games the first time you open it is a different product from
       the one you left. The choice persists once made. */
    shellGameScope: 'all',

    /** The control only exists when it would do something: you have players in
     *  this slate, and not in every single game of it. A spectator owns
     *  nothing, so "Mine" would be an empty board. */
    get shellGameScopeOn() {
      if (this.spectatorMode) return false;
      const all = this.allGames || [];
      if (all.length < 2) return false;
      const owned = all.filter(g => this.shellGameMine(g) > 0).length;
      return owned > 0 && owned < all.length;
    },

    setShellGameScope(v) {
      if (v !== 'mine' && v !== 'all') return;
      this.shellGameScope = v;
      this.shellChipsAll = false;          // a new scope starts folded again
      try { localStorage.setItem('ls_games_scope_phone', v); } catch (_) {}
    },

    /** What the disclosure under the strip says. With Mine selected the hidden
     *  cards are YOURS, so "8 other games" would be a lie. */
    get shellMoreGamesLabel() {
      const n = this.shellChipsHidden;
      if (this.shellChipsAll) return 'Show fewer';
      if (!this.shellChipsShown.length) {
        return n === 1 ? 'Show the final score' : 'Show ' + n + ' final scores';
      }
      if (this.shellGameScopeOn && this.shellGameScope === 'mine') {
        return 'Show ' + n + ' more of yours';
      }
      // "8 other games" over five games you have players in is the same lie
      // the Mine branch above already guards against, arrived at from the
      // other direction: the cap hid yours rather than the filter (DFS-9).
      // Only call them other when they are.
      const hidden = this.shellChipGames.slice(this.shellChipsShown.length);
      if (hidden.some(g => this.shellGameMine(g) > 0)) {
        return 'Show ' + n + (n === 1 ? ' more game' : ' more games');
      }
      return n + (n === 1 ? ' other game' : ' other games');
    },

    get shellChipsShown() {
      const rows = this.shellChipGames;
      if (this.shellChipsAll) return rows;
      // Once the slate is over every card says FINAL, and the Dream Team - what
      // could have been - is the thing worth reading. It sat under 160px of
      // final scores; they fold behind the toggle now (run 8, HIER-10).
      if (this.shellState === 'final') return [];
      // A hard cap, even when every game is one of yours. Keeping them all was
      // 10 chips and 219px of scoreboard on a 10-game slate, which is the wall
      // this pass exists to remove; shellChipGames already sorts yours first
      // and by how many of your players are in them, so the six that show are
      // the six worth showing. The rest are one tap away.
      //
      // ...but the cap is a ceiling, not a quota. On an 8-of-11 slate it took
      // the top 3 and, when you were in only 2 of the live games, spent the
      // third slot on NYY-BOS with none of your players in it - while five
      // games you ARE in sat behind the toggle. A stranger reads the strip as
      // "these are the ones that matter to you"; the padding card says
      // otherwise (run 10, DFS-9). If you own any of these games, the cards
      // before the toggle are yours.
      const cap = this._SHELL_CHIP_CAP;
      const owned = rows.filter(g => this.shellGameMine(g) > 0).length;
      return rows.slice(0, owned ? Math.min(cap, owned) : cap);
    },
    get shellChipsHidden() {
      return Math.max(0, this.shellChipGames.length - this.shellChipsShown.length);
    },
    // Set by _shellMountScoreboard when a scoreboard really mounted. Drives
    // shellChipGames above; the CSS keys off the .is-on class for the same
    // reason (see that method).
    _shellSbOn: false,

    get badgeSlates() {
      // Dedupe by id before sorting — multiple code paths prepend to
      // savedSlates (_injectPickerSlates,
      // _loadSlates re-runs after picker injection) and racy timing
      // can land the same slate_id twice. Alpine's x-for :key="s.id"
      // crashes ("Cannot read properties of undefined (reading
      // 'after')") on duplicate keys, taking the whole dashboard down.
      // Keep the FIRST occurrence so the most recently prepended copy
      // (usually the freshest enrichment) wins. (Xav 2026-06-06.)
      const seen = new Set();
      const unique = [];
      for (const s of (this.savedSlates || [])) {
        if (s && s.id != null && !seen.has(s.id)) {
          seen.add(s.id);
          unique.push(s);
        }
      }
      return this._sortedSlates(unique).map(s => ({
        ...s, status: this.badgeStatus(s),
      }));
    },

    get selectedSlateBadge() {
      if (!this.selectedSlate) return null;
      return { ...this.selectedSlate, status: this.badgeStatus(this.selectedSlate) };
    },

    // P2 (true switcher): other LIVE slates you can spectate straight from the
    // slate picker — sorted + status-stamped, excluding the one you're viewing.
    get liveDiscoverSlates() {
      return this._sortedSlates(this._discoverSlates || [])
        .filter(s => s && s.id !== this.selectedSlate?.id)
        .map(s => ({ ...s, status: this.badgeStatus(s) }));
    },

    statusBadge(status) {
      if (status === 'live')     return { label: 'Live',     cls: 'bg-emerald-600 dark:bg-emerald-900/60 text-white dark:text-emerald-300 border border-emerald-600 dark:border-emerald-700' };
      if (status === 'upcoming') return { label: 'Upcoming', cls: 'bg-slate-700 text-white border border-slate-700 dark:border-slate-500' };
      if (status === 'demo')     return { label: 'Demo',     cls: 'bg-violet-600 dark:bg-violet-900/60 text-white dark:text-violet-300 border border-violet-600 dark:border-violet-700' };
      return                            { label: 'Ended',    cls: 'bg-red-600 dark:bg-red-900/40 text-white dark:text-red-400 border border-red-600 dark:border-red-800' };
    },

    slateStatusPill(slate) {
      // started, nothing in play, next game 30+ minutes off (window.slateOngoing)
      if (slate?.status === 'live' && window.slateOngoing && window.slateOngoing(slate))
        return { label: 'Ongoing', cls: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600' };
      const base = this.statusBadge(slate?.status || 'upcoming');
      if ((slate?.status || 'upcoming') !== 'upcoming' || !slate?.start_time) return base;
      const diff = new Date(slate.start_time).getTime() - Date.now();
      if (diff <= 0) return base;
      // Calendar-date diff, not elapsed 24h windows — a slate ~30h away
      // at 10pm is two calendar days out, not 'in 1 day'.
      const slateMid = new Date(slate.start_time);
      slateMid.setHours(0, 0, 0, 0);
      const todayMid = new Date();
      todayMid.setHours(0, 0, 0, 0);
      const dayDiff = Math.round((slateMid - todayMid) / 86400000);
      if (dayDiff >= 1) return { ...base, label: dayDiff === 1 ? 'in 1 day' : `in ${dayDiff} days` };
      const totalSec = Math.floor(diff / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      return { ...base, label: h > 0 ? `${h}h ${m}m` : `${m}m` };
    },

    fmtStartTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
           + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    countdown(ts) {
      if (!ts) return '';
      const diff = new Date(ts).getTime() - Date.now();
      if (diff <= 0) return '';
      const totalSec = Math.floor(diff / 1000);
      const d = Math.floor(totalSec / 86400);
      const h = Math.floor((totalSec % 86400) / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (d > 0) return `${d}d ${h}h`;
      if (h > 0) return `${h}h ${m}m`;
      return `${m}m ${s}s`;
    },

    platformColor(id) {
      return (window.PLATFORM_THEMES?.[id] || window.PLATFORM_THEMES?.fanteam)?.color || '#10b981';
    },

    /** The theme, reactively - see the note in lineups.js. Reading the DOM
     *  inside a method makes the binding that calls it non-reactive, so a
     *  toggle left these chips on the previous palette until a reload
     *  (run 10, THEME-05). */
    get _isDark() {
      try { const s = window.Alpine && Alpine.store('theme'); if (s) return s.dark; } catch (e) {}
      return document.documentElement.classList.contains('dark');
    },
    platformBadgeStyle(id) {
      return window.platformBadgeStyle?.(id, this._isDark) || '';
    },

    platformBadgeClass(id) {
      return window.platformBadgeClass?.(id, this._isDark) || '';
    },

    platformCurrency(id) {
      return window.platformCurrency?.(id) || '£';
    },

    platformName(id) {
      return window.platformDisplayName?.(id) || (id || '').toUpperCase();
    },

    fmtMatchup: window.fmtMatchup,
    // Region-derived (window.matchupStyle) — was a user setting. The 24
    // reads in dash_matches_duel.html swap which team takes the top row,
    // so this stays as reactive state rather than a call in the markup.
    gameFmt: window.matchupStyle(),

    sportIconHtml(id, size) {
      return window.sportIconHtml?.(id, size) || id;
    },

    // Sort slates: today first, then future (asc), then past (desc)
    _sortedSlates(slates) {
      const today = new Date().toISOString().slice(0, 10);
      const score = d => {
        if (!d) return 1;
        if (d === today) return 0;
        if (d > today)  return 1;
        return 2;
      };
      return [...slates].sort((a, b) => {
        const sa = score(a.game_date), sb = score(b.game_date);
        if (sa !== sb) return sa - sb;
        // today: preserve order; future: asc; past: desc
        if (sa === 1 && a.game_date > today) return a.game_date < b.game_date ? -1 : 1;
        if (sa === 2) return a.game_date > b.game_date ? -1 : 1;
        return 0;
      });
    },

    // Slates grouped by platform for the dropdown
    get slatesByPlatform() {
      const groups = {};
      for (const s of this._sortedSlates(this.savedSlates)) {
        const pid  = s.platform_id || "unknown";
        const name = s.platform?.name || pid;
        if (!groups[pid]) groups[pid] = { pid, name, slates: [] };
        groups[pid].slates.push(s);
      }
      return Object.values(groups);
    },

    /* The phone's simple lineup row shows four fields; the per-sport box score
     * sits behind the All stats chip. It used to be RENDERED and hidden with
     * `display:none`, which hides the pixels and keeps every cost: Alpine still
     * evaluated `:class` and `x-text` for each of ~18 columns per player on
     * every frame. Measured, those invisible cells were 15,405 of the page's
     * 38,674 DOM mutations in 40s - the single biggest source on the page
     * (audit 2026-09-13).
     *
     * So the grid iterates THIS instead: nothing to render, nothing to bind.
     * Desktop is untouched - `mobile` is false there, so it is always the full
     * set. */
    pgAll: false,
    get gridStatCols() {
      return (this.mobile && !this.pgAll) ? [] : this.statCols;
    },
    get statCols() {
      const sport    = this.selectedSlate?.sport_id || this.selectedSlate?.sport?.id || "nba";
      const platform = this.selectedSlate?.platform_id || this.selectedSlate?.platform?.id || "";
      // Per-sport stat columns live in static/js/sports/<sport>.js; the
      // registry handles platform variants (e.g. soccer_fanteam) internally.
      return window.SportRegistry.statCols(sport, platform);
    },

    get recentSlates() {
      return [];
    },

    daysUntilDeletion(slate) {
      return null;
    },

    get sortedLineupRows() {
      const dir = this.luSortDir === "asc" ? 1 : -1;
      const key = this.luSortKey === "est" ? "est_total" : "live_total";
      return [...this.lineupRows].sort((a, b) => ((a[key] || 0) - (b[key] || 0)) * dir);
    },

    setLuSort(key) {
      if (this.luSortKey === key) { this.luSortDir = this.luSortDir === "desc" ? "asc" : "desc"; }
      else { this.luSortKey = key; this.luSortDir = "desc"; }
      this.luPage = 20;   // re-sort → show the top page of the new order
    },

    get anyLive() { return this.allGames.some(g => this.effStatus(g) === 2); },
    /** Has any game on the slate kicked off? Before one has, Broadcast's All
     *  Players has no Live or Value to show (every row says when it starts). */
    get anyStarted() { return (this.allGames || []).some(g => this.effStatus(g) >= 2); },

    get theme() {
      const pid = this.selectedSlate?.platform_id || this.selectedSlate?.platform?.id;
      return _DASH_THEMES[pid] || _DASH_THEMES.fanteam;
    },

    // ── Stat helpers (wrappers so Alpine x-text can call them) ────────────────
    _fmtStat(col, p) { return _fmtStat(col, p); },
    /** The player's box score as one phrase (Broadcast All Players). */
    /** A player whose game has not started: when it does ("Starts Sun
     *  18:00", the Games panel's day and time). On Broadcast's All Players
     *  his Line says it and Live / Value stay blank - the row read "– – –"
     *  for every player still to play (Xav 2026-10-10). */
    _pgKickoff(p) {
      const g = p && (this.allGames || []).find(x => String(x.game_id) === String(p.game_id));
      const t = g && g.game_time_utc ? new Date(g.game_time_utc) : null;
      return t && !isNaN(t) ? 'Starts ' + t.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : '';
    },
    _statLine(p) {
      const sl = this.selectedSlate || {};
      const sport = sl.sport_id || (sl.sport && sl.sport.id) || 'nba';
      return window.SportRegistry && window.SportRegistry.statLine
        ? window.SportRegistry.statLine(sport, p, sl.platform_id || (sl.platform && sl.platform.id) || '') : '';
    },

    /** The stat cell's tone, painted by .pg-stat[data-tone] (widgets.css).
     *  An attribute, not a :class string: see player_grid.html. */
    _statCellTone(col, p) {
      if (col.ftBonus) {
        const v = p.ft_bonus;
        if (v === undefined || v === null) return 'faint';
        return v >= 0 ? 'bonus-pos' : 'bonus-neg';
      }
      const sport = this.selectedSlate?.sport_id || 'nba';
      if (col.group && col.group !== _playerPosGroup(sport, p)) return 'dim';
      const flash = this.statFlashIds[`${p.player_id}_${col.key}`];
      if (flash) return col.neg ? 'flash-neg' : 'flash-pos';
      // near-black, not grey - matches the sharp black type on the
      // scoreboard pitcher rows (Xav 2026-06-12: "no more of this greyish
      // font"). Group-greyed cells above stay faded by design.
      return col.neg ? 'neg' : 'ink';
    },

    _pDelta(p) {
      // Use the backend's pace_delta directly: live - (proj * elapsed_frac)
      // when still tracking, or live - proj if already past proj / game is final.
      // (The previous formula extrapolated final pace, so early-game deltas
      // exploded — a 6-pt Q1 with proj 30 read +25 instead of the realistic +3.)
      return p.pace_delta || 0;
    },

    _flashStat(playerId, statKey) {
      const k = `${playerId}_${statKey}`;
      this.statFlashIds = { ...this.statFlashIds, [k]: true };
      setTimeout(() => {
        const f = { ...this.statFlashIds };
        delete f[k];
        this.statFlashIds = f;
      }, 1900);
    },
    _fmtStatCell(col, p) {
      if (col.ftBonus) return _fmtStat(col, p);
      const sport = this.selectedSlate?.sport_id || 'nba';
      if (col.group && col.group !== _playerPosGroup(sport, p)) return '';
      return _fmtStat(col, p);
    },

    // ── Status box ────────────────────────────────────────────────────────────
    _timerNow: Date.now(),   // ticks every second via _startClock()
    syncFlash:  false,       // briefly true after each successful poll

    _slateGames() {
      if (!this.selectedSlate || !this.allGames.length) return [];
      return this.filteredGames;
    },

    get slatePhase() {
      const games = this._slateGames();
      if (!games.length) return "";
      if (games.every(g => this.effStatus(g) === 3)) return "finished";
      if (games.some(g => this.effStatus(g) === 2))  return "live";
      return "pre";
    },

    get countdownLabel() {
      const games = this._slateGames();
      if (!games.length) return "";
      const startTimes = games
        .map(g => g.game_time_utc ? new Date(g.game_time_utc).getTime() : null)
        .filter(Boolean);
      if (!startTimes.length) return "";
      const diff = Math.min(...startTimes) - this._timerNow;
      if (diff <= 0) return "Starting...";
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (h > 0) return `${h}h ${String(m).padStart(2,"0")}m ${String(s).padStart(2,"0")}s`;
      if (m > 0) return `${m}m ${String(s).padStart(2,"0")}s`;
      return `${s}s`;
    },

    _startClock() {
      setInterval(() => { this._timerNow = Date.now(); }, 1000);
    },

    _flashSync() {
      this.syncFlash = true;
      setTimeout(() => { this.syncFlash = false; }, 800);
    },

    get uniqueStakes() {
      const s = new Set();
      for (const lu of this.lineupRows) {
        if (lu.stake != null) s.add(+lu.stake);
      }
      return [...s].sort((a, b) => a - b);
    },

    toggleStake(stake) {
      const s = new Set(this.selectedStakes);
      if (s.has(stake)) { s.delete(stake); } else { s.add(stake); }
      this.selectedStakes = s;
    },

    get activeLineupRows() {
      const rows = this.lineupRows || _EMPTY_ARR;
      const key = this.luSortKey + '|' + this.luSortDir + '|'
        + [...this.selectedStakes].sort().join(',') + '|' + this.luSearch;
      const m = _luMemo.get(rows);
      if (m && m.key === key) return m.active;
      let sorted = this.sortedLineupRows;
      if (this.selectedStakes.size > 0) {
        sorted = sorted.filter(lu => this.selectedStakes.has(+lu.stake));
      }
      if (this.luSearch.trim()) {
        const q = this.luSearch.toLowerCase().trim();
        sorted = sorted.filter(lu => (lu.lineup_name || '').toLowerCase().includes(q));
      }
      _luMemo.set(rows, { key, active: sorted, inCount: null });
      return sorted;
    },

    // Re-aggregate in_count from stake-filtered lineups
    get _inCountMap() {
      const active = this.activeLineupRows;
      const m = _luMemo.get(this.lineupRows || _EMPTY_ARR);
      if (m && m.active === active && m.inCount) return m.inCount;
      const counts = {};
      for (const lu of active) {
        const seen = new Set();
        for (const p of lu.players) {
          const key = String(p.player_id || p.external_player_id || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          counts[key] = (counts[key] || 0) + 1;
        }
      }
      if (m && m.active === active) m.inCount = counts;
      return counts;
    },

    get filteredNLineups() { return this.activeLineupRows.length; },

    // An estimate needs something to estimate FROM. With no projection,
    // core/tracker.estimated_final hands back the live score, so every card
    // read "91.82 PTS · EST 91.82" and every row repeated its own score as a
    // forecast - for most users, who never paste projections (run 8, DFS-16).
    get showEst() {
      return (this.selectedSlate?.sport_id || 'nba') !== 'mlb' && !this.slateOver
          && !!this.hasProjections;
    },

    // Players join by id, never by name (audit run 2, CODE-25). A Dream Team
    // row without an id (none expected) still joins by name, so the star
    // can't silently vanish.
    /** Every game on the slate is final - the board stops saying "live". */
    get allGamesFinal() {
      const gs = this.allGames || [];
      return gs.length > 0 && gs.every(g => this.effStatus(g) >= 3);
    },

    get dreamTeamKeys() {
      return new Set(this.dreamTeam.map(p => String(p.player_id || p.external_player_id || ('n:' + p.name))));
    },
    inDreamTeam(p) {
      const k = this.dreamTeamKeys;
      return k.has(String(p.player_id || '')) || k.has(String(p.external_player_id || '')) || k.has('n:' + p.name);
    },

    get sortedDreamTeam() {
      return [...this.dreamTeam].sort((a, b) => (a._slot_idx ?? 99) - (b._slot_idx ?? 99));
    },

    get dreamTeamScore() {
      return this.dreamTeam.reduce((s, p) => s + (p.live_fpts || 0) * (p.role_mult || 1), 0);
    },

    get dreamTeamSalary() {
      return this.dreamTeam.reduce((s, p) => s + (p.salary || 0) * (p.role_mult || 1), 0);
    },

    // ── SLATE TIME FRACTION ──────────────────────────────────────────────────
    // Returns 0→1: how far through the slate we are, based on actual game clocks.
    get slateTimeFrac() {
      const games = this.filteredGames;
      if (!games.length) return 0;
      const sport = (this.selectedSlate?.sport_id || 'nba').toLowerCase();
      // seconds per period and total game seconds by sport
      const cfg = {
        nba:    { periodSecs: 720,  periods: 4 },
        nhl:    { periodSecs: 1200, periods: 3 },
        nfl:    { periodSecs: 900,  periods: 4 },
        soccer: { periodSecs: 2700, periods: 2 },
        mma:    { periodSecs: 300,  periods: 5 },
      }[sport] || { periodSecs: 720, periods: 4 };
      const totalSecs = cfg.periodSecs * cfg.periods;

      let sumFrac = 0;
      for (const g of games) {
        const st = this.effStatus(g);
        if (st === 3) {
          sumFrac += 1;
        } else if (st === 2) {
          const periodsPast = Math.max(0, (g.period || 1) - 1);
          const clockElapsed = cfg.periodSecs - Math.max(0, g.clock_secs ?? cfg.periodSecs);
          sumFrac += Math.min((periodsPast * cfg.periodSecs + clockElapsed) / totalSecs, 1);
        }
        // status === 1 (pre-game): contributes 0
      }
      return sumFrac / games.length;
    },

    // ── GRAPHS ──────────────────────────────────────────────────────────────
    graphLineupColor(i) {
      const palette = ['#10b981','#6366f1','#f59e0b','#ef4444','#0ea5e9','#a855f7','#ec4899','#14b8a6','#84cc16','#f97316'];
      return palette[i % palette.length];
    },

    // ── Dashboard graphs ────────────────────────────────────────────
    // Data shaping for all 8 graphs lives in static/js/dashboard/graphs.js
    // (LSGraphs.*). Each getter is a thin delegation that hands `this`
    // to the pure reducer. Don't inline the bodies back here — the
    // module exists so graph logic can be scanned in one file.
    get graphBarChartHtml()         { return LSGraphs.barChart(this); },
    get graphPaceData()             { return LSGraphs.paceData(this); },
    get graphPlayerHeatmap()        { return LSGraphs.heatmap(this); },
    get graphPlayerContributions()  { return LSGraphs.contributions(this); },
    get graphLineupLeaderboard()    { return LSGraphs.lineupLeaderboard(this); },
    get graphPositionBreakdown()    { return LSGraphs.positionBreakdown(this); },
    get graphStackPerformance()     { return LSGraphs.stackPerformance(this); },
    get graphGameExposure()         { return LSGraphs.gameExposure(this); },

    get filteredBestLive() {
      if (!this.activeLineupRows.length) return 0;
      return Math.max(...this.activeLineupRows.map(r => r.live_total || 0));
    },

    get filteredAvgLive() {
      if (!this.activeLineupRows.length) return 0;
      return this.activeLineupRows.reduce((s, r) => s + (r.live_total || 0), 0) / this.activeLineupRows.length;
    },

    get filteredBestEst() {
      if (!this.activeLineupRows.length) return 0;
      return Math.max(...this.activeLineupRows.map(r => r.est_total || 0));
    },

    get filteredSpread() {
      const rows = this.activeLineupRows.filter(r => r.proj_total > 0);
      if (rows.length < 2) return 0;
      const projs = rows.map(r => r.proj_total);
      return Math.max(...projs) - Math.min(...projs);
    },

    get filteredAvgSalary() {
      const rows = this.activeLineupRows.filter(r => r.players?.length);
      if (!rows.length) return 0;
      const total = rows.reduce((s, r) => s + r.players.reduce((ps, p) => ps + (p.salary || 0), 0), 0);
      return total / rows.length;
    },

    get sortedPlayers() {
      const inMap    = this._inCountMap;
      const activeIds = new Set(Object.keys(inMap));
      let rows = this.players
        .filter(p => activeIds.has(String(p.player_id || p.external_player_id || "")))
        .filter(p =>
          p.game_phase === 'not_started' ? this.spShowNS :
          p.game_phase === 'finished'    ? this.spShowDone : this.spShowLive
        )
        .filter(p => {
          if (this.spPosFilter === null) return true;
          const PITCHER = new Set(['SP','RP','P']);
          if (this.spPosFilter === '__pitchers') return (p.pos || '').split('/').some(x => PITCHER.has(x));
          if (this.spPosFilter === '__hitters')  return !(p.pos || '').split('/').some(x => PITCHER.has(x));
          return (p.pos || '').split('/').includes(this.spPosFilter);
        })
        .map(p => ({ ...p, in_count: inMap[String(p.player_id || p.external_player_id || "")] || 0 }));
      if (this.selectedGames.size > 0) {
        const keys = this.selectedTeamKeys;
        rows = rows.filter(p => keys.has(this._teamKey(p)));
      }
      const spQ = this.spSearch.toLowerCase().trim();
      if (spQ) rows = rows.filter(p => p.name.toLowerCase().includes(spQ));
      const col = this.sortCol, dir = this.sortDir;
      // Broadcast: live and finished players sort together - "LIVE ↓" put
      // every live player above a finished top scorer, so page 1 missed him
      // (Broadcast audit v2, NUMBERS-1 / UX-3). Not started still sorts last.
      const bc = this.activeSkin === 'broadcast';
      const phaseScore = p => p.game_phase === 'not_started' ? (bc ? 2 : 1) : p.game_phase === 'finished' ? (bc ? 0 : 2) : 0;
      const groupByPhase = col === 'name' || col === 'team';
      return rows.slice().sort((a, b) => {
        if (groupByPhase) { const pd = phaseScore(a) - phaseScore(b); if (pd !== 0) return pd; }
        let av = 0, bv = 0;
        if      (col === "in_count") { av = a.in_count || 0;                    bv = b.in_count || 0; }
        else if (col === "live")     { av = a.live_fpts || 0;                   bv = b.live_fpts || 0; }
        else if (col === "proj")     { av = a.proj      || 0;                   bv = b.proj      || 0; }
        else if (col === "est")      { av = a.est_final || 0;                   bv = b.est_final || 0; }
        // Sal / Value on the Broadcast board (Xav 2026-10-07); no salary sorts last
        else if (col === "salary")   { av = +a.salary || 0;                     bv = +b.salary || 0; }
        else if (col === "value")    { av = (+a.salary > 0) ? (a.live_fpts || 0) / a.salary : -Infinity;
                                       bv = (+b.salary > 0) ? (b.live_fpts || 0) / b.salary : -Infinity;
                                       if (av === bv) return 0;
                                       if (av === -Infinity) return 1;
                                       if (bv === -Infinity) return -1; }
        else if (col === "min")      { av = parseFloat((a.stats||{}).min) || 0; bv = parseFloat((b.stats||{}).min) || 0; }
        else if (col === "pts")      { av = (a.stats||{}).pts || 0;             bv = (b.stats||{}).pts || 0; }
        else if (col === "reb")      { av = (a.stats||{}).reb || 0;             bv = (b.stats||{}).reb || 0; }
        else if (col === "ast")      { av = (a.stats||{}).ast || 0;             bv = (b.stats||{}).ast || 0; }
        else if (col === "stl")      { av = (a.stats||{}).stl || 0;             bv = (b.stats||{}).stl || 0; }
        else if (col === "blk")      { av = (a.stats||{}).blk || 0;             bv = (b.stats||{}).blk || 0; }
        else if (col === "to")       { av = (a.stats||{}).to  || 0;             bv = (b.stats||{}).to  || 0; }
        else if (col === "name")     { return dir * a.name.localeCompare(b.name); }
        else if (col === "team")     { return dir * (a.team||"").localeCompare(b.team||""); }
        else                         { av = parseFloat((a.stats||{})[col]) || 0; bv = parseFloat((b.stats||{})[col]) || 0; }
        return (av - bv) * dir;
      }).slice(0, this.spPage);
    },

    get filteredPlayersCount() {
      const inMap    = this._inCountMap;
      const activeIds = new Set(Object.keys(inMap));
      let rows = this.players
        .filter(p => activeIds.has(String(p.player_id || p.external_player_id || "")))
        .filter(p =>
          p.game_phase === 'not_started' ? this.spShowNS :
          p.game_phase === 'finished'    ? this.spShowDone : this.spShowLive
        )
        .filter(p => {
          if (this.spPosFilter === null) return true;
          const PITCHER = new Set(['SP','RP','P']);
          if (this.spPosFilter === '__pitchers') return (p.pos || '').split('/').some(x => PITCHER.has(x));
          if (this.spPosFilter === '__hitters')  return !(p.pos || '').split('/').some(x => PITCHER.has(x));
          return (p.pos || '').split('/').includes(this.spPosFilter);
        });
      if (this.selectedGames.size > 0) {
        const keys = this.selectedTeamKeys;
        rows = rows.filter(p => keys.has(this._teamKey(p)));
      }
      const spQ = this.spSearch.toLowerCase().trim();
      if (spQ) rows = rows.filter(p => p.name.toLowerCase().includes(spQ));
      return rows.length;
    },

    get apPositions() {
      const sport = (this.selectedSlate?.sport_id || '').toLowerCase();
      const PITCHER = new Set(['SP', 'RP', 'P']);

      if (sport === 'mlb') {
        const hasPitcher = this.allSlatePlayers.some(p => PITCHER.has(p.pos));
        const hasHitter  = this.allSlatePlayers.some(p => !PITCHER.has(p.pos));
        const indivOrder = ['P','C','1B','2B','3B','SS','OF'];
        const indiv = indivOrder.filter(pos => this.allSlatePlayers.some(p => (p.pos || '').split('/').includes(pos)));
        return [
          ...(hasPitcher ? [{ label: 'Pitchers', key: '__pitchers' }] : []),
          ...(hasHitter  ? [{ label: 'Hitters',  key: '__hitters'  }] : []),
          ...indiv.map(pos => ({ label: pos, key: pos })),
        ];
      }

      // Generic: dynamic individual positions. Split dual-eligible strings
      // ('PF/C', 'PG/SG', ...) into their parts so we don't render combo
      // buttons — the filter logic already does part-wise matching.
      const order = ['PG','SG','SF','PF','C','QB','RB','WR','TE','K','DST','GK','DEF','MID','FWD'];
      const expanded = new Set();
      for (const p of this.allSlatePlayers) {
        if (!p.pos) continue;
        for (const part of String(p.pos).split('/')) {
          if (part) expanded.add(part.trim());
        }
      }
      return [...expanded].sort((a, b) => {
        const ai = order.indexOf(a), bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      }).map(pos => ({ label: pos, key: pos }));
    },

    get spPositions() {
      const sport = (this.selectedSlate?.sport_id || '').toLowerCase();
      const PITCHER = new Set(['SP', 'RP', 'P']);

      if (sport === 'mlb') {
        const hasPitcher = this.players.some(p => PITCHER.has(p.pos));
        const hasHitter  = this.players.some(p => !PITCHER.has(p.pos));
        const indivOrder = ['P','C','1B','2B','3B','SS','OF'];
        const indiv = indivOrder.filter(pos => this.players.some(p => (p.pos || '').split('/').includes(pos)));
        return [
          ...(hasPitcher ? [{ label: 'Pitchers', key: '__pitchers' }] : []),
          ...(hasHitter  ? [{ label: 'Hitters',  key: '__hitters'  }] : []),
          ...indiv.map(pos => ({ label: pos, key: pos })),
        ];
      }

      const order = ['PG','SG','SF','PF','C','QB','RB','WR','TE','K','DST','GK','DEF','MID','FWD'];
      const unique = [...new Set(this.players.map(p => p.pos).filter(Boolean))];
      return unique.sort((a, b) => {
        const ai = order.indexOf(a), bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      }).map(pos => ({ label: pos, key: pos }));
    },

    get sortedAllPlayers() {
      const inMap = this._inCountMap;

      let rows = this.allSlatePlayers
        .filter(p =>
          p.game_phase === 'not_started' ? this.apShowNS :
          p.game_phase === 'finished'    ? this.apShowDone : this.apShowLive
        )
        // Hide DNPs but tolerate replay fixtures and sports without an explicit
        // minutes stat: a player with ANY positive counting stat is not a DNP.
        // 'playing' is unambiguously not a DNP — keep them visible even when
        // their stats are all zero (e.g. a tennis player at 0-0 in set 1, an
        // MMA fighter pre-first-strike, an F1 driver at lights-out). Without
        // this carve-out individual-sport players blink out of the list as
        // their stats fluctuate around zero early in their match.
        .filter(p => {
          if (p.game_phase === 'not_started') return true;
          if (p.game_phase === 'playing')     return true;
          const s = p.stats || {};
          if ((s.mins || s.min || 0) > 0) return true;
          for (const v of Object.values(s)) {
            if (typeof v === 'number' && v > 0) return true;
          }
          return false;
        })
        .filter(p => {
          if (this.apPosFilter === null) return true;
          const PITCHER = new Set(['SP','RP','P']);
          if (this.apPosFilter === '__pitchers') return (p.pos || '').split('/').some(x => PITCHER.has(x));
          if (this.apPosFilter === '__hitters')  return !(p.pos || '').split('/').some(x => PITCHER.has(x));
          return (p.pos || '').split('/').includes(this.apPosFilter);
        })
        .map(p => ({ ...p, in_count: inMap[String(p.player_id || p.external_player_id || "")] || 0 }));

      if (this.selectedGames.size > 0) {
        const keys = this.selectedTeamKeys;
        rows = rows.filter(p => keys.has(this._teamKey(p)));
      }

      const q = this.apSearch.toLowerCase().trim();
      if (q) rows = rows.filter(p => p.name.toLowerCase().includes(q));

      const col = this.apSortCol, dir = this.apSortDir;
      // Broadcast: live and finished players sort together - "LIVE ↓" put
      // every live player above a finished top scorer, so page 1 missed him
      // (Broadcast audit v2, NUMBERS-1 / UX-3). Not started still sorts last.
      const bc = this.activeSkin === 'broadcast';
      const phaseScore = p => p.game_phase === 'not_started' ? (bc ? 2 : 1) : p.game_phase === 'finished' ? (bc ? 0 : 2) : 0;
      return rows.slice().sort((a, b) => {
        const pd = phaseScore(a) - phaseScore(b);
        if (pd !== 0) return pd;
        let av = 0, bv = 0;
        if      (col === "in_count") { av = a.in_count || 0;                    bv = b.in_count || 0; }
        else if (col === "live")     { av = a.live_fpts || 0;                   bv = b.live_fpts || 0; }
        else if (col === "proj")     { av = a.proj      || 0;                   bv = b.proj      || 0; }
        else if (col === "est")      { av = a.est_final || 0;                   bv = b.est_final || 0; }
        // Sal / Value on the Broadcast board (Xav 2026-10-07); no salary sorts last
        else if (col === "salary")   { av = +a.salary || 0;                     bv = +b.salary || 0; }
        else if (col === "value")    { av = (+a.salary > 0) ? (a.live_fpts || 0) / a.salary : -Infinity;
                                       bv = (+b.salary > 0) ? (b.live_fpts || 0) / b.salary : -Infinity;
                                       if (av === bv) return 0;
                                       if (av === -Infinity) return 1;
                                       if (bv === -Infinity) return -1; }
        else if (col === "min")      { av = parseFloat((a.stats||{}).min) || 0; bv = parseFloat((b.stats||{}).min) || 0; }
        else if (col === "pts")      { av = (a.stats||{}).pts || 0;             bv = (b.stats||{}).pts || 0; }
        else if (col === "reb")      { av = (a.stats||{}).reb || 0;             bv = (b.stats||{}).reb || 0; }
        else if (col === "ast")      { av = (a.stats||{}).ast || 0;             bv = (b.stats||{}).ast || 0; }
        else if (col === "stl")      { av = (a.stats||{}).stl || 0;             bv = (b.stats||{}).stl || 0; }
        else if (col === "blk")      { av = (a.stats||{}).blk || 0;             bv = (b.stats||{}).blk || 0; }
        else if (col === "to")       { av = (a.stats||{}).to  || 0;             bv = (b.stats||{}).to  || 0; }
        else if (col === "name")     { return dir * a.name.localeCompare(b.name); }
        else if (col === "team")     { return dir * (a.team||"").localeCompare(b.team||""); }
        else                         { av = parseFloat((a.stats||{})[col]) || 0; bv = parseFloat((b.stats||{})[col]) || 0; }
        return (av - bv) * dir;
      });
      // NB: no .slice() here — this returns the FULL filtered+sorted pool so the
      // "Show more" pager (sortedAllPlayers.length > apPage) has the real total.
      // The grid slices to apPage at the render site (widgets/all_players.html),
      // so only apPage rows are ever built — the fix for the ~5s Podium open that
      // was rendering the whole ~995-row pool synchronously.
    },

    setApSort(col) {
      if (this.apSortCol === col) { this.apSortDir *= -1; }
      else { this.apSortCol = col; this.apSortDir = -1; }
      this.apPage = 50;
    },

    setSort(col) {
      if (this.sortCol === col) { this.sortDir *= -1; }
      else { this.sortCol = col; this.sortDir = -1; }
      this.spPage = 20;
    },

    // pnl / loadPnL / dismissLineup cluster REMOVED 2026-08-13 with the
    // Financials panel (no live stake/payout data source — see
    // widgets_registry.py note). Server endpoints (/api/lineups/pnl-summary,
    // /api/lineups/<id>/dismiss) remain for /results.

    async init() {
      if (this._initialized) return;
      // BO Dashboard Studio (P2): render the board read-only, skipping the ENTIRE
      // live bootstrap (slate load / SSE / polling / watchers). Alpine auto-calls
      // init(), so this branch — not x-init — is what actually routes to the
      // preview. Gated on window.LS_STUDIO, set only by the Studio page, so it's
      // a no-op on the live dashboard. initStudioPreview sets _initialized itself.
      if (window.LS_STUDIO) { this.initStudioPreview(window.LS_STUDIO); return; }
      this._initialized = true;
      // The server-painted opening board (partials/shell_first_frame.html) has
      // to outlive Alpine's boot: Alpine is up ~1.4s before the board it builds
      // has anything in it, and hiding the frame then is exactly the blank the
      // spinner already gave. So the frame stays until real games land, and the
      // app's own loadscreen keeps out of its way until then.
      try {
        // Only where the frame is actually shown: mobile_shell.css carries the
        // shell's media query, so on a desktop the frame stays display:none and
        // suppressing the app's own loadscreen there would leave a blank.
        if (document.querySelector('.msh-boot') && this._isShellDevice()) {
          document.documentElement.classList.add('ls-firstframe');
        }
      } catch (e) {}
      // Showcase demo (public /demo/<slug>) — set BEFORE the layout preset
      // resolves so _resolveInitialPreset picks the analytical dashboard.
      if (window.DEMO) {
        this.demoShowcase = true;
      }
      // The one ask inside the page, a minute in. Armed for ANY logged-out
      // board, not just the demo: a visitor who followed a shared link to a
      // live slate got the bottom bar and nothing else, while the demo they
      // never asked for got a proper ask (run 9, FIRST). The section it drives
      // only exists for a logged-out visitor, so arming it always is harmless.
      setTimeout(() => { this._demoAsk = true; }, 60000);
      // Mobile-beta kill switch: ?noshell=1 forces the legacy stacked grid on
      // a phone (QA / A-B compare). Parsed once; read by the shellMode getter.
      try { this._noShell = new URLSearchParams(location.search).has('noshell'); } catch (e) { this._noShell = false; }
      // Deep link into a phone-shell tab: Today's "Log winnings" opens
      // /live/<id>?tab=lineups, where the Slate's-done card is waiting.
      const TABS = ['live', 'lineups', 'players', 'more'];
      try {
        const t = new URLSearchParams(location.search).get('tab');
        if (TABS.includes(t)) this.shellTab = t;
      } catch (e) { /* bad URL: stay on Live */ }
      // Back / Forward between tabs (shellGo pushes them).
      window.addEventListener('popstate', (e) => {
        if (!this.shellMode) return;
        // Consuming the entry we pushed ourselves when a sheet was dismissed by
        // tap/Escape - not a user gesture, and the tab must not move.
        if (this._sheetSkipPop) { this._sheetSkipPop = false; return; }
        // A sheet is open: Back closes THAT, and goes no further.
        if (this._anySheetOpen) {
          this._sheetHistory = false;
          this.shellCloseSheets();
          return;
        }
        let t = (e.state && e.state.shellTab) || null;
        if (!t) {
          try { t = new URLSearchParams(location.search).get('tab'); } catch (_) { t = null; }
        }
        this.shellGo(TABS.includes(t) ? t : 'live', true);
      });
      // One-time purge of legacy ls_vidi_<slate> entries so reloads after
      // the cache removal don't briefly restore stale events.
      this._purgeLegacyVidiCache();
      // Bfcache restore: Chrome serves a frozen snapshot of the previous
      // dashboard render when the user clicks Back / Forward. Force a
      // fresh load so we don't show stale lineups + a stale SSE connection.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) window.location.reload();
      });
      // Auto-pin first PIN_CAP live games on first visit to a slate.
      // _loadPinnedGames sets _pendingAutoPin if no localStorage exists
      // for the slate; this watcher fires _maybeAutoPin whenever allGames
      // updates, so we run as soon as the first SSE message lands.
      this.$watch('allGames', () => this._maybeAutoPin());

      // Vidi tab-return de-flood. When the tab is hidden, the browser
      // throttles setTimeout to ~1Hz so the drip queue stalls while SSE
      // keeps pushing events. On return, the queue can hold dozens of
      // backlog events — dripping them all triggers a torrent of sounds
      // + animations for events the user can't react to anyway. Flush
      // all-but-the-newest directly into the feed (silent), and let the
      // last one drip normally so the user sees fresh activity.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) return;
        const queue = this._vidiQueue || [];
        if (queue.length <= 3) return;
        const backlog = queue.splice(0, queue.length - 1);
        for (const ev of backlog) {
          if (this.feed.findIndex(e => e.id === ev.id) === -1) {
            this.feed = [ev, ...this.feed].slice(0, LSEventDetection.VIDI_FEED_CAP);
          }
        }
      });
      // Auto-fit row heights so each panel divides its scroll area evenly
      // among its visible rows. Vidiprinter has 2-row events (60/40 split
      // handled by CSS). Dream team and Top 10 have single-row entries —
      // for those, subtract the table header before dividing.
      // ResizeObserver re-fits when the panel resizes (window resize,
      // fullscreen toggle); $watch on dreamTeam keeps dream-team in sync
      // when slate changes the position count.
      const fitTable = (containerId, rowCount, varName) => {
        const el = document.getElementById(containerId);
        if (!el || rowCount < 1) return;
        const thead = el.querySelector('thead');
        const headerH = thead ? thead.offsetHeight : 0;
        const available = el.clientHeight - headerH;
        if (available > 0) {
          document.documentElement.style.setProperty(
            varName, `${Math.floor(available / rowCount) - 1}px`
          );
        }
      };
      const refitAll = () => {
        fitTable('dream-scroll', (this.dreamTeam || []).length || 8, '--dream-row-h');
        fitTable('top10-scroll', 10, '--top10-row-h');
      };
      this.$nextTick(() => {
        refitAll();
        for (const id of ['dream-scroll', 'top10-scroll']) {
          const el = document.getElementById(id);
          if (el) new ResizeObserver(refitAll).observe(el);
        }
        // Slate change can shift dream-team row count (NBA Classic = 8,
        // showdown = 6, etc.); refit when it does.
        this.$watch('dreamTeam', () => this.$nextTick(refitAll));
      });
      // Keep isFullscreen in sync — covers both the control-deck button and
      // the user pressing Esc / F11.
      this.isFullscreen = !!document.fullscreenElement;
      document.addEventListener('fullscreenchange', () => {
        this.isFullscreen = !!document.fullscreenElement;
        // Fullscreen change reflows the page (nav + sub-nav hide via CSS
        // :fullscreen rules in live.html) but doesn't fire a resize
        // event on most browsers — dispatch one so any listener that
        // sizes itself to the viewport (the grid height measure() in
        // particular) picks up the new layout.
        window.dispatchEvent(new Event('resize'));
      });
      const _t0 = performance.now();
      const _lap = (label) => console.log(`[dashboard] ${label}: ${(performance.now() - _t0).toFixed(0)}ms`);
      // THE BOARD'S OWN DATA, ASKED FOR FIRST. On the demo it was the LAST
      // request the page made - after settings, the layout preset, the widget
      // catalog and the vidi rules - so a phone sat on "Loading the player
      // pool…" through that whole chain before the fetch that paints the board
      // even started: 4.4s in, on a 4x-throttled phone (run 8, first contact
      // #15). It is in flight from here; _fetchInitialData awaits it.
      if (window.DEMO && window.DEMO.slug) {
        // Inlining this frame in the page was tried and reverted: the payload
        // is ~800KB of JSON (every pool player with his stats), which cost more
        // in HTML transfer and parse than the round trip it saved.
        const t = (+this.demoT || 0).toFixed(2);
        this._firstData = fetch(`/api/demo-live/${encodeURIComponent(window.DEMO.slug)}?t=${t}`)
          .then(r => (r.ok ? r.json() : Promise.reject(new Error('demo HTTP ' + r.status))));
        this._firstData.catch(() => {});   // a rejection here is handled at the await
      }
      let s = {};
      const [settingsOk] = await Promise.allSettled([
        fetch('/api/settings').then(r => r.json()).then(j => { s = j; }),
        // Showcase demo: never load real slates — the picker must only ever
        // surface the demo, never a user's (or anyone's) actual slates.
        // Skipped for a spectator as well as the demo: _loadSlates fetches
        // the saved-slate, discover, template and platform lists purely to
        // populate the slate switcher, which spectator mode does not render.
        // Four requests for a control nobody can see - and /api/slates lists
        // every slate on the platform, so an anonymous viewer should not be
        // asking for it at all.
        (window.DEMO || (window.LIVE_INIT && window.LIVE_INIT.spectator))
          ? Promise.resolve() : this._loadSlates(),
      ]);
      _lap('slates+settings loaded');
      try {
        // Stash the raw settings blob so vidiPref() can read per-sport
        // toggles on demand. Saved shape is flat snake_case
        // (vidi_<sport>_<leaf>); the accessor below knows the mapping.
        this._vidiSettings = s || {};
        if (s.vidi_sound  != null) this.soundOn  = s.vidi_sound !== false;
        // A phone with no saved choice starts MUTED. That includes every
        // logged-out visitor: their settings request is refused, so the phone
        // default never reached them - the public demo called the sound player
        // 5 times in 60s with no switch anywhere to stop it (run 8, LIVE-17).
        else if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) this.soundOn = false;
        // The phone keeps its OWN Wire filter. vidi_density is a server-side,
        // cross-device setting, so choosing "All" once on the desktop silently
        // made the phone's only "what just happened" feed 100% other people's
        // players: measured 0 of 6 events involving the user, on the biggest
        // block of the Live tab (Michelin run 6, auditor A1). The phone plan's
        // hard rule is that layout and preferences are per device, and that
        // the desktop value is never read or written from a phone. Phone
        // defaults to Mine - on a 390px screen you are sweating your own.
        if (this._isShellDevice()) {
          let v = null;
          try { v = localStorage.getItem('ls_vidi_density_phone'); } catch (_) {}
          this.vidiDensity = (v === 'all' || v === 'mine') ? v : 'mine';
          // The games strip keeps its own Mine / All (see setShellGameScope).
          let gs = null;
          try { gs = localStorage.getItem('ls_games_scope_phone'); } catch (_) {}
          this.shellGameScope = (gs === 'all' || gs === 'mine') ? gs : 'all';
        } else if (s.vidi_density != null && ['mine','big','all'].includes(s.vidi_density)) {
          // 'big' retired in Vidi v2 — the Moments lane replaces it, so the
          // filter is now just Mine / All. Migrate any saved 'big' to 'all'.
          this.vidiDensity = s.vidi_density === 'big' ? 'all' : s.vidi_density;
        }
        // vidiScope drives the scorer-event scope filter; vidiDensity drives
        // the pbp layer + the filter label. They must stay in lockstep — the
        // filter buttons set both, but on load only density is restored, so
        // sync scope here or a saved 'all' silently keeps scope 'my' and drops
        // every slate-wide scorer event.
        this.vidiScope = this.vidiDensity === 'mine' ? 'my' : 'all';
      } catch(_) {}
      const pathSlateId  = (typeof window !== 'undefined' && window.LIVE_INIT) ? window.LIVE_INIT.slateId : null;
      const slateParam   = new URLSearchParams(window.location.search).get('slate');
      // Last-accessed slate, this browser's (the account copy, last_slate_id,
      // was written on every switch and never reached: /live always names one).
      const lastSlateId  = (() => {
        try { return localStorage.getItem('ls_last_slate_id'); } catch (e) { return null; }
      })();
      // Resolve the slate to show. A slate requested via the path (/live/<id>)
      // or ?slate= that the user has NO lineup on isn't in savedSlates (which
      // is has_lineups=true) — cold-fetch it and enter Spectator mode rather
      // than bouncing to a lineup'd slate (the old behaviour, which made the
      // no-lineup state unreachable).
      const requestedId = pathSlateId || slateParam;
      let sel = requestedId ? (this.savedSlates.find(s => s.id === requestedId) || null) : null;
      if (requestedId && !sel) {
        sel = await this._loadSpectatorSlate(requestedId);
      }
      if (!sel) {
        sel = (lastSlateId && this.savedSlates.find(s => s.id === lastSlateId))
              || this.activeSlates[0] || this.recentSlates[0] || null;
      }
      // Showcase demo — override with a synthetic slate fed by the auth-free
      // demo stream. NOT _spectator: the demo ships fake lineups, so we want
      // the ANALYTICAL dashboard (My Lineups / My Players / Optimal), not the
      // slate-only spectator preset. Force the wire slate-wide regardless.
      if (window.DEMO) {
        this.demoShowcase = true;
        this.vidiDensity = 'all';
        this.vidiScope = 'all';
        sel = {
          id: window.DEMO.slug, name: window.DEMO.name,
          sport_id: window.DEMO.sport, platform_id: window.DEMO.platform || 'draftkings',
          _demo: true, template: { id: '' },
        };
        // Feed the subnav demo picker (switch sports without leaving showcase).
        this.demoSlug  = window.DEMO.slug;
        this.demoName  = window.DEMO.name;
        this.demoSport = window.DEMO.sport;
        this.demoList  = Array.isArray(window.DEMO_LIST) ? window.DEMO_LIST : [];
      }
      // Mask the ENTIRE initial resolve+load behind the switch overlay so the
      // board reveals fully-formed instead of flashing an intermediate pre-format
      // board first. init() bypasses selectSlate (which normally raises this), so
      // the board would otherwise paint before initWidgets() + the format
      // re-resolve land the real board. Cleared by the first SSE push (sse.js) or
      // the 12s fallback — same mechanism selectSlate uses. Only when a slate is
      // actually loading (empty state stays unmasked). (Allocation audit F5 —
      // first-paint flash.)
      if (sel) {
        this._switching      = true;
        this._awaitFirstPush = true;
        clearTimeout(this._switchFallback);
        this._switchFallback = setTimeout(() => {
          this._switching = false; this._awaitFirstPush = false;
        }, 12000);
      }
      this.selectedSlate = sel;
      this._loadMomentSeed();
      // Land on the Cover (THE SLATE) SYNCHRONOUSLY for a pre-game MLB slate,
      // before the first applyData renders. The section resolver is async and
      // loses a race with applyData, so without this the initial data commit
      // paints the (Cover-hidden) KPI strip for ~2.4s before the resolver flips
      // to the Cover — measured as the biggest chunk of the pre-game load. Empty
      // layout too, so the board grid paints nothing. The resolver confirms the
      // same choice a moment later.
      try {
        if (this._isPregame) {
          this._activeSection = 'pregame';
          this.layout = { scoreboard_id: null, kpis: [], panels: [], widgetOpts: {} };
          this.layoutLoaded = true;
        }
      } catch (e) {}
      // `sel._spectator` is set when a LOGGED-IN user switches to a slate they
      // have no lineup on. An anonymous visitor arriving straight from the
      // homepage CTA has no saved slates at all, so nothing would set it and
      // they would get the full board with every "my lineups" panel empty.
      // window.LIVE_INIT.spectator is the server saying so outright.
      this.spectatorMode = !!(sel && sel._spectator) || !!(window.LIVE_INIT && window.LIVE_INIT.spectator);
      // Signed in and has never entered a lineup ANYWHERE — server-computed,
      // because savedSlates is recency+region scoped and an empty one means
      // "nothing recent here", not "new". Spectator mode hides every way in;
      // this is what lets it show a newcomer exactly one, without putting it
      // back in front of a scout who knows where it is.
      this.newToLineups = !!(window.LIVE_INIT && window.LIVE_INIT.newToLineups);
      this.isGuest = !!(window.LIVE_INIT && window.LIVE_INIT.guest);
      // Spectating = no lineup, so "Mine" is meaningless. Force the feed slate-
      // wide (the filter is hidden in this mode) so scorer events aren't dropped
      // for having no rostered players.
      if (this.spectatorMode) { this.vidiDensity = 'all'; this.vidiScope = 'all'; }
      this.sportMeta = sportStatusMeta(this.selectedSlate?.sport_id);
      this._loadPinnedGames();
      // Init bypasses selectSlate (to avoid resetting feed/prevStats), so
      // also kick the vidi-rules fetch here — otherwise the engine path
      // won't activate until the user manually picks a slate.
      this._fetchVidiRulesForCurrentSlate();
      _lap('slate selected');
      this._startClock();
      // Fetch initial data via HTTP (fast) then hand off to SSE for live updates.
      await this._fetchInitialData(_lap);
      _lap('initial data applied');
      this._connectSSE();

      // Admin-only flag — populated from server-stamped DASHBOARD_INIT.
      this.isAdmin = !!(window.DASHBOARD_INIT && window.DASHBOARD_INIT.isAdmin);
    },

    async _fetchInitialData(_lap) {
      if (!this.selectedSlate) { this.loading = false; return; }
      const seq   = this._fetchSeq;
      const tid   = this.selectedSlate.template?.id || "";
      const csid  = this.selectedSlate.id || "";
      const plat  = this.selectedSlate.platform_id || this.selectedSlate.platform?.id || "fanteam";
      const sport = this.selectedSlate.sport_id    || this.selectedSlate.sport?.id    || "nba";
      // ?t= ON THE FIRST FETCH TOO. Without it the demo's opening board was the
      // t=0 frame - nothing has happened yet - and the 17 events up to the
      // open point then dripped in on arrival, with sound, as if they were
      // live. A visitor's first ten seconds were a replay of a replay
      // (audit LIVE-6, 2026-09-16). demoT is already seeded to the open point.
      const url   = (this.demoShowcase && window.DEMO)
        ? `/api/demo-live/${encodeURIComponent(window.DEMO.slug)}?t=${(+this.demoT || 0).toFixed(2)}`
        : `/api/slate-live?slate_template_id=${encodeURIComponent(tid)}&slate_id=${encodeURIComponent(csid)}&platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}`;
      try {
        _lap?.('fetch start');
        // Already in flight from init() on the demo - see _firstData there.
        const first = this._firstData;
        this._firstData = null;
        const [data, rules, rulesByPos] = await Promise.all([
          first || fetch(url).then(r => {
            if (!r.ok) throw new Error('slate-live HTTP ' + r.status);
            return r.json();
          }),
          fetch(`/api/scoring-rules?platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}`).then(r => r.json()).catch(() => ({})),
          fetch(`/api/scoring-rules/by-pos?platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}`).then(r => r.json()).catch(() => ({})),
        ]);
        _lap?.('fetch done');
        if (seq !== this._fetchSeq) return; // slate changed while in-flight — discard
        this.scoringRules    = rules || {};
        this.scoringRulesByPos = rulesByPos || {};
        this.loadFailed = false;
        this._applyData(data);
      } catch(_) {
        this.loadFailed = true;
        this.loading    = false;
      }
    },

    async _loadSlates() {
      const [slatesRes, tmplRes, platRes, discoverRes] = await Promise.all([
        fetch('/api/slates?has_lineups=true'),
        fetch("/api/slate-templates?sport_id=all"),
        fetch("/api/platforms"),
        fetch('/api/slates'),   // P2: region-wide discover set for cross-slate switching
      ]);
      const rawPlats = platRes.ok ? await platRes.json() : [];
      _DASH_PLATFORMS = rawPlats.map(p => ({
        ...p,
        logo: p.logo_path,
        theme: _DASH_THEMES[p.id] || _DASH_THEMES.fanteam,
        sports: p.sports.map(s => ({ id: s.sport_id, name: s.sport_name })),
      }));
      const allTemplates = tmplRes.ok ? await tmplRes.json() : [];
      this.slatesFailed = !slatesRes.ok;
      const raw = slatesRes.ok ? await slatesRes.json() : [];

      this.savedSlates = _dbEnrichSlates(raw, allTemplates);
      // P2 (true switcher): other LIVE slates you're NOT entered on, so the
      // on-board slate picker can jump to any live contest without leaving the
      // board. Switching enters read-only Spectator mode (_spectator). Capped +
      // deduped vs your own slates to keep the dropdown tight. Best-effort.
      try {
        const rawDisc  = discoverRes.ok ? await discoverRes.json() : [];
        const enteredIds = new Set((this.savedSlates || []).map(s => s.id));
        const live = _dbEnrichSlates(rawDisc, allTemplates)
          .filter(s => s.live_status === 'live' && !enteredIds.has(s.id))
          .slice(0, 12);
        live.forEach(s => { s._spectator = true; });
        this._discoverSlates = live;
      } catch (_) { this._discoverSlates = []; }
    },

    // Cold-fetch a single slate that isn't in the has_lineups=true savedSlates
    // set (deep-link / ?slate= to a slate outside the 3-day+region-scoped list),
    // enrich it, and prepend so the rest of the page treats it like any other.
    // _spectator is set from the SERVER-authoritative own_lineup_count, NOT from
    // "wasn't in savedSlates" — that list is recency/region scoped, so a slate
    // you OWN a lineup on (e.g. a >3-day-old one via View Final, or one whose
    // platform is outside your current region) must not be mis-flagged spectator.
    // (P0 allocation audit, 2026-08-08). Returns the enriched slate or null.
    async _loadSpectatorSlate(slateId) {
      try {
        const sr = await fetch(`/api/slate/${slateId}`);
        if (!sr.ok) return null;
        const raw = await sr.json();
        const tmplRes = await fetch('/api/slate-templates?sport_id=all').catch(() => null);
        const allTemplates = tmplRes && tmplRes.ok ? await tmplRes.json() : [];
        const slate = (_dbEnrichSlates([raw], allTemplates) || [])[0];
        if (!slate) return null;
        slate._spectator = (raw.own_lineup_count || 0) === 0;
        this.savedSlates = [slate, ...this.savedSlates];
        return slate;
      } catch (e) {
        return null;
      }
    },

    selectSlate(slate) {
      // Mask the rebuild with the transition overlay while the new slate's data
      // lands and widgets mount, then reveal it all at once (no widget-by-widget).
      // Cleared by the first SSE push (sse.js); 12s fallback so a stalled fetch
      // can't strand the dashboard behind the overlay.
      this._switching      = true;
      this._awaitFirstPush = true;
      clearTimeout(this._switchFallback);
      this._switchFallback = setTimeout(() => {
        this._switching = false; this._awaitFirstPush = false;
      }, 12000);
      this._fetchSeq        = (this._fetchSeq || 0) + 1;
      this.selectedSlate    = slate;
      this._loadMomentSeed();
      this.spectatorMode    = !!slate._spectator;
      // Spectating → force the feed slate-wide (filter hidden, no lineup).
      if (this.spectatorMode) { this.vidiDensity = 'all'; this.vidiScope = 'all'; }
      this.sportMeta        = sportStatusMeta(slate?.sport_id);
      this.prevStats        = {};
      this.prevGames        = {};
      // Also reset the 'all' scope baseline — LiveSlate player_ids are global,
      // so same-day slates overlap heavily; without this the first tick of the
      // new slate diffs against the OLD slate's snapshots for shared players and
      // fires spurious vidi events/animations.
      this.prevAllStats     = {};
      // Reset personal-moment state so signals don't bleed across slates
      // (e.g. an "ahead of pace" flag from MLB shouldn't suppress a fresh
      // crossing on NBA selected right after).
      this._momentPrevBestLineupId = null;
      this._momentLineupAhead      = {};
      this._momentSlateOverFired   = false;
      // ...and re-seed: without this the first update of the NEW slate was
      // compared against nothing and fired BIG SWING 10 times at once.
      this._pmSeeded               = false;
      this._shellHeroLast          = null;   // a new slate's first number is not a move
      this.allGames         = [];
      this._seenMatchEventIds = new Set();
      this.feed             = [];
      if (this._vidiDripTimer) { clearTimeout(this._vidiDripTimer); this._vidiDripTimer = null; }
      this._vidiQueue       = [];
      this._scorerSeenIds   = new Set();   // server scorer-event dedup (slate-scoped)
      this._vidiSeeded      = false;       // next apply seeds the feed statically (no replay)
      this._vidiHadLiveEvent = false;      // lane paints stay instant until the first live drip
      this._pbpFeed         = [];
      if (this._vidiFeed) this._vidiFeed.clear();   // reset v2 renderer nodes
      this.freshIds         = {};
      this.newestVidiIds    = {};
      this.fptDeltaMap      = {};
      this.flashIds         = {};
      this.slateOver        = false;
      this.lineupRows       = [];
      this.players          = [];
      this.allSlatePlayers  = [];
      this.dataReady        = false;
      this.apPosFilter   = null;
      this.spPosFilter   = null;
      this.spSearch      = "";
      this.luSearch      = "";
      this.luPage        = 20;
      this.spPage        = 20;
      this.apPage        = 50;
      this._connectSSE();
      // Persist so reload restores this slate.
      try { localStorage.setItem('ls_last_slate_id', slate.id); } catch (e) {}
      // THE URL FOLLOWS THE SLATE. Switching used to leave /live/<old-id> in
      // the bar: a refresh threw you back to the slate you had just left, the
      // back button did nothing, and copying the link to a mate sent them the
      // wrong board (audit, live slate). replaceState, not pushState - a
      // switch replaces what you are watching, it does not add a step to go
      // back through. Only ever rewrites a /live/ path, so /demo and the
      // Studio are untouched.
      try {
        const here = window.location.pathname;
        if (/^\/live\/[^/]+$/.test(here) && slate.id) {
          const next = '/live/' + encodeURIComponent(slate.id) + window.location.search;
          if (next !== here + window.location.search) {
            window.history.replaceState(window.history.state, '', next);
          }
        }
      } catch (e) {}
      // Reload the pinned-games set for this slate (each slate has its own).
      this._loadPinnedGames();
      // Refresh scoring rules + by-pos for the slate's platform/sport. Without
      // this they stay at whatever loaded on initial dashboard render — which
      // breaks the new vidi engine and any other consumer that needs current
      // stat weights.
      const plat  = slate.platform_id || slate.platform?.id || 'fanteam';
      const sport = (slate.sport_id || '').toLowerCase() || 'nba';
      Promise.all([
        fetch(`/api/scoring-rules?platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}`).then(r => r.ok ? r.json() : {}).catch(() => ({})),
        fetch(`/api/scoring-rules/by-pos?platform_id=${encodeURIComponent(plat)}&sport_id=${encodeURIComponent(sport)}`).then(r => r.ok ? r.json() : {}).catch(() => ({})),
      ]).then(([rules, rulesByPos]) => {
        this.scoringRules      = rules || {};
        this.scoringRulesByPos = rulesByPos || {};
      });
      this._fetchVidiRulesForCurrentSlate();
    },

    // Fetch vidi engine rules for the active slate's (sport, platform).
    // Cached per pair (different platforms can have different rule sets,
    // e.g. FD NBA uses fg2m/fg3m/ftm not pts). Refetches when cache is
    // empty so a previous failed/pre-seed fetch doesn't permanently
    // disable the engine.
    // Pulled out as a helper so init() can call it after assigning
    // selectedSlate directly (init bypasses selectSlate to avoid a feed reset).
    _fetchVidiRulesForCurrentSlate() {
      // Post-cutover this feeds ONLY the media/celebration lookup (event_type by
      // rule_id) — scoring is server-side. Rules are sport-level now, keyed by
      // sport alone (no platform).
      const slate = this.selectedSlate;
      if (!slate) return;
      const sport = (slate.sport_id || '').toLowerCase();
      if (!sport) return;
      const _cached = this._vidiRulesBySport[sport];
      if (_cached && _cached.length > 0) return;
      fetch(`/api/vidi-rules/${encodeURIComponent(sport)}`)
        .then(r => r.ok ? r.json() : [])
        .then(rules => { this._vidiRulesBySport[sport] = Array.isArray(rules) ? rules : []; })
        .catch(() => { this._vidiRulesBySport[sport] = []; });
    },


    toggleLineup(id) {
      const s = new Set(this.openLineups);
      if (s.has(id)) { s.delete(id); } else { s.add(id); }
      this.openLineups = s;
    },

    // ── Per-sport vidi prefs ──────────────────────────────────────────────
    // Single read path for vidi_<sport>_<leaf> settings. Saved shape
    // (settings.js + /api/settings) is flat snake_case; one alias —
    // euroleague is stored as vidi_el_*. Use this everywhere instead of
    // a `sport === 'foo' ? this.vidiFoo... : this.vidiBar...` ladder.
    //
    // Examples:
    //   this.vidiPref('mlb',    'min_delta',    0)
    //   this.vidiPref('soccer', 'pitch_events', true)
    //   this.vidiPref(sport,    'match_events', true)
    vidiPref(sport, leaf, fallback) {
      const s = (sport || '').toLowerCase();
      const key = 'vidi_' + (s === 'euroleague' ? 'el' : s) + '_' + leaf;
      const v = (this._vidiSettings || {})[key];
      return v !== undefined && v !== null ? v : fallback;
    },

    // Mutator counterpart to vidiPref — writes one key, persists to
    // /api/settings, and triggers reactive re-evaluation of every
    // vidi event filter (which calls vidiPref on every render). Used
    // by the Vidi cog popover when the user flips an event toggle or
    // edits the min-delta input mid-slate.
    //
    // Persistence is best-effort + optimistic — the local state mutates
    // synchronously so the UI reflects the change instantly; if the
    // POST fails we log and leave the local state in place (next page
    // reload would revert it). That tradeoff favours UX: the user sees
    // their toggle action take effect, and a one-in-a-million server
    // glitch doesn't surface as a stuck-spinner mid-slate.
    setVidiPref(sport, leaf, value) {
      const s = (sport || '').toLowerCase();
      const key = 'vidi_' + (s === 'euroleague' ? 'el' : s) + '_' + leaf;
      // Assign a NEW object so Alpine's proxy fires the reactive update
      // (in-place mutation of a Set-style dict wouldn't always trip
      // the dep on _vidiSettings).
      this._vidiSettings = { ...(this._vidiSettings || {}), [key]: value };
      fetch('/api/settings', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ [key]: value }),
      }).catch(() => {});
    },

    // Lookup the VIDI_SPORT_PANELS entry for the current slate's sport.
    // Used by widgets/vidi_options.html to render only the toggles that
    // apply to the active sport (NBA dashboards don't see MLB toggles).
    // Returns null if the sport isn't in the meta or the slate isn't
    // selected yet.
    get _activeVidiPanel() {
      const sport = (this.selectedSlate?.sport_id || '').toLowerCase();
      if (!sport) return null;
      const panels = window.VIDI_SPORT_PANELS || [];
      return panels.find(p => p.id === (sport === 'euroleague' ? 'euroleague' : sport))
          || null;
    },

    // ── Game clock helper ──────────────────────────────────────────────────
    _gameTime(player) {
      // Takes a PLAYER. It used to also accept a bare team-abbrev string, and
      // that branch could only ever compare labels - no id to try. Every one of
      // the eight callers passes a player object (sports/{mlb,nba,nfl,nhl,soccer}
      // and event_detection), so the string path was dead legacy carrying the
      // one abbrev join in here that had no id fallback behind it.
      //
      // Identity via _teamKey: team_id when both sides have one, the label only
      // when they do not - so the lookup still works while the player's abbrev
      // disagrees with the game's ("AZ" vs "ARI", "WSH" vs "WAS").
      const want = this._teamKey(player);
      const g = want
        ? this.allGames.find(gm => this._gameTeamKeys(gm).includes(want))
        : null;
      const txt = (g || {}).status_text || "";
      // Pad single-digit minutes: "Q1 2:14" → "Q1 02:14"
      return txt.replace(/^(\S+)\s+(\d):/, "$1 0$2:");
    },

    // Stagger an NBA-style game time ("Q1 06:01") by adding `secondsBack` to
    // the clock — used when a single snapshot emits multiple split events
    // from the same rule. Earlier events get more clock left ⇒ they happened
    // earlier within the snapshot window. Returns input unchanged for
    // sport-time formats that don't fit the pattern.
    _staggerGameTime(gameTime, secondsBack) {
      if (!secondsBack) return gameTime;
      const m = (gameTime || '').match(/^(\S+\s+)(\d{1,2}):(\d{2})$/);
      if (!m) return gameTime;
      const prefix = m[1];
      const totalSec = parseInt(m[2], 10) * 60 + parseInt(m[3], 10) + (secondsBack | 0);
      const mins = Math.floor(totalSec / 60);
      const secs = totalSec % 60;
      return `${prefix}${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },

    _fptsBreakdown(p) {
      const stats = p.stats || {};

      // DB labels (from scoring_rules.label) take priority, then tip text, then fallback map
      const _dbLabels = (this.scoringRulesByPos || {})._labels || {};
      const _STAT_LABELS = {
        appearance: 'Appearance', min60: '60+ Minutes', full_match: 'Full Match (90 min)',
        win: 'Win', loss: 'Loss', goal: 'Goal', assist: 'Assist',
        clean_sheet: 'Clean Sheet', goals_ag: 'Goals Against',
        save: 'Save', sog: 'Shot on Target', yellow: 'Yellow Card', red: 'Red Card',
        pen_save: 'Penalty Save', pen_miss: 'Penalty Miss',
        pen_won: 'Penalty Won', pen_conceded: 'Penalty Conceded',
        pts: 'Points', reb: 'Rebound', ast: 'Assist', stl: 'Steal', blk: 'Block',
        fg3m: '3-Pointer', to: 'Turnover', min: 'Minutes',
        goals: 'Goal', assists: 'Assist', shots: 'Shot on Goal',
        hits: 'Hit', blocks: 'Block', plus_minus: 'Plus/Minus', saves: 'Save',
        toi: 'Time on Ice', pass_yds: 'Pass Yards', pass_td: 'Pass TD',
        rush_yds: 'Rush Yards', rush_td: 'Rush TD', rec: 'Reception',
        rec_yds: 'Rec Yards', rec_td: 'Rec TD', fum: 'Fumble Lost',
        hr: 'Home Run', rbi: 'RBI', r: 'Run', sb: 'Stolen Base',
        ip: 'Innings Pitched', so: 'Strikeout', er: 'Earned Run',
        single: 'Single', double: 'Double', triple: 'Triple',
        quality_start: 'Quality Start', bb: 'Walk', hbp: 'Hit by Pitch',
        rating: 'Rating',
      };
      const cols = this.statCols || [];
      for (const c of cols) {
        if (c.tip) _STAT_LABELS[c.key] = c.tip;
      }

      // Pick position-specific rules when available, fall back to flat rules
      const _posMap = {
        // Soccer
        GK:'gk', G:'gk', DEF:'def', D:'def', MID:'mid', M:'mid', FWD:'fwd', F:'fwd', ATT:'fwd',
        // NHL
        C:'skater', LW:'skater', RW:'skater', W:'skater',
        // MLB
        SP:'pitcher', RP:'pitcher', P:'pitcher',
        '1B':'hitter','2B':'hitter','3B':'hitter',SS:'hitter',
        OF:'hitter',LF:'hitter',CF:'hitter',RF:'hitter',DH:'hitter',
        // NFL
        K:'kicker', PK:'kicker', DST:'dst',
      };
      const posGroup = _posMap[(p.pos || '').toUpperCase()] || '';
      const byPos = this.scoringRulesByPos || {};
      // Only use real position groups — exclude metadata keys like _labels
      const posGroups = Object.fromEntries(Object.entries(byPos).filter(([k]) => !k.startsWith('_')));
      const rules = (posGroup && posGroups[posGroup]) ? posGroups[posGroup]
                  : posGroups[''] ? posGroups['']
                  : (Object.keys(posGroups).length ? Object.assign({}, ...Object.values(posGroups)) : this.scoringRules);

      const sport    = (this.selectedSlate?.sport_id    || '').toLowerCase();
      const platform = (this.selectedSlate?.platform_id || '').toLowerCase();
      const lines = [];
      for (const [stat, weight] of Object.entries(rules)) {
        let val = stats[stat];
        if (!val || val === 0) continue;
        // FT soccer: goals conceded penalised as -1 per 2 (floor), not -0.5 per goal
        if (stat === 'goals_ag' && platform === 'fanteam' && sport === 'soccer') {
          val = Math.floor(val / 2) * 2;
        }
        const contrib = Math.round(val * weight * 100) / 100;
        if (contrib === 0) continue;
        lines.push({ stat, label: _dbLabels[stat] || _STAT_LABELS[stat] || stat, value: stats[stat], fpts: contrib });
      }
      // FT soccer: append post-game confirmed FT-specific stats not in DB scoring rules
      if (platform === 'fanteam' && sport === 'soccer') {
        const ftSpecific = [
          { key: 'ft_fantasy_assist', label: 'Fantasy Assist', pts: 3  },
          { key: 'ft_blunder',        label: 'Caused Goal',    pts: -2 },
        ];
        for (const { key, label, pts } of ftSpecific) {
          const val = stats[key] || 0;
          if (val) lines.push({ stat: key, label, value: val, fpts: Math.round(val * pts * 100) / 100 });
        }
      }

      lines.sort((a, b) => Math.abs(b.fpts) - Math.abs(a.fpts));
      return lines;
    },

    _fmtKickoff(utcStr) {
      if (!utcStr) return '';
      try {
        const d = new Date(utcStr);
        return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      } catch { return ''; }
    },

    // ── Vidiprinter event detection ────────────────────────────────────────
    // Per-sport detectors live on window.SPORT_REGISTRY[sport].detectEvents
    // (defined in static/js/sports/<sport>.js). Each starts by trying the
    // engine path (DB rules); the legacy stat-delta logic is the fallback.
    // Sports without a custom detector (tennis, mma, golf, ...) inherit the
    // basketball-shape detector — in practice they all hit the engine path
    // since they have DB rules.
    // ── Event detection + vidi drip ─────────────────────────────────
    // Bodies live in static/js/dashboard/event_detection.js
    // (LSEventDetection.*) — kept as thin .call(this, ...) delegations
    // here so the live.js controller stays scannable. State writes
    // (this.feed, this._vidiQueue, this.prevStats, etc.) still happen
    // on `this` because .call preserves the binding. See module header
    // for the full state-touching contract.
    detectEvents(newPlayers, localTime, myIds)       { return LSEventDetection.detectEvents.call(this, newPlayers, localTime, myIds); },
    _processMatchEvents(events, localTime, myIds)    { return LSEventDetection.processMatchEvents.call(this, events, localTime, myIds); },
    _purgeLegacyVidiCache()                          { return LSEventDetection.purgeLegacyVidiCache.call(this); },
    _pushVidiBatch(batch)                            { return LSEventDetection.pushVidiBatch.call(this, batch); },
    // seedMode is the 5th argument and it is load-bearing: sse.js passes it on
    // the FIRST apply for a slate so the backlog paints statically instead of
    // dripping. Dropping it here left the branch dead, so opening the app
    // mid-slate replayed the whole night - 145 entrance animations in the
    // first 18 seconds, with sound - and devalued the real events that
    // followed (Michelin run 6, auditor A6).
    _ingestScorerEvents(evs, localTime, myIds, evtPlayers, seedMode) { return LSEventDetection.ingestScorerEvents.call(this, evs, localTime, myIds, evtPlayers, seedMode); },
    _vidiDrip()                                      { return LSEventDetection.vidiDrip.call(this); },

    // Display the user's media (GIF/video) in the Animation panel when an
    // event with a matching key fires. Lookup tries rule_id first (engine
    // path — e.g. 'mlb_home_run'), then sound_key (legacy path — e.g.
    // 'nhl_goal'). Files live in IndexedDB; nothing leaves the browser.
    // Return true if this event should produce sound/media output.
    // Rule: if the event maps to a DB rule, gate on `event_type` — only
    // `achievement` and `big_play` celebrate. Mirrors the Settings → Media
    // event-library filter so what the user sees configurable is exactly
    // what plays. `allow_media` is no longer consulted (deprecated 2026-05-20).
    // Legacy fallback: events without a rule_id (NHL, soccer, EL sound_key
    // path) or sports without DB rules loaded still allow by default.
    // ── Event sound ──────────────────────────────────────────────────
    // Bodies live in static/js/dashboard/media_playback.js
    // (LSMediaPlayback.*); sound state lives on the controller
    // (this._audioCtx, this.soundOn).
    _eventAllowsMedia(ev)                { return LSMediaPlayback.eventAllowsMedia.call(this, ev); },

    _detectCourtEvent(p, prev, localTime, batch)       { return LSEventDetection.detectCourtEvent.call(this, p, prev, localTime, batch); },

    _detectPersonalMoments(toDeltas, myPlayers) { return LSEventDetection.detectPersonalMoments.call(this, toDeltas, myPlayers); },
    detectGameEvents(newGames, localTime)       { return LSEventDetection.detectGameEvents.call(this, newGames, localTime); },

    toggleSound() {
      this.soundOn = !this.soundOn;
      if (this.soundOn) this._getAudioCtx();
      fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vidi_sound: this.soundOn }),
      }).catch(() => {});
    },

    toggleFullscreen() {
      // Best-effort — silently swallow if the browser blocks (e.g. user
      // gesture timing) so the button doesn't throw mid-render.
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      } else {
        document.documentElement.requestFullscreen?.().catch(() => {});
      }
    },


    _getAudioCtx()                            { return LSMediaPlayback.getAudioCtx.call(this); },
    _playSoundForEvent(eventKey, altKey)      { return LSMediaPlayback.playSoundForEvent.call(this, eventKey, altKey); },
    playVidiSound()                           { return this._playSoundForEvent('default'); },

    // ── SSE connection + incoming-data fanout ───────────────────────
    // Bodies live in static/js/dashboard/sse.js (LSSse.*). this._sse
    // (the EventSource) stays a controller field so teardown paths
    // elsewhere can still see it.
    _sseUrl()       { return LSSse.sseUrl.call(this); },
    _connectSSE()   { return LSSse.connectSSE.call(this); },
    _applyData(data){ return LSSse.applyData.call(this, data); },

    // ── Demo playback engine (client-driven clock) ──────────────────
    // The public showcase demo doesn't stream from server wall-clock (that
    // auto-looped and ran a whole game in ~2 min — too fast, confusing). We
    // own the clock here: a ticker advances `demoT` by the chosen speed and
    // polls /api/demo-live/<slug>?t=demoT; play/pause/restart/speed just steer
    // that clock. No loop — it stops at the end and offers Replay.
    _demoStart() {
      if (this._demoTimer) return;              // idempotent (init may call twice)
      // 2s, not 500ms. The replay advances by wall-clock (tickMs x speed), so
      // the pace of the story is unchanged - it is a quarter of the requests
      // and a quarter of the renders, in bigger steps. At 500ms the demo held
      // ~51% of a mid-range phone's main thread in long tasks and taps took
      // 300-800ms (mobile audit 2026-09-12) - on the page most phone visitors
      // land on first. Each frame re-applies the whole snapshot, so the cost is
      // per RENDER, not per byte; fewer, larger steps is the lever we have
      // without rebuilding the demo as a delta stream.
      const TICK = 2000;                        // ms between polls
      this._demoTimer = setInterval(() => this._demoTick(TICK), TICK);
      this._demoFetch();                        // paint the first frame immediately
      // A backgrounded tab kept fetching and re-rendering to the end of the
      // replay - data and battery for a screen nobody is looking at.
      if (!this._demoVisBound) {
        this._demoVisBound = true;
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this._demoStop();
          } else if (this.demoPlaying && !this.demoEnded && !this._demoTimer) {
            this._demoTimer = setInterval(() => this._demoTick(TICK), TICK);
            this._demoFetch();
          }
        });
      }
    },
    _demoStop() {
      if (this._demoTimer) { clearInterval(this._demoTimer); this._demoTimer = null; }
    },
    _demoTick(tickMs) {
      if (!this.demoPlaying || this.demoEnded) return;
      this.demoT += (tickMs / 1000) * this.demoSpeed;
      if (this.demoT >= this.demoDuration) {
        this.demoT      = this.demoDuration;
        this.demoPlaying = false;
        this.demoEnded   = true;
      }
      this._demoFetch();
    },
    _demoFetch() {
      const slug = window.DEMO && window.DEMO.slug;
      if (!slug) return;
      // Keep t strictly inside [0, dur) — the server does `t % dur`, so exactly
      // dur would wrap back to 0. dur-0.05 shows the settled final frame.
      const t   = Math.max(0, Math.min(this.demoT, this.demoDuration - 0.05));
      // One frame in flight at a time, and a reply is thrown away only when a
      // restart or seek (which bump _demoSeq) came after it. Every poll used
      // to bump the sequence, so on a server slower than the 2s tick EVERY
      // reply was already superseded when it landed: the board froze, said
      // "feed paused" and jumped to the final whistle (Broadcast audit v2,
      // LIVE-7). Slow now means a slower replay, never a frozen one.
      const seq = this._demoSeq;
      if (this._demoBusy === seq) return;       // the next tick asks again
      this._demoBusy = seq;
      fetch('/api/demo-live/' + encodeURIComponent(slug) + '?t=' + t.toFixed(2))
        // A non-2xx used to resolve and be parsed as if it were a frame, so a
        // 500 counted as a successful poll. It is a failed one.
        .then(r => { if (!r.ok) throw new Error('demo-live ' + r.status); return r.text(); })
        .then(txt => {
          this._demoFailStreak = 0;             // the endpoint answered
          if (seq !== this._demoSeq) return;    // a restart/seek superseded this
          // Between events the replay returns the SAME frame, and re-applying it
          // re-ran the whole Alpine tree for nothing. Compare first, parse only
          // when something actually moved.
          if (txt === this._demoLastRaw) return;
          this._demoLastRaw = txt;
          const d = JSON.parse(txt);
          if (d && d.duration_secs) this.demoDuration = d.duration_secs;
          // Speed is baked per-demo server-side (NFL 0.5×) — no user control.
          if (d && d.speed) this.demoSpeed = d.speed;
          this._applyData(d);
        })
        // Swallowing this meant the board froze in silence. Count it instead:
        // shellBadge stops claiming LIVE after two in a row (run 10, LIVE-24).
        .catch(() => { this._demoFailStreak = (this._demoFailStreak || 0) + 1; })
        .finally(() => {
          if (this._demoBusy === seq) this._demoBusy = null;
          // the clock stopped (the end, or a pause) while this was in flight
          // and its own ask was skipped: fetch where it stopped, once
          if (seq === this._demoSeq && (!this.demoPlaying || this.demoEnded) && Math.abs(Math.min(this.demoT, this.demoDuration - 0.05) - t) > 0.01) this._demoFetch();
        });
    },
    demoTogglePlay() {
      if (this.demoEnded) return this.demoRestart();
      this.demoPlaying = !this.demoPlaying;
      if (this.demoPlaying) this._demoResume();
    },
    // Playing again needs the CLOCK, not just a frame. Hiding the page stops
    // the timer, and coming back only restarts it if the replay was playing at
    // that moment - so a replay that had ended (or was paused) came back with
    // no timer, and Watch again fetched t=0 once and froze there under a LIVE
    // badge (run 8, LIVE-14).
    _demoResume() {
      if (!this._demoTimer && !document.hidden) this._demoStart();   // starts the clock and fetches
      else this._demoFetch();
    },
    demoSetSpeed(s) { this.demoSpeed = s; },

    // Mobile shell active? MOBILE BETA (2026-07-11): the shell now activates
    // for EVERY phone user (real data + entitlement-gated tabs), not just the
    // public demo. Reversible two ways: `?noshell=1` (per-load, see init) and
    // the `_shellBeta` kill switch below (flip to false to fully disable).
    _shellBeta: true,
    _noShell: false,
    get shellMode() {
      // Phone, or an iPad held upright (tabletTouch - see live_v2 initWidgets).
      if ((!this.mobile && !this.tabletTouch) || this._noShell) return false;
      return this.demoShowcase || this._shellBeta;
    },
    // ── Bottom sheets behave like bottom sheets ────────────────────────────
    // (mobile audit 2026-09-12: the page behind scrolled with a sheet open -
    // measured 0 -> 583px - and every sheet drew a grab handle that did
    // nothing, which is the tell of a web page wearing an app's clothes.)
    /** Lock the page while any sheet is open. Called from an x-effect. */
    shellSheetLock(open) {
      try { document.body.classList.toggle('msh-locked', !!open); } catch (e) {}
    },
    _drag: null,
    /** Drag the sheet down to dismiss. Past a third of its height (or a flick)
     *  it closes; anything less springs back. `close` is the sheet's own
     *  closer, so each sheet keeps its own rules (the winnings sheet, for
     *  instance, refuses while saving). */
    shellSheetDrag(e, close) {
      const sheet = e.currentTarget.closest('.msh-sheet');
      if (!sheet || e.button > 0) return;
      this._drag = { y: e.clientY, h: sheet.getBoundingClientRect().height, t: Date.now(), sheet, close };
      sheet.classList.add('is-dragging');
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    },
    shellSheetMove(e) {
      const d = this._drag;
      if (!d) return;
      const dy = Math.max(0, e.clientY - d.y);
      d.dy = dy;
      d.sheet.style.transform = 'translateY(' + dy + 'px)';
    },
    shellSheetEnd() {
      const d = this._drag;
      if (!d) return;
      this._drag = null;
      d.sheet.classList.remove('is-dragging');
      d.sheet.style.transform = '';
      const far  = (d.dy || 0) > Math.max(90, d.h * 0.33);
      const fast = (d.dy || 0) > 40 && (Date.now() - d.t) < 300;
      if (far || fast) { try { d.close(); } catch (_) {} }
    },

    // ── What state is this slate ACTUALLY in? (mobile audit 2026-09-12) ──
    // The header badge used to be the literal word LIVE with a pulsing dot,
    // hardcoded: it pulsed before kick-off, after the final whistle and on a
    // dead feed, and the slate picker marked whichever slate was SELECTED as
    // live. A tracker that lies about the one thing it exists to report is
    // worse than one that says nothing.
    get shellState() {
      const gs = this.allGames || [];
      if (!gs.length) return 'none';
      if (gs.some(g => this.effStatus(g) === 2)) return 'live';
      if (this.slateOver || gs.every(g => this.effStatus(g) === 3)) return 'final';
      // PRE-game means nothing has started YET. A slate with early games done
      // and late ones to come has scores on it: it is between games, not
      // pre-game, and hiding its board behind a countdown would be a lie in
      // the other direction (mobile audit follow-up, 2026-09-12).
      if (gs.every(g => this.effStatus(g) === 1)) return 'pre';
      return 'between';
    },
    /** Earliest kick-off still to come, as epoch ms (null once all have). */
    get shellFirstStart() {
      const ts = (this.allGames || [])
        .filter(g => this.effStatus(g) === 1)
        .map(g => new Date(g.game_time_utc || g.start_time || 0).getTime())
        .filter(t => t > 0)
        .sort((a, b) => a - b);
      return ts.length ? ts[0] : null;
    },
    /** "2h 10m" / "14m" / "2d" until the first kick-off. Ticks on _timerNow. */
    get shellCountdown() {
      const t = this.shellFirstStart;
      if (!t) return null;
      const ms = t - (this._timerNow || Date.now());
      if (ms <= 0) return null;
      // Never below 1: rounding read "IN 0m" for the last 29 seconds (run 8,
      // LIVE-9). At 0 the countdown is over and returns null above. Not ceil -
      // that turns 14m + 5ms into "15m", and every countdown up by a minute.
      const mins = Math.max(1, Math.round(ms / 60000));
      if (mins < 60) return mins + 'm';
      const h = Math.floor(mins / 60);
      if (h >= 24) { const d = Math.round(h / 24); return d + (d === 1 ? 'd' : 'd'); }
      return h + 'h ' + (mins % 60) + 'm';
    },
    /** A COARSE countdown, for the thing that SPEAKS it.
     *
     *  The badge carries role="status", so every value it takes is announced.
     *  The visible countdown ticks every minute, which is right to read and
     *  wrong to hear: ten minutes on a pre-game board produced eleven
     *  announcements - "in 40 minutes", "in 39 minutes", "in 38 minutes" -
     *  while the visitor was trying to listen to anything else on the page
     *  (run 10, LIVE-7b).
     *
     *  Buckets, not minutes. From 40m the next ten minutes cross one boundary,
     *  so a screen-reader user hears it change once; the sighted countdown is
     *  untouched and still ticks. Deliberately vague wording - "about", "under"
     *  - because a bucket is not a minute and should not sound like one. */
    get _shellCountdownSay() {
      const t = this.shellFirstStart;
      if (!t) return null;
      const ms = t - (this._timerNow || Date.now());
      if (ms <= 0) return null;
      const mins = ms / 60000;
      if (mins >= 1440) return 'starts tomorrow or later';
      if (mins >= 60)   return 'starts in over an hour';
      if (mins >= 30)   return 'starts in about half an hour';
      if (mins >= 15)   return 'starts in under half an hour';
      if (mins >= 5)    return 'starts in under fifteen minutes';
      if (mins >= 1)    return 'starts in a few minutes';
      return 'about to start';
    },

    /** The header badge: {text, kind} - plus `say` where what is WRITTEN and
     *  what should be SPOKEN are different things. */
    get shellBadge() {
      const st = this.shellState;
      // Freshness FIRST, including in the demo. The demo branch used to return
      // before the check ever ran, so with the connection dropped the header
      // sat on "LIVE" and the hero froze at its last number indefinitely -
      // measured 90s offline with no cue at any point, on the highest-traffic
      // phone screen there is (audit 2026-09-13). A tracker that has stopped
      // tracking has to say so, demo or not.
      // A FINISHED REPLAY IS NOT A LATE FEED. When the demo reaches its end it
      // stops fetching, so the freshness check below started calling it
      // DELAYED and then STALE - the demo ended by telling a stranger the
      // product is broken (audit LIVE-2 = FIRST-8, 2026-09-16).
      if (this.demoShowcase && this.demoEnded) return { text: 'REPLAY OVER', kind: 'final' };
      // ...and the badge has to ASK, whatever the slate state: it only
      // consulted freshness while live, so the offline cue above could never
      // reach a pre-game or finished header (audit LIVE-9).
      const _f = this.dataFreshness;
      if (_f && _f.offline) return { text: 'OFFLINE', kind: 'dead' };
      const fresh = st === 'live' ? _f : null;
      if (fresh) {
        // Carry the AGE, not just the fact. The desktop pill says "2m ago";
        // the phone used to collapse every degree of lateness to the single
        // word DELAYED, which is the difference between "keep watching" and
        // "the feed is dead, go and check DraftKings".
        // The age is APPENDED, never assumed: the demo path produces a
        // freshness object with a null age, and _fmtAge(null) stringifies to
        // "nulls" - which is how this first shipped past me and how the JS
        // tests caught it.
        const age = this.dataAgeSeconds;
        const ageTxt = (typeof age === 'number' && isFinite(age)) ? ' ' + this._fmtAge(age) : '';
        // DEAD AND LATE ARE DIFFERENT ANSWERS. OFFLINE, STALE and DELAYED all
        // rendered in the same amber, so "your phone has no signal" and "the
        // feed is 90 seconds behind" looked identical - the first means go and
        // check DraftKings, the second means keep watching (audit LIVE-3,
        // 2026-09-16).
        // WAITING / PAUSED, not DELAYED / STALE: the words describe the feed
        // we are waiting on, not LiveSlates being late - the same vocabulary as
        // the strip under this bar and the desktop card (Xav, 2026-09-26). The
        // kinds - and so the colours and the dimmed hero - are unchanged.
        if (fresh.offline)        return { text: 'OFFLINE', kind: 'dead' };
        if (fresh.cls === 'red')  return { text: 'PAUSED' + ageTxt, kind: 'dead' };
        if (this.sseReconnecting) return { text: 'RECONNECTING', kind: 'stale' };
        // a short gap between updates is still live (Xav 2026-10-12, see feedStatus)
      }
      // The demo says it is one while it plays - a bare LIVE over Ruth and
      // Ohtani claimed a real slate (run 8, first contact: demo honesty) - and
      // FINAL once every game is, not DEMO (run 8, LIVE-2).
      // A DEMO THAT CANNOT REACH ITS OWN FEED IS NOT LIVE EITHER. _demoFetch
      // swallowed every failure, so the highest-traffic phone screen there is
      // held a confident green LIVE DEMO over a frozen board until the generic
      // age check noticed, 60 seconds later - while the real SSE path flips to
      // RECONNECTING on the first drop. Same word as the real path, because it
      // is the same situation and it recovers the same way (run 10, LIVE-24).
      if (this.demoShowcase && st === 'live' && (this._demoFailStreak || 0) >= 2) {
        return { text: 'RECONNECTING', kind: 'stale' };
      }
      if (this.demoShowcase) {
        if (st === 'live')  return { text: 'LIVE DEMO', kind: 'live' };
        if (st === 'final') return { text: 'FINAL', kind: 'final' };
        return { text: 'DEMO', kind: 'pre' };
      }
      if (st === 'live') {
        // The freshness pill lives in the desktop sub-nav, which the shell
        // hides - so a dead feed looked exactly like a live one on a phone.
        // dataFreshness is NULL while the data is moving and an object
        // ({label, cls}) once it isn't - red = properly stale. Handled above.
        return { text: 'LIVE', kind: 'live' };
      }
      if (st === 'final') return { text: 'FINAL', kind: 'final' };
      // KICK-OFF ON A DEAD STREAM. Pre-game, freshness means nothing, so a
      // stream that died before the start sat on PRE-GAME once the countdown ran
      // out - no data would ever arrive to flip the board to LIVE (run 8,
      // LIVE-9). Past the start with the stream down, say so.
      if ((st === 'pre' || st === 'between') && this.sseReconnecting && !this.shellCountdown)
        return { text: 'RECONNECTING', kind: 'stale' };
      if (st === 'pre') {
        const c = this.shellCountdown;
        return { text: c ? 'IN ' + c : 'PRE-GAME', kind: 'pre',
                 say: c ? this._shellCountdownSay : null };
      }
      if (st === 'between') {
        // Short, because this badge shares a 390px header with the wordmark and
        // the slate name - and the NAME is the "where am I" (audit re-run).
        const c = this.shellCountdown;
        // ...and the same quantising as the pre-game branch. BETWEEN games the
        // countdown ticks every minute too, and role=status announces every
        // value of it; only the `pre` branch was given a spoken form, so this
        // one still read out "next one hour twenty-nine minutes" a minute
        // later (run 10, LIVE-7b - the gap found by LIVE-7's own rebase).
        // ONGOING when the next game is more than 30 minutes off - the same
        // rule as window.slateOngoing, here from the board's own games (an NFL
        // Thu-Mon slate on a Friday read NEXT 2d; Xav 2026-10-12)
        const far = this.shellFirstStart && this.shellFirstStart - (this._timerNow || Date.now()) > 30 * 60000;
        if (far) return { text: c ? 'ONGOING · ' + c : 'ONGOING', kind: 'pre',
                          say: c ? ('ongoing, next game ' + this._shellCountdownSay) : 'ongoing' };
        return { text: c ? 'NEXT ' + c : 'BETWEEN', kind: 'pre',
                 say: c ? ('next game ' + this._shellCountdownSay) : null };
      }
      return null;
    },
    /** Local kick-off time for a game that hasn't started ("18:10"). */
    /** The badge text on a mini game chip.
     *
     * The badge used to read LIVE / END / NEXT, which meant a live board on a
     * phone carried NO quarter and NO clock anywhere: a game 40 seconds into Q1
     * and a game at the two-minute warning of Q4 looked identical, and the chip
     * is not tappable so there was no way to find out. Five independent
     * auditors reported it, and the iPad renders "Q3 10:55" from the very same
     * payload (audit, live slate, 2026-09-13).
     *
     * The clock goes IN the badge rather than beside the score because the
     * green tint and border already say "live" - the word was spending the one
     * piece of space the chip had on something the colour repeats.
     *
     * `status_text` is the provider's own line ("5:19 - 2nd", "Halftime",
     * "Final/OT"). Parsed, not trusted: anything unrecognised falls back to the
     * old label, so a sport whose wording we have not seen degrades to LIVE
     * rather than printing something wrong.
     */
    /** Where a player is in his game, as a WORD.
     *
     * The exposure list showed seven players on 0.00, in the same green, with
     * nothing to separate them: six had not kicked off and one - Tennessee
     * D/ST - had genuinely finished on nothing. Three independent auditors
     * reported it, and they were right that it is worse than no screen, since
     * five identical zeros read as five busts when most of them are still to
     * come (audit, live slate, 2026-09-13).
     *
     * A word, not the status DOT the Lineups tab uses. That dot carries five
     * states in colour alone with the meaning in a `title` - WCAG 1.4.1 Level
     * A, and a tooltip cannot fire on touch anyway - so copying it here would
     * have spread the defect rather than fixed it.
     */
    shellPhaseLabel(p) {
      if (!p) return '';
      if (p.halftime) return 'HALF';
      const ph = p.game_phase;
      if (ph === 'not_started') return 'NOT STARTED';
      if (ph === 'finished')    return 'FINAL';
      return '';                       // playing - the live number says it
    },

    shellGameLabel(g) {
      const st = String((g && g.status_text) || '').trim();
      const status = this.effStatus(g);
      if (status === 3) return /\bOT\b/i.test(st) ? 'FINAL/OT' : 'FINAL';
      if (status !== 2) return 'NEXT';
      if (/half/i.test(st)) return 'HALF';
      // "5:19 - 2nd" / "12:03 - 4th" / "3:22 - OT"
      const m = st.match(/(\d{1,2}:\d{2})\s*[-·]\s*(\d)(?:st|nd|rd|th)/i);
      if (m) return 'Q' + m[2] + ' ' + m[1];
      const ot = st.match(/(\d{1,2}:\d{2})\s*[-·]\s*OT/i);
      if (ot) return 'OT ' + ot[1];
      // Baseball and the rest keep their own words ("Top 5", "Bot 9").
      if (/^(top|bot)\b/i.test(st)) return st.toUpperCase().slice(0, 7);
      return 'LIVE';
    },

    /** The same state, short enough that a chip stays on ONE line.
     *
     *  The full label ("Q3 12:03", "TOP 5TH", "FINAL") is 44-55px of a 146px
     *  chip at 360, which is why every chip used to wrap to two lines there:
     *  61px instead of 37px, an 11-game grid 393px tall, and the Wire pushed
     *  off the first screen entirely (audit, three auditors, 2026-09-16).
     *  Period only - Q3 / T5 / B9 / HALF / OT / F - costs about 22px, which
     *  the scoreline can spare. Pre-game returns nothing at all: the kick-off
     *  time is already IN the chip, so a row of ten identical "NEXT" pills was
     *  saying nothing twice.
     *
     *  The NFL game clock is the real loss here. It stays on the desktop board
     *  and the tablet scoreboard, both of which have the width for it. */
    shellGameLabelShort(g, full) {
      const status = this.effStatus(g);
      // A finished game gets no pill at all - the chip is dimmed instead. A
      // 14-game slate at full time was 14 identical "F" pills, which is a lot
      // of ink to say "nothing is happening here".
      if (status !== 2) return '';
      // The clock, not just the quarter: "Q4" says nothing about whether there
      // is time for your running back to get another carry (run 8, DFS-19).
      // Each sport reads its own provider text - see SportRegistry.chipClock.
      const R = window.SportRegistry;
      const label = R && R.chipClock ? R.chipClock(this.selectedSlate?.sport_id || '', g, !!full) : '';
      return label || 'LIVE';
    },
    /** The same clock for one of your players' rows: where his game is, on the
     *  line under his name. '' before his game and once it is over. */
    shellRowClock(p) {
      if (!p || p.game_phase !== 'playing') return '';
      const g = (this.allGames || []).find(x => String(x.game_id) === String(p.game_id));
      return g ? this.shellGameLabelShort(g, true) : '';
    },

    /** Which of my players are in this game, deduped across lineups.
     *  A chip that says "3 yours" is the difference between a slate grid and a
     *  wall of other people's baseball. */
    get _shellMineByGame() {
      // Memoised on the rows array: read once per game card per render, and at
      // 150 lineups each read walked 1,350 players - 5.5s of a 20s phone
      // profile (Phase 10, 4x CPU throttle).
      const rows = this.lineupRows || _EMPTY_ARR;
      const memo = _shellMemo.get(rows) || {};
      if (memo.mineByGame) return memo.mineByGame;
      const out = {};
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          const gid = p.game_id;
          if (!gid) continue;
          (out[gid] = out[gid] || new Set()).add(String(p.player_id || p.external_player_id || ''));
        }
      }
      const counts = {};
      for (const gid in out) counts[gid] = out[gid].size;
      _shellMemo.set(rows, Object.assign(memo, { mineByGame: counts }));
      return counts;
    },
    shellGameMine(g) { return this._shellMineByGame[g && g.game_id] || 0; },

    /** The board header says WHICH slate. It does not need to repeat the sport,
     *  which the board beside it is already showing, and the prefix is what
     *  makes the name too long to read: "MLB - Diamond Legends" needs 157px of
     *  a 132px slot at 360, so a visitor got "MLB - Diamond Le". Dropping it
     *  also pays for the 58px the wordmark needs to say whose app this is
     *  (run 9, FIRST). Only a code followed by a dash goes; "NFL Classic
     *  (Thu-Mon)" is untouched. */
    /** The strip a SPECTATOR gets. Signed out on a live slate, the KPI strip
     *  renders nothing at all: every tile it has (carrying, dragging, vs
     *  optimal, to play) is about lineups the visitor does not own, so the gate
     *  is `lineupRows.length` and a shared link showed no facts whatsoever -
     *  the thinnest first contact in the product (run 9, FIRST-10).
     *
     *  These three are true for anyone watching: how much of the slate is
     *  actually happening, who is winning the night, and what a perfect lineup
     *  would have. No tile is allowed to read 0 or a dash - a dead tile is
     *  worse than no tile. */
    get _shellSpectate() {
      const games = this.allGames || [];
      if (!games.length) return null;
      const live = games.filter(g => this.effStatus(g) === 2).length;
      const done = games.filter(g => this.effStatus(g) === 3).length;
      const pool = (this.allSlatePlayers || []).length ? this.allSlatePlayers : (this.players || []);
      let top = null;
      for (const p of pool) if (!top || (p.live_fpts || 0) > (top.live_fpts || 0)) top = p;
      const dream = +(this.dreamTeamScore || 0);
      const tiles = [];
      if (live > 0) tiles.push({k: 'Games live', v: String(live), s: 'of ' + games.length});
      else if (done === games.length) tiles.push({k: 'Games', v: String(games.length), s: 'all final'});
      else tiles.push({k: 'Games', v: String(games.length), s: 'not started'});
      if (top && (top.live_fpts || 0) > 0) {
        tiles.push({k: 'Top scorer', v: this._fmtScore(top.live_fpts || 0),
                    s: this._shellSurname(top.name || '')});
      }
      if (dream > 0) tiles.push({k: 'Dream Team', v: this._fmtScore(dream), s: 'the perfect lineup'});
      return tiles.length ? tiles : null;
    },

    /** What you built, and what it is expected to do - the pre-game board's
     *  content. It used to say "2 lineups in" and stop, leaving 380px of empty
     *  between that and the nav bar on a slate you have money in (run 9,
     *  SPACE-19). The number beside each lineup is YOUR projection where you
     *  have one and the platform's average where you do not, labelled either
     *  way, because an unlabelled total is the thing this product refuses to
     *  print (see _judge_lineup_player in core/slate_live.py). */
    get _shellPreLineups() {
      return (this.lineupRows || []).map(lu => {
        const proj = +(lu.proj_total || 0);
        const base = +(lu.baseline_total || 0);
        const n = proj > 0 ? proj : base;
        return {
          id: lu.id,
          name: lu.lineup_name || 'Lineup',
          total: n > 0 ? this._fmtScore(n) : '',
          basis: proj > 0 ? 'proj' : 'avg',
          // The roster itself, in the order the platform gave it. Each player's
          // own projection where there is one and the platform average where
          // there is not - the same basis as the total above them, so a lineup
          // does not add up in one currency and print in another.
          players: (lu.players || []).map(pl => {
            const pp = +(pl.proj || 0), pb = +(pl.baseline || 0);
            const v = pp > 0 ? pp : pb;
            return {
              id: String(pl.player_id || pl.external_player_id || ''),
              pos: pl.pos || '',
              name: pl.name || '',
              team: pl.team || '',
              total: v > 0 ? this._fmtScore(v) : '',
            };
          }),
        };
      });
    },

    /** Which lineup is open on the pre-game board. The best one to begin with,
     *  because that is the one being asked about; tapping another opens it. */
    shellPreOpen: null,
    get _shellPreOpenId() {
      const rows = this._shellPreLineups;
      if (!rows.length) return null;
      const has = rows.some(r => r.id === this.shellPreOpen);
      return has ? this.shellPreOpen : rows[0].id;
    },
    shellPreToggle(id) {
      this.shellPreOpen = (this._shellPreOpenId === id) ? '__none__' : id;
    },

    shellHeadShort(name) {
      return String(name || '').trim().replace(/^[A-Z]{2,4}\s*[—–-]\s*/, '');
    },

    shellGameTime(g) {
      // A GAME carries `game_time_utc` (core/slate_live.py:506). A SLATE carries
      // `start_time`. Reading start_time off a game returned undefined every
      // time, so the countdown was permanently null: a slate 37 minutes away
      // said "any moment" in the hero, "BETWEEN" in the badge, and showed no
      // kick-off time on any chip (audit 2026-09-13). The tests passed because
      // their fixture invented the field - see tests/js/shell_state.test.js.
      const raw = g && (g.game_time_utc || g.start_time);
      const t = raw ? new Date(raw) : null;
      if (!t || isNaN(t.getTime())) return '';
      return t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    /** Kick-off time, short: "7:05p" in the US, "19:05" everywhere else.
     *
     *  `hour: '2-digit'` gives en-US "07:05 PM" - 8 characters and a space for
     *  what a phone can say in 5. On a 320px chip that cost the team codes
     *  their letters: "W… @ M…" for WSH @ MIA, which is a different team, not
     *  a shortened one (audit, 2026-09-16; the geometry sweep never saw it
     *  because it runs in en-GB). */
    shellGameTimeShort(g) {
      const raw = g && (g.game_time_utc || g.start_time);
      const t = raw ? new Date(raw) : null;
      if (!t || isNaN(t.getTime())) return '';
      const parts = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' })
        .formatToParts(t);
      const val = (type) => (parts.find(p => p.type === type) || {}).value || '';
      const period = val('dayPeriod');          // "AM"/"PM", absent on a 24h clock
      const hm = `${val('hour')}:${val('minute')}`;
      return period ? hm + period.trim().toLowerCase()[0] : hm;
    },
    /** A slate's name for the phone, with DraftKings' generic single-game
     *  names resolved to the matchup they are actually about.
     *
     *  DK calls EVERY Showdown slate literally "Showdown", and the phone
     *  printed that string raw: measured 2026-09-16 on a real US slate list,
     *  30 of the 56 rows in the picker read "Showdown" and nothing else, so
     *  the switcher was a list of identical rows and the header never said
     *  which game you were watching. /slates has answered this since the
     *  slate badge shipped - the same window.fmtMatchup call, on the same
     *  condition (DK, exactly one matchup, a team sport) - so this is that
     *  rule reaching the phone, not a second rule.
     *
     *  `full` picks the long form ("Baltimore Orioles @ New York Mets") for
     *  the picker rows, which wrap; the header and its one clipped nowrap
     *  line take the short one ("BAL @ NYM").
     */
    /** DK's multi-game names are "<SPORT> Classic (<qualifier>)", and the
     *  qualifier is the only part that tells them apart - the sport is already
     *  on the icon beside the name and "Classic" is on every one of them. The
     *  header is a single clipped line, so it kept the half that repeats:
     *  measured on the real slate list, three "NFL Classic (Preseason ...)"
     *  slates rendered as the identical string "NFL Classic (Preseaso…" at
     *  320, and four collided at 360 (audit TRUNC-7 = TYPE-7, 2026-09-16). */

    /** The switcher's second line. Platform and game count alone left two
     *  different "MLB Classic" slates - one ended, one live - reading the same
     *  but for the badge (run 8, TRUNC-18). The start day and time is what
     *  actually tells them apart. */
    shellSlateTag(s) {
      if (!s) return '';
      const parts = [];
      if (s.platform?.name) parts.push(s.platform.name);
      if (s.n_games) parts.push(s.n_games + (s.n_games === 1 ? ' game' : ' games'));
      if (s.start_time) {
        const d = new Date(s.start_time);
        if (!isNaN(d)) parts.push(d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' }));
      }
      return parts.join(' · ');
    },

    shellSlateName(s, full = false) {
      // The shared rule (platform-themes.js): the header shows the sport's
      // icon beside this, so a Classic's qualifier is enough there.
      if (typeof window.slateLabel !== 'function') return (s && s.name) || '';
      return window.slateLabel(s, { full, short: !full, fmt: this.gameFmt });
    },

    /** The earliest kick-off still to come, in the SAME format the game chips
     *  use. The hero built this from `allGames.filter(...)[0]` - the first
     *  not-started game the feed happened to return, in no particular order.
     *  Measured on a real 7-game slate 2026-09-16: the payload's first game is
     *  ATL@CHC at 00:40 while the slate opens with BAL@NYM at 00:10, so the
     *  hero named a time 30 minutes later than the chip right under it.
     *  shellFirstStart already sorts, and it is what the countdown beside this
     *  line counts down to - so now the two agree by construction. */
    get shellFirstStartTime() {
      const t = this.shellFirstStart;
      return t ? this.shellGameTime({ game_time_utc: new Date(t).toISOString() }) : '';
    },

    /** Per-slate status for the picker rows, so the list tells the truth
     *  about every slate rather than labelling the selected one LIVE. */
    shellSlateBadge(s) {
      // The slate you're watching has real game statuses behind it, so read
      // them rather than the list's badge: the switcher said ENDED on the same
      // screen whose header said BETWEEN GAMES (audit re-run 2026-09-12), and
      // the switcher is the only way to change slate in watching mode - a live
      // slate that reads finished is one nobody taps.
      const cur = s && this.selectedSlate && String(s.id) === String(this.selectedSlate.id);
      if (cur) {
        if (this.shellState === 'live')    return { text: 'LIVE', kind: 'live' };
        if (this.shellState === 'between') return { text: 'BETWEEN', kind: 'live' };
        if (this.shellState === 'final')   return { text: 'ENDED', kind: 'final' };
      }
      const st = (s && s.status) || 'upcoming';
      if (st === 'live')  return { text: 'LIVE', kind: 'live' };
      if (st === 'ended') {
        // 'ended' with no live_status behind it is badgeStatus's time fallback
        // ("started more than 5 minutes ago"), which calls every multi-day DK
        // slate finished on its first evening. We don't actually know, so say
        // nothing rather than something false.
        if (s && s.live_status === 'ended') return { text: 'ENDED', kind: 'final' };
        return null;
      }
      const pill = this.slateStatusPill(s);
      return { text: (pill && pill.label) || 'Upcoming', kind: 'pre' };
    },

    /* Switching tabs writes ?tab= into history, so Android's Back goes Players
     * -> Live instead of leaving the slate, a reload stays where you were, and
     * the deep link is symmetric with the way in (mobile audit 2026-09-12).
     * pushState, not replaceState: Back is the phone's main gesture, and the
     * popstate handler below puts the tab back without a reload.
     * The scroll reset targets the WINDOW - .msh-view is not a scroll
     * container (overflow: visible), so scrolling it did nothing and a new tab
     * opened wherever the last one was left. */
    /* ── Back dismisses the sheet, not the page ─────────────────────────────
     * On Android, Back IS the dismiss gesture. With the slate switcher open it
     * did nothing to the sheet: the tab underneath went Lineups -> Players ->
     * Live and the third press left the site entirely, all while the modal sat
     * there (audit 2026-09-13).
     *
     * One entry is pushed when ANY sheet opens - driven by the same expression
     * that drives the scroll lock, so a new sheet cannot be forgotten here. If
     * the sheet is closed some other way (tap, Escape, drag) the entry is
     * consumed with history.back(), so Back never needs two presses.
     */
    mshPickOpen: false,
    _sheetHistory: false,
    _sheetSkipPop: false,
    get _anySheetOpen() {
      return !!(this.mshPickOpen || this.shellBoardMenu || this.shellLu || this.shellWin);
    },
    shellCloseSheets() {
      this.mshPickOpen = false;
      this.shellBoardMenu = false;
      this.shellLu = null;
      this.shellWin = false;
    },
    shellSheetHistory(open) {
      if (open && !this._sheetHistory) {
        this._sheetHistory = true;
        try { history.pushState({ lsSheet: true }, '', window.location.href); } catch (e) {}
      } else if (!open && this._sheetHistory) {
        this._sheetHistory = false;
        this._sheetSkipPop = true;      // our own back(), not the user's
        try { history.back(); } catch (e) { this._sheetSkipPop = false; }
      }
    },
    /** The slate name the SERVER already knew, for the header's first paint.
     *  Only a fallback: once the slates list lands, selectedSlate.name wins. */
    get shellHeadName() {
      try { return (window.LIVE_INIT && window.LIVE_INIT.slateHead
                    && window.LIVE_INIT.slateHead.name) || ''; } catch (e) { return ''; }
    },
    shellGo(t, fromHistory) {
      const same = this.shellTab === t;
      // Per-tab scroll memory. Going Lineups -> Players -> Lineups threw away
      // where you were in a 40-row table and dropped you at the top, which on
      // a phone is several seconds of thumb work to undo. Native tab bars all
      // keep a position per tab; so do we (audit re-run 2026-09-12).
      if (!same) {
        this._shellScroll = this._shellScroll || {};
        try { this._shellScroll[this.shellTab] = window.scrollY || 0; } catch (e) {}
      }
      this.shellTab = t;
      if (!same) {
        const y = (this._shellScroll && this._shellScroll[t]) || 0;
        // After the tab's content is in the DOM, not before it.
        requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch (e) {} });
      }
      if (fromHistory || same || !this.shellMode) return;
      try {
        const u = new URL(window.location.href);
        if (t === 'live') u.searchParams.delete('tab'); else u.searchParams.set('tab', t);
        window.history.pushState({ shellTab: t }, '', u);
      } catch (e) { /* history unavailable: the tab still switched */ }
    },

    // ── Your own board on the phone (Phase 5, Xav 2026-09-11) ──────────────
    // A switch at the top of the Live tab: LiveSlates (the phone's Live tab)
    // or ★ one of your boards, which turns the Live tab into that board in its
    // PHONE layout (built in the desktop editor's Phone canvas, or automatic).
    // The choice is remembered on this device only - live_v2/index.js
    // _readPersistedActiveDashboard - so the phone and the desktop never
    // change each other's board. Same entitlement + format rules as desktop.
    shellBoardMenu: false,
    get shellBoardsAllowed() {
      if (!this.shellMode || this.demoShowcase || this.isFree || this.spectatorMode) return false;
      const fmt = window.LSFormat && window.LSFormat.resolve(this._formatVariant || '');
      if (fmt && Array.isArray(fmt.presets) && fmt.presets.length && !fmt.user_dashboards_allowed) return false;
      return (this.userDashboards || []).length > 0;
    },
    get shellBoardOn() { return this.shellBoardsAllowed && this._phoneBoardOn; },
    get shellBoardLabel() {
      const boards = this.userDashboards || [];
      if (this._phoneBoardOn) {
        const d = boards.find(b => b.id === this._activeDashboard.id);
        if (d) return d.name;
      }
      return boards.length === 1 ? boards[0].name : 'My boards';
    },
    /** ★ tapped: one board → show it; several → the picker (also how you
     *  swap boards while one is showing). */
    shellBoardTap() {
      const boards = this.userDashboards || [];
      if (boards.length === 1 && !this._phoneBoardOn) return this.shellBoardPick(boards[0].id);
      this.shellBoardMenu = true;
    },
    async shellBoardPick(id) {
      this.shellBoardMenu = false;
      if (id) await this.switchToActiveDashboard('user', id);
    },
    async shellBoardOff() {
      this.shellBoardMenu = false;
      if (this._activeDashboard.kind !== 'user') return;
      await this.switchToActiveDashboard('system', this._resolveInitialPreset(this._sportId));
    },
    /** Mount the board into the Live tab. The grid markup is the desktop
     *  tree's (the inert <template id="ls-desk-tpl">, x-if'd out in shell
     *  mode) CLONED here, not included twice - it's ~650KB of the page. Same
     *  component scope, so every widget renders from the same state. */
    _shellMountBoard(el) {
      const tpl  = document.getElementById('ls-desk-tpl');
      const main = tpl && tpl.content && tpl.content.querySelector('.ls-dash-main');
      if (!main || !window.Alpine || el.childElementCount) return;
      const nodes = [...main.childNodes].filter(n => !(n.nodeType === 1 && n.hasAttribute('data-desk-only')));
      window.Alpine.mutateDom(() => { for (const n of nodes) el.appendChild(n.cloneNode(true)); });
      for (const c of [...el.children]) window.Alpine.initTree(c);
    },
    /** A tablet held upright gets the REAL per-sport scoreboard in the Live
     *  tab's left column. Measured before this: that column ran out of content
     *  at 308px of a 1,137px screen, so an iPad was showing phone-quantity
     *  content in a tablet frame (audit re-run 2026-09-12) - the one thing a
     *  tablet has the room for and a phone genuinely does not.
     *
     *  Cloned from the same inert desktop tree the board view uses, not
     *  included a second time. Safe to rely on here: routes/pages.py only
     *  leaves that tree out for PHONES, never for a tablet. */
    _shellMountScoreboard(el) {
      const tpl = document.getElementById('ls-desk-tpl');
      const sb  = tpl && tpl.content && tpl.content.querySelector('.ls-headline-scoreboard');
      // The clone source is NOT always there. routes/pages.py strips the desk
      // tree for phone user agents, and `tabletTouch` - which gates this mount -
      // includes a phone held sideways by design (core/device_class.py). So an
      // iPhone in landscape asked for a scoreboard whose source had been
      // removed and got nothing at all: no game state on the live tracker, on
      // the public demo (audit 2026-09-13).
      //
      // `is-on` is set only when something really mounted, and the compact game
      // chips hide on that class rather than on the media query - so when there
      // is no scoreboard to show, the chips stay and nobody loses the scores.
      if (!sb || !window.Alpine || el.childElementCount) return;
      window.Alpine.mutateDom(() => el.appendChild(sb.cloneNode(true)));
      for (const c of [...el.children]) window.Alpine.initTree(c);
      el.classList.add('is-on');
      this._shellSbOn = true;
    },
    /** Half-width panels on a phone: the stacked grid is two columns and a
     *  panel spans both unless its phone layout says half. Scoped rules keyed
     *  on the cell's data-flip-id (the shared grid markup stays untouched). */
    get _shellBoardCss() {
      if (!this.shellBoardOn || !this.mobile) return '';
      return (this.layout.panels || []).filter(p => (p.w | 0) < 12)
        .map(p => '.msh .msh-board .ls-grid-stack > .ls-cell[data-flip-id="'
                  + String(p.id).replace(/[^\w-]/g, '') + '"]{grid-column:span 1}')
        .join('\n');
    },

    // ── Phone lineup menu — the ⋯ on each lineup card in the shell ─────────
    // Edit (late swap) goes to the builder and comes back here; rename and
    // delete happen in the sheet. Phone-only: the ⋯ renders under shellMode,
    // so the desktop card is untouched (/lineups stays its management page).
    shellLu:     null,       // the lineup the sheet is open for
    shellLuMode: 'menu',     // 'menu' | 'rename' | 'delete'
    shellLuArmed: true,      // false for 350ms after opening - see shellLuOpen
    shellLuName: '',
    shellLuBusy: false,
    shellLuErr:  '',
    shellLuOpen(lu) {
      this.shellLu = lu; this.shellLuMode = 'menu';
      this.shellLuName = lu.lineup_name || ''; this.shellLuErr = '';
      // The sheet's rows are live during its 220ms rise, so the second tap of
      // a double-tap on ⋯ went straight into a row: Delete's confirm at 320,
      // Rename at 360, the Edit page at 390 (run 8, TOUCH-12). The rows ignore
      // taps until the sheet has arrived.
      this.shellLuArmed = false;
      clearTimeout(this._shellLuArmT);
      this._shellLuArmT = setTimeout(() => { this.shellLuArmed = true; }, 350);
    },
    // Same window as the rows: the second tap of a double-tap lands on the
    // scrim above the still-rising sheet, and closing on it made the ⋯ open
    // and vanish in one gesture.
    shellLuClose() { if (!this.shellLuBusy && this.shellLuArmed !== false) this.shellLu = null; },
    shellLuEditHref(lu) {
      const sid = this.selectedSlate?.id || '';
      return '/build?slate=' + encodeURIComponent(sid) + '&edit=' + encodeURIComponent(lu.id)
           + '&return=' + encodeURIComponent('/live/' + sid);
    },
    async _shellLuSend(method, body) {
      const lu = this.shellLu;
      if (!lu || this.shellLuBusy) return null;
      this.shellLuBusy = true; this.shellLuErr = '';
      try {
        const r = await fetch('/api/lineups/' + encodeURIComponent(lu.id), {
          method, headers: { 'Content-Type': 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'That didn\'t go through - try again.');
        return d;
      } catch (e) {
        this.shellLuErr = e.message || 'That didn\'t go through - try again.';
        return null;
      } finally {
        this.shellLuBusy = false;
      }
    },
    async shellLuRename() {
      const name = (this.shellLuName || '').trim();
      if (!name) return;
      const id = this.shellLu?.id;
      const d = await this._shellLuSend('PATCH', { lineup_name: name });
      if (!d) return;
      // Show it now; the server also nudges the stream so the next push agrees.
      this.lineupRows = (this.lineupRows || []).map(x => x.id === id ? { ...x, lineup_name: d.lineup_name } : x);
      this.shellLu = null;
    },
    async shellLuDelete() {
      const id = this.shellLu?.id;
      const d = await this._shellLuSend('DELETE');
      if (!d) return;
      this.lineupRows = (this.lineupRows || []).filter(x => x.id !== id);
      this.shellLu = null;
    },

    // ── Phone "Log winnings" — once every game is final ────────────────────
    // The desktop does this from /lineups (lineups.js openRecord/saveRecord);
    // the phone does it where you already are when the slate ends. Same batch
    // endpoint and the same rules, minus rank/field size (optional there too) -
    // on a phone the job is "what did I win", not data entry.
    shellWin: null,   // { entries:[{lineup_id, name, fpts, stake, payout, currency, err}], saving, error }
    get _shellSlateDone() {
      if (this.slateOver) return true;
      const gs = this.allGames || [];
      return gs.length > 0 && gs.every(g => this.effStatus(g) === 3);
    },
    get _shellHasWinnings() { return (this.lineupRows || []).some(l => l.live_payout != null); },
    // Currency belongs to the PLATFORM: DK/FD are USD, FanTeam GBP. Same rule
    // as lineups.js platformCurrencyCode (read the note there on why a symbol
    // must never be stored as the code).
    _winCcy(p) { return (p === 'draftkings' || p === 'fanduel') ? 'USD' : 'GBP'; },
    winSym(c)  { return ({ GBP: '£', USD: '$', EUR: '€' })[c] || '£'; },
    shellWinOpen() {
      const plat = this.selectedSlate?.platform_id || this.selectedSlate?.platform?.id || '';
      this.shellWin = {
        // dirty: once a figure is typed, a stray tap on the scrim keeps the sheet.
        saving: false, error: '', dirty: false,
        entries: (this.lineupRows || []).map(lu => ({
          lineup_id: lu.id,
          name:      lu.lineup_name || 'Lineup',
          fpts:      lu.live_total || 0,
          stake:     lu.stake ?? '',
          payout:    lu.live_payout ?? '',
          currency:  this._winCcy(lu.external_platform || plat),
          err:       '',
        })).sort((a, b) => b.fpts - a.fpts),   // likely cashers first
      };
    },
    get shellWinNet() {
      return (this.shellWin?.entries || []).reduce((t, e) =>
        (e.stake === '' || e.stake == null) ? t : t + (Number(e.payout) || 0) - (Number(e.stake) || 0), 0);
    },
    async shellWinSave() {
      const w = this.shellWin;
      if (!w || w.saving) return;
      let bad = false;
      const toSave = [];
      for (const e of w.entries) {
        e.err = '';
        const stake  = (e.stake  === '' || e.stake  == null) ? null : Number(e.stake);
        const payout = (e.payout === '' || e.payout == null) ? null : Number(e.payout);
        if (stake == null && !(payout > 0)) continue;             // untouched: skip
        if (stake == null)                 { e.err = 'Add a stake to record this'; bad = true; continue; }
        if (!(stake >= 0))                 { e.err = "Stake can't be negative"; bad = true; continue; }
        if (payout != null && !(payout >= 0)) { e.err = "Winnings can't be negative"; bad = true; continue; }
        toSave.push({ lineup_id: e.lineup_id, stake, payout: payout ?? 0, rank: null, currency: e.currency });
      }
      if (bad) return;
      if (!toSave.length) { w.error = 'Add a stake to at least one lineup.'; return; }
      w.saving = true; w.error = '';
      try {
        const r = await fetch('/api/lineups/results/batch', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: toSave }),
        });
        if (!r.ok) throw new Error();
        // Trust what the server SAYS it saved - it skips rows it can't store.
        let saved = null;
        try { saved = (await r.json())?.saved ?? null; } catch (_) {}
        if (saved !== null && saved < toSave.length) throw new Error(`Saved ${saved} of ${toSave.length}`);
        const byId = Object.fromEntries(toSave.map(s => [s.lineup_id, s]));
        this.lineupRows = (this.lineupRows || []).map(l => byId[l.id]
          ? { ...l, stake: byId[l.id].stake, live_payout: byId[l.id].payout } : l);
        this.shellWin = null;
      } catch (err) {
        w.error = /^Saved \d+ of \d+$/.test(err?.message || '')
          ? err.message + ' - the rest didn\'t save. Try those again.'
          : 'Couldn\'t save - check your connection and try again.';
      } finally {
        if (this.shellWin) this.shellWin.saving = false;
      }
    },
    /** DFS value: live fantasy points per $1K of salary - "2.4x".
     *
     * Why this and not pace: pace, est-final and hot/cold in core/tracker.py
     * are all RELATIVE TO PROJECTION, and projections are user input. Measured
     * on a live MLB slate, 0 of 752 players had one - so an MLB clock alone
     * would have lit up nothing (Michelin run 6 flagged "0 of 753 players have
     * a pace delta"; the clock was the smaller of its two causes). Salary is on
     * 752 of 752, and in DFS salary IS the market's projection: DK prices
     * players by expected output, which is why grinders talk in "x value".
     *
     * Honest by construction: it states what a player has RETURNED, not a
     * guess at what he will score. No projection is invented.
     *
     * Blank rather than wrong:
     *  - not started: 0.0x for a man who has not had an at-bat is a number the
     *    product would be making up (same rule as the em-dash on his points).
     *  - no salary: nothing to divide by.
     *  - a multiplier slot (Showdown CPT/MVP): value is a property of the
     *    PLAYER, but whether the row's salary is the base or the slot price is
     *    not something this layer can tell, and a 1.5x error in either
     *    direction is a confidently wrong number. Left blank until it can be.
     */
    _valueX(p) {
      if (!p) return '';
      if ((p.role_mult || p.multiplier || 1) !== 1) return '';
      if (p.game_phase === 'not_started') return '';
      const sal = +p.salary || 0;
      if (sal <= 0) return '';
      const v = (+p.live_fpts || 0) / (sal / 1000);
      // Broadcast says value one way everywhere, pts per $1K to 2 places - the
      // KPIs read "9.15 pts/$1K" while this column read "9.1x" (NUMBERS-12)
      if (this.activeSkin === 'broadcast') return v.toFixed(2);
      return (Math.round(v * 10) / 10).toFixed(1) + 'x';
    },

    /** Does the phone shell mount on this device? Read from the ONE device
     *  definition (core/device_class.py -> window.LS_DEVICE), not a width. */
    _isShellDevice() {
      try { return !!(window.LS_DEVICE && window.matchMedia(window.LS_DEVICE.shell).matches); }
      catch (_) { return false; }
    },
    /** Change the Wire filter.
     *
     * All four filter buttons (vidi.html and vidi_wire.html) used to POST
     * vidi_density to /api/settings inline - the cross-device setting - so
     * tapping "Mine" on a phone quietly changed the desktop Wire too. That
     * breaks the phone plan's isolation rule. A phone writes its own local
     * value and stops; the desktop keeps the server-side behaviour it had. */
    setVidiDensity(v) {
      if (v !== 'mine' && v !== 'all') return;
      this.vidiDensity = v;
      this.vidiScope   = v === 'mine' ? 'my' : 'all';
      if (this._isShellDevice()) {
        try { localStorage.setItem('ls_vidi_density_phone', v); } catch (_) {}
        return;                       // never write the desktop's setting from a phone
      }
      fetch('/api/settings', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                               body: JSON.stringify({ vidi_density: v }) }).catch(() => {});
    },
    /** Flash the hero when its number moves.
     *
     * The phone shows ONE number and it changed silently: measured over a
     * session it went 18.20 -> 19.70 -> 25.50 with no visual acknowledgement
     * at all, on the screen whose entire job is watching a number move
     * (Michelin run 6, auditor A6, whose lens scored lowest at 5.8).
     *
     * ls-tween-pulse already exists and is wired into ten desktop widget
     * templates; the phone hero was simply never one of them. Direction
     * matters — a lineup going DOWN must not flash the same celebratory green
     * as a score, which is why --pulse-rgb exists (v4 KPI audit).
     *
     * Called from the apply path, after lineupRows is fresh. */
    _shellHeroTick() {
      const lu  = this._shellBestLineup;
      // The number the hero is showing: your best lineup's, or - with no lineup
      // on the board - the perfect lineup so far. Only the signed-in card was
      // wired to pulse, so the demo a visitor watches and a spectator's board
      // never acknowledged a move at all (run 8, LIVE-13).
      const raw = lu ? (lu.live_total || 0) : (this.shellState === 'pre' ? null : +(this.dreamTeamScore || 0));
      const now = raw === null ? null : Math.round(raw * 100) / 100;
      const was = this._shellHeroLast;
      this._shellHeroLast = now;
      // First observation has nothing to compare against - don't flash on load.
      if (was === null || now === null || now === was) return;
      const dir = now > was ? 'up' : 'down';
      clearTimeout(this._shellHeroT);
      if (this._shellHeroPulse) {
        // Still pulsing from the last move: drop the class for a frame, or the
        // animation runs out under the new number instead of starting again.
        this._shellHeroPulse = '';
        requestAnimationFrame(() => { this._shellHeroPulse = dir; });
      } else {
        this._shellHeroPulse = dir;
      }
      this._shellHeroT = setTimeout(() => { this._shellHeroPulse = ''; }, 900);
    },

    // Top lineup / player by live points — for the shell's Live-tab summary.
    get _shellBestLineup() {
      const rows = (this.lineupRows || []).slice().sort((a, b) => (b.live_total || 0) - (a.live_total || 0));
      return rows[0] || null;
    },
    /** How much of the hero lineup is still to come, as a short phrase.
     *
     * The subtitle under the big number used to read "<name> · #1 of 3". The
     * "#1" was a string literal, and the hero always shows the BEST lineup, so
     * it could never say anything else - the most valuable line of secondary
     * text on the phone carried zero bits. Worse, "#1 of 3" reads in DFS as a
     * CONTEST rank: a grinder could take it as "I am winning" when it only
     * ever meant "your best of your own three" (Michelin run 6, auditors A1
     * and A8).
     *
     * Players-left is the fact that makes the number above it mean something:
     * 18.20 with nine to come is a different evening from 18.20 with none. The
     * my_lineups widget computes the same thing as _phases(), but that lives
     * in the widget's own x-data and is not reachable from the shell. */
    get _shellBestLeft() {
      // All three buckets, in words (Xav's pick, 2026-09-17): one phrase said
      // "10 still playing" and nothing else, so "how much of my night is left"
      // had no answer (run 8, DFS-7). Where there is a projection or a platform
      // average, the players yet to start become the points still to come:
      // what is left of each live player's projection plus the whole of each
      // unstarted one. The count of those players is on the bar just above.
      //
      // An unused substitute is DONE, not live: his game is running but he will
      // not score in it (DFS-11) - same for a player ruled out.
      const lu = this._shellBestLineup;
      if (!lu) return '';
      let done = 0, live = 0, next = 0, left = 0, basis = false;
      // A live player NOBODY CAN MEASURE. prog_src is "" when the sport has no
      // progress signal at all - golf, f1, nascar, mma, tennis - and the
      // arithmetic below used to read his elapsed_frac of 0 as "0% through"
      // and hand back 100% of his projection as still to come, all day, for a
      // golfer standing on the 18th green (run 10, DFS-24). He is counted as
      // live, because he is, and left out of the forecast, because a forecast
      // built on a number nobody has is not a forecast.
      let unknown = 0;
      // WHICH basis, not just whether there is one: "113.34 pts to come" says
      // nothing about where the 113.34 came from, and it came from two
      // different places depending on the player (run 10, DFS-23).
      let fromProj = false;
      for (const p of (lu.players || [])) {
        const pr = +p.proj || 0;
        const b = pr || (+p.baseline || 0);
        if (b > 0) { basis = true; if (pr) fromProj = true; }
        const out = p.lineup_status === 'bench_unplayed' || p.lineup_status === 'not_play';
        if (p.game_phase === 'finished' || out)     done++;
        else if (p.game_phase === 'not_started') { next++; left += b; }
        else if (!p.prog_src)                    { live++; unknown++; }
        else { live++; left += b * Math.max(0, 1 - (+p.elapsed_frac || 0)); }
      }
      if (!(done + live + next)) return '';
      if (!live && !next) return 'all played';
      const parts = [];
      if (done) parts.push(done + ' done');
      if (live) parts.push(live + ' live');
      // ROUNDED, and it says what it is made of. This is a forecast - the sum
      // of each live player's projection pro-rated by how much of his game is
      // left - and it was rendering to the hundredth, right beside a live FPts
      // total that is also rendered to the hundredth and is a FACT. Two decimal
      // places on a linear extrapolation is a claim the arithmetic cannot
      // support, and this app already refuses to print an estimated MLB lineup
      // total at all for exactly that reason (see showEst). So: one significant
      // change - the tilde and the basis - and the number stops overclaiming
      // (run 10, DFS-23).
      if (basis && left > 0 && this._shellVsOptimal !== null) {
        // ...and it says who it does not cover, rather than quietly covering
        // them wrong. On a golf or MMA slate this is every live player, so the
        // figure disappears instead of being invented.
        parts.push('~' + Math.round(left) + ' pts to come of '
                   + (fromProj ? 'projection' : 'DK average')
                   + (unknown ? ' (' + unknown + ' not forecast)' : ''));
      } else if (next) parts.push(next + ' to come');
      return parts.join(' · ');
    },
    /** MY top scorer, with a name — not the slate's.
     *
     * The KPI strip read "Lineups 3 (mine) · Top scorer 16.25 (the SLATE's) ·
     * Games live 5 (the slate's)", and the middle tile carried the hot
     * treatment, so the most eye-catching number on the Live tab belonged to a
     * player the user did not own, sitting between two that read as theirs.
     * Three separate auditors reported it independently (Michelin run 6, A1,
     * A2 and A8). A bare figure is also unusable without a name — "16.25" tells
     * you nothing about who is carrying you. */
    /** Your best lineup as a percent of the live Dream Team total — the
     *  "ceiling". 0-100, or null before the Dream Team has a score.
     *
     *  WHY THIS IS THE PHONE'S ANSWER TO "AM I WINNING". Live rank is the
     *  number people actually want, and for DK it cannot be had: the
     *  standings endpoint needs an authenticated US userKey, server-side
     *  replay dies on Akamai, and the bookmarklet route is moot on a phone
     *  because Android strips javascript: URLs. See the probe in memory
     *  project_dk_standings_api_findings (2026-06-29).
     *
     *  So Xav's July call stands: best lineup vs the optimal lineup is the
     *  proxy. Not science — a big field can be brutal at 85% — but it turns
     *  the hero's bare "16.00" into a number that means something, and it
     *  needs no platform data at all. Deliberately the SAME maths as the
     *  desktop widget kpi_vs_dream_team, so the two can never disagree. */
    get _shellVsOptimal() {
      const dt = +(this.dreamTeamScore || 0);
      if (dt <= 0) return null;
      const top = (this.lineupRows || [])
        .reduce((m, lu) => Math.max(m, +(lu.live_total || 0)), 0);
      if (top <= 0) return null;
      return Math.max(0, Math.min(100, (top / dt) * 100));
    },
    get _shellMyTop() {
      const seen = new Set();
      let best = null;
      for (const p of (this.players || [])) {
        if (!p || seen.has(p.player_id)) continue;      // same player across entries
        seen.add(p.player_id);
        if (!best || (p.live_fpts || 0) > (best.live_fpts || 0)) best = p;
      }
      return best;
    },
    /** How many of MY players are on the field right now, out of how many.
     *  "Games live 5" was the slate's business; this is the sweat meter. */
    get _shellMyPlaying() {
      const seen = new Set();
      let live = 0, total = 0;
      for (const p of (this.players || [])) {
        if (!p || seen.has(p.player_id)) continue;
        seen.add(p.player_id);
        total++;
        const ph = p.game_phase;
        if (ph && ph !== 'not_started' && ph !== 'finished') live++;
      }
      return { live: live, total: total };
    },
    /** Every player across MY lineups, once, with how many of them he is in.
     *  Exposure is what turns a score into a stake: 20.00 in eight lineups is
     *  worth more to you than 25.00 in one. */
    get _shellMyExposure() {
      const rows = this.lineupRows || _EMPTY_ARR;   // memoised like _shellMineByGame
      const memo = _shellMemo.get(rows) || {};
      if (memo.exposure) return memo.exposure;
      const by = {};
      for (const lu of (this.lineupRows || [])) {
        for (const p of (lu.players || [])) {
          const key = String(p.player_id || p.external_player_id || '');
          if (!key) continue;
          const row = by[key] || (by[key] = { key: key, p: p, n: 0, pts: 0 });
          row.n += 1;
          row.pts += (p.live_fpts || 0);   // what he has actually scored for you, copy by copy
          // The BASE copy, whatever the lineup order. On Showdown a captain's
          // copy carries 1.5x the points, and "any copy" meant the figure on
          // the tile depended on which lineup came last (run 8, DFS-18).
          if ((+p.multiplier || 1) < (+row.p.multiplier || 1)) row.p = p;
        }
      }
      const out = Object.values(by);
      _shellMemo.set(rows, Object.assign(memo, { exposure: out }));
      return out;
    },
    /** The player doing most FOR you: points weighted by how many of your
     *  lineups he is in. Replaces "My top", which was raw points and treated a
     *  one-off the same as a player in every entry. */
    get _shellCarrying() {
      let best = null, bestV = 0;
      for (const r of this._shellMyExposure) {
        if (r.pts > bestV) { bestV = r.pts; best = r; }
      }
      return best;
    },
    /** The player doing most AGAINST you: started, most exposed, furthest
     *  behind what was expected of him by now. Nothing on the Live tab used to
     *  answer "who is killing me", which is half of what a sweat is. */
    get _shellDragging() {
      // Three rules from run 8's DFS-18, all measured:
      //  - never the player in Carrying - one tile said he was doing most for
      //    you and the next that he was doing most against you;
      //  - not before a quarter of his game: two seconds into the NFL demo it
      //    already named someone, where the answer is "no one behind" (the
      //    same 0.25 floor lineup pace uses);
      //  - with nothing to measure against, the least SCORED FOR YOU, not the
      //    lowest points per lineup - 12.00 in 20 lineups is not dragging you
      //    next to 2.00 in 2.
      const carrying = this._shellCarrying;
      const started = this._shellMyExposure.filter(
        r => r.p.game_phase && r.p.game_phase !== 'not_started'
          && !(carrying && r.key === carrying.key) && (+r.p.elapsed_frac || 0) >= 0.25);
      let worst = null, worstV = 0, worstPer = 0;
      for (const r of started) {
        const p = r.p;
        // Projections are USER INPUT and most users never paste any, so
        // expectation falls back to the platform's own average (DK FPPG, on the
        // payload as `baseline`). Without it this tile read "—" for everyone
        // who has not typed a projection, which is nearly everyone (Xav's call,
        // 2026-09-16).
        const basis = (p.proj || 0) || (p.baseline || 0);
        const due = basis * (p.elapsed_frac || 0);
        if (due <= 0) continue;
        const per = due - (p.live_fpts || 0);              // positive = losing you points
        const behind = per * r.n;                          // exposure decides WHO
        if (behind > worstV) { worstV = behind; worst = r; worstPer = per; }
      }
      // The number that SELECTED him is the deficit, not his score. The tile
      // printed p.live_fpts, so "Dragging 1.40 Ryan" against a 19.00
      // projection looked like the same kind of figure as "Carrying 20.00"
      // when it is a different quantity entirely (run 9, DFS-21).
      // Exposure decides WHO is dragging; the tile prints the PER-PLAYER
      // deficit, because the Carrying tile beside it prints a per-player score
      // and the count that scales both is already in the subline (×15).
      if (worst) return {p: worst.p, n: worst.n, pts: worst.pts, key: worst.key,
                         behind: Math.round(worstPer * 100) / 100, judged: true};
      // NO EXPECTATION TO MEASURE AGAINST - no projections pasted and no
      // platform average on file yet - but a lineup always has someone doing
      // least for you. "Nobody is dragging" is never true of a lineup that
      // scored 111 against a 291 optimal (Xav, 2026-09-16), so fall back to the
      // lowest points among the players you are most exposed to.
      let least = null, leastV = Infinity;
      for (const r of started) {
        // Least scored for you; on a tie, the one you have more of.
        if (r.pts < leastV || (r.pts === leastV && least && r.n > least.n)) { leastV = r.pts; least = r; }
      }
      // judged:false - there is no expectation, so the tile shows what he HAS
      // scored and says that is what it means.
      return least ? {p: least.p, n: least.n, pts: least.pts, key: least.key,
                      behind: null, judged: false} : null;
    },
    /** How the REST of your entries are doing. The hero only ever speaks for
     *  your best lineup, so a multi-entry player could not see the other 19. */
    get _shellPortfolio() {
      const rows = this.lineupRows || [];
      const dream = +(this.dreamTeamScore || 0);
      if (!rows.length || dream <= 0) return null;
      // The middle entry and the best one, as a share of the perfect lineup.
      // "Near perfect" counted entries within 15% of it - a bar GPP winners
      // rarely clear - so it read "0/20" almost every night, a great night and
      // a bad one alike (run 8, DFS-20).
      const pct = rows.map(lu => 100 * (+(lu.live_total || 0)) / dream).sort((a, b) => a - b);
      const mid = pct.length >> 1;
      const median = pct.length % 2 ? pct[mid] : (pct[mid - 1] + pct[mid]) / 2;
      return { median: Math.round(median), best: Math.round(pct[pct.length - 1]), total: rows.length };
    },
    /** Players in your best lineup still to come, and out of how many. What a
     *  one- or two-entry player wants from the third tile: "0/1 near optimal"
     *  is a portfolio number, and they do not have a portfolio. */
    /** How far through its own games the best lineup is, 0-1.
     *
     *  The "% of optimal" bar used to append " so far" only while somebody had
     *  yet to START. On a single-window slate - which is most DK slates -
     *  nobody is not-started after first pitch, so the qualifier was never
     *  shown when it mattered: measured at MLB t=40, the 2nd/3rd inning with
     *  113 points still to come, the bar read a flat "84% of optimal". A
     *  grinder reads that as a verdict on a night that has barely begun
     *  (run 10, DFS-22).
     *
     *  Progress is the honest gate. A finished player is 1, a not-started one
     *  is 0, and a live one is however much of his game has gone - which is
     *  the same elapsed_frac the "pts to come" arithmetic already leans on. */
    get _shellSlateProgress() {
      const ps = (this._shellBestLineup && this._shellBestLineup.players) || [];
      if (!ps.length) return 1;
      let sum = 0;
      for (const p of ps) {
        const out = p.lineup_status === 'bench_unplayed' || p.lineup_status === 'not_play';
        if (p.game_phase === 'finished' || out) sum += 1;
        else if (p.game_phase === 'not_started') sum += 0;
        else sum += Math.min(1, Math.max(0, +p.elapsed_frac || 0));
      }
      return sum / ps.length;
    },

    get _shellLeftToPlay() {
      const lu = this._shellBestLineup;
      const ps = (lu && lu.players) || [];
      // Not started only. Counting everyone not FINISHED put a player three
      // innings into his game under "To play" - "9 of 9 to play" with both NFL
      // games in the fourth quarter (run 8, DFS-17).
      const left = ps.filter(p => (p.game_phase || 'not_started') === 'not_started').length;
      return { left: ps.length ? left : '—', total: ps.length || 0 };
    },

    /** Surname only — a KPI label is 9px in a 117px tile. */
    _shellSurname(n) {
      const parts = String(n || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      const last = parts[parts.length - 1];
      // "Jr." / "III" are not the name people recognise.
      if (parts.length > 2 && /^(jr|sr|ii|iii|iv)\.?$/i.test(last)) return parts[parts.length - 2];
      return last;
    },

    get _shellTopPlayer() {
      const ps = (this.allSlatePlayers || []).slice().sort((a, b) => (b.live_fpts || 0) - (a.live_fpts || 0));
      return ps[0] || null;
    },

    // Players tab: 'top' = slate leaders (the top10 widget), 'mine' = exposure.
    shellPlayersView: 'top',
    // My exposure — every player in your lineups, most-owned first. in_count
    // comes from the server's players_agg (lineups containing the player); the
    // % is of ALL your lineups on this slate. No field ownership - we don't
    // have it, so this is honestly "you", not "them".
    get _shellExposure() {
      // DERIVE in_count from the lineups instead of trusting the payload to
      // carry it. Two builders fill `players`: core/slate_live.py adds
      // in_count, core/demo_engine.py does not - so on /demo not one of the 12
      // players had the field, every row filtered out, and with 2 lineups the
      // "one lineup" note was hidden too. The result was a completely blank
      // MY EXPOSURE screen, which three independent auditors reported on the
      // page most first-time visitors land on (audit run 5, 2026-09-13).
      // ._inCountMap is the same derivation the desktop grid has always used.
      const inMap = this._inCountMap;
      const n = this.activeLineupRows.length;   // the map's denominator, so the
                                                // shown "2/3" can never exceed 1
      const rows = (this.players || [])
        .map(p => ({ ...p, in_count: inMap[String(p.player_id || p.external_player_id || "")] || 0 }))
        .filter(p => p.in_count > 0)
        .map(p => ({ ...p, pct: n ? Math.round((p.in_count / n) * 100) : 0 }))
        .sort((a, b) => (b.in_count - a.in_count) || ((b.live_fpts || 0) - (a.live_fpts || 0)));
      return { n, rows };
    },

    // Live tab: your best lineup's score over the slate against the Dream
    // Team's - "chasing the dream" as one small line chart under the hero.
    // Hand-drawn SVG paths, not the ECharts race widget: that pulls ~1MB of
    // charting library onto a phone for a 56px-tall sparkline. Frames are
    // the server trajectory (survives a reload) or, failing that, this
    // session's samples - same inputs lineup_race.js reads.
    get _shellRace() {
      const best = this._shellBestLineup;
      const frames = (this.lineupHistory && this.lineupHistory.length >= 2)
        ? this.lineupHistory : (this._lineupHistory || []);
      if (frames.length < 2) return null;
      if (!best) {
        // A spectator has no lineup to race, but the perfect lineup's climb is
        // the story of the slate they are watching - and the recorded frames
        // were there, drawn for nobody (run 8, LIVE-16).
        const dv = frames.map(f => (f.dream == null ? null : Number(f.dream)));
        if (!dv.some(v => v != null && v > 0)) return null;
        const top = Math.max(1, ...dv.filter(v => v != null));
        const Wd = 300, Hd = 56, nd = frames.length;
        const pts = dv.map((v, i) => (v == null ? null : [(i / (nd - 1)) * Wd, Hd - 3 - (v / top) * (Hd - 8)])).filter(Boolean);
        const line = pts.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ');
        return { dreamOnly: true, dream: line, mine: '', gap: null,
                 area: line + ' L' + pts[pts.length - 1][0].toFixed(1) + ' ' + Hd + ' L' + pts[0][0].toFixed(1) + ' ' + Hd + ' Z' };
      }
      const mine  = frames.map(f => Number((f.totals || {})[best.id]) || 0);
      const dream = frames.map(f => (f.dream == null ? null : Number(f.dream)));
      const hasDream = dream.some(v => v != null && v > 0);
      const max = Math.max(1, ...mine, ...(hasDream ? dream.filter(v => v != null) : []));
      const W = 300, H = 56, n = frames.length;
      const xy = (v, i) => [(i / (n - 1)) * W, H - 3 - (v / max) * (H - 8)];
      const path = arr => arr
        .map((v, i) => (v == null ? null : xy(v, i))).filter(Boolean)
        .map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
      const minePath = path(mine);
      const lastDream = hasDream ? [...dream].reverse().find(v => v != null) : null;
      return {
        mine:  minePath,
        area:  minePath + ' L' + W + ' ' + H + ' L0 ' + H + ' Z',
        dream: hasDream ? path(dream) : '',
        // How far behind the Dream Team you are right now (<= 0 = level).
        gap:   lastDream != null ? lastDream - mine[n - 1] : null,
      };
    },
    // Click-to-seek on the scrubber — jump the clock to the clicked fraction.
    // Wipe the wire so it reflects the new position rather than replaying stale
    // events, then repaint from the snapshot at the new t.
    demoSeek(ev) {
      const rect = ev.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const frac = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      this._demoSeq++;
      this.demoT     = frac * this.demoDuration;
      this.demoEnded = this.demoT >= this.demoDuration - 0.05;
      this._demoResetFeed();
      this._demoFetch();
    },
    // mm:ss for the scrubber readout.
    _mmss(s) {
      s = Math.max(0, Math.floor(Number(s) || 0));
      return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    },
    demoRestart() {
      this._demoSeq++;                          // invalidate any in-flight fetch
      this.demoEnded  = false;
      this.demoT      = 0;
      this._demoResetFeed();
      this.demoPlaying = true;
      this._demoResume();
    },
    // Clear the accumulated wire / diff state so a restart begins from a clean
    // slate rather than replaying against stale prev-snapshots.
    _demoResetFeed() {
      this._demoLastRaw  = null;   // the frame cache must not survive a restart
      this.feed          = [];
      this._vidiSeeded   = false;
      this._vidiHadLiveEvent = false;
      this._pbpFeed      = [];
      this._vidiQueue    = [];
      this.prevStats     = {};
      this.prevAllStats  = {};
      this.prevGames     = {};
      this.lineupHistory = [];
      if (this._vidiFeed && this._vidiFeed.clear) this._vidiFeed.clear();   // wipe rendered wire nodes
      // A seek or a restart is a new first look at the board: re-seed the big
      // moments, or "NFL fires AHEAD OF PACE after the end and a +0.0 on
      // restart" (run 8, FIRST-8 caveat).
      this._pmSeeded = false;
      this._momentLineupAhead = {};
      this._momentSlateOverFired = false;
      // ...and a restart's 0.00 is not the hero dropping.
      this._shellHeroLast = null;
    },
  };
}
